import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calculator, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Time12Input from '../ui/Time12Input';
import UserPicker from '../ui/UserPicker';
import PayrollPage from './PayrollPage';
import SecurityDevices from './SecurityDevices';
import {
    setDeviceWorkspace, registerDevice, subscribeToDevices,
    subscribeToSecuritySettings, getDeviceId, describeDevice,
} from '../../services/deviceService';
import {
    subscribeToUsers,
    addOvertimeRecord,
    subscribeToOvertimeRecords,
    deleteOvertimeRecord,
    subscribeToUserRates,
    registerPayment,
    subscribeToPayments,
    addAttendanceRecord,
    updateAttendanceRecord,
    subscribeToAttendanceRecords,
    deleteAttendanceRecord,
    subscribeToEmployeeSettings,
    subscribeToGlobalSettings,
    serverNow,
    saveGlobalSettings,
    setOvertimeWorkspace
} from '../../services/overtimeService';
import {
    setSolicitudesWorkspace,
    subscribeToMySolicitudes,
    subscribeToAllSolicitudes,
    createSolicitud,
    cancelSolicitud
} from '../../services/solicitudesService';
import HRDocumentation from './HRDocumentation';
import UserSwitcher from './UserSwitcher';
import RRHHHome from './RRHHHome';
import CalendarioLaboral from './CalendarioLaboral';
import {
    FiCalendar, FiClock, FiDollarSign, FiSave, FiTrash2,
    FiDownload, FiUser, FiBriefcase, FiPlus, FiSettings, FiX, FiLock, FiCheckCircle, FiUpload, FiFileText,
    FiLogIn, FiLogOut, FiAlertTriangle, FiCheck, FiList, FiBarChart2,
    FiTrendingUp, FiZap, FiArrowUpRight, FiCoffee, FiUsers, FiMinusCircle, FiPlusCircle, FiSmartphone, FiMonitor,
    FiHome, FiHeart, FiMessageSquare, FiHelpCircle, FiChevronsDown
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { format, parseISO, differenceInMinutes, isWeekend, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isWithinInterval } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    DEFAULT_ENTRY, DEFAULT_EXIT, DEFAULT_WORK_HOURS,
    DEFAULT_BREAK_MINUTES, DEFAULT_MIN_WAGE, DEFAULT_WORK_DAYS,
    DAY_LABELS, DAY_INITIALS,
    calculateHours,
    calcLateMinutes, calcEarlyEntryMinutes, calcEarlyExitMinutes, calcOvertimeMinutes, calcFlexibleDayMinutes,
    getBreakMinutes, effectiveDailyHours,
    isWorkingDay, isInstructorOnly, isActiveUser, isInPayroll,
    takenBreakMinutes, breakExcessMinutes, effectiveBreakMinutes,
    netWorkedHours, computeEmployeePeriod, computeDayBreakdown,
    getHolidayName, computeHolidayWork,
} from '../../services/payrollCalc';

const ADMIN_EMAIL = 'vcecursos@gmail.com';

const MoneyMask = ({ children, visible = true }) => {
    if (visible) return children;
    return <span className="blur-sm select-none text-gray-300 bg-gray-100 rounded px-1">S/ ***.**</span>;
};

// ── Helpers de formato 12 horas (display only; storage/inputs siguen siendo HH:mm 24h) ──
const formatHM12 = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return '—';
    const parts = hhmm.split(':');
    if (parts.length < 2) return hhmm;
    const h = parseInt(parts[0], 10);
    const m = parts[1].slice(0, 2);
    if (isNaN(h)) return hhmm;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m} ${period}`;
};
const formatDate12 = (date) => {
    if (!date) return '—';
    const h = date.getHours();
    const m = String(date.getMinutes()).padStart(2, '0');
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m} ${period}`;
};
const formatDate12s = (date) => {
    if (!date) return '—';
    const h = date.getHours();
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m}:${s} ${period}`;
};

// Los helpers de calculo viven en services/payrollCalc.js
// ── Controles de refrigerio de la tarjeta de marcación ──
// Sale entre la entrada y la salida: primero "Salir a refrigerio", luego
// "Volver", y al final el resumen con el exceso si se pasó del tiempo.
const BreakControls = ({ record, allowedMinutes, onStart, onEnd }) => {
    if (!record?.entryTime || record?.exitTime) return null;
    if (!allowedMinutes) return null; // sin refrigerio configurado, nada que marcar

    const taken = takenBreakMinutes(record);
    const excess = breakExcessMinutes(record, allowedMinutes);

    if (!record.breakStart) {
        return (
            <button onClick={onStart}
                className="w-full mb-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white py-2.5 rounded-xl text-[13px] font-bold transition-colors flex items-center justify-center gap-2 active:scale-95">
                <FiCoffee size={15} /> Salir a refrigerio
                <span className="text-white/50 font-semibold">· {allowedMinutes} min</span>
            </button>
        );
    }

    if (!record.breakEnd) {
        return (
            <button onClick={onEnd}
                className="w-full mb-2 bg-amber-400/90 hover:bg-amber-400 text-amber-950 py-2.5 rounded-xl text-[13px] font-extrabold transition-colors flex items-center justify-center gap-2 active:scale-95">
                <FiCoffee size={15} /> Volver del refrigerio
                <span className="font-semibold opacity-70">· salí {formatHM12(record.breakStart)}</span>
            </button>
        );
    }

    return (
        <div className={`w-full mb-2 py-2 rounded-xl text-[12px] font-bold flex items-center justify-center gap-2 border ${excess > 0 ? 'bg-red-500/15 border-red-400/30 text-red-200' : 'bg-white/[0.07] border-white/15 text-white/70'}`}>
            <FiCoffee size={14} />
            Refrigerio {formatHM12(record.breakStart)} - {formatHM12(record.breakEnd)} · {taken} min
            {excess > 0 && <span className="text-red-300">· {excess} min de más</span>}
        </div>
    );
};

// Los marcajes guardan la hora que calcula el navegador ("HH:mm") y, ademas,
// el sello del servidor. NO se corrige la hora automaticamente con el sello:
// convertirlo a "HH:mm" usa la zona horaria de QUIEN CONSULTA, asi que un
// admin en otra zona veria todo corrido. Lo que se hace es COMPARAR: si el
// sello y la hora guardada difieren mas de 2 minutos, se marca el registro
// para que la discrepancia se vea y se pueda corregir a mano, en vez de
// arreglarla a ciegas.
const DRIFT_TOLERANCE_MIN = 2;

const stampToHM = (stamp) => {
    try {
        const d = stamp?.toDate ? stamp.toDate() : null;
        return d ? format(d, 'HH:mm') : null;
    } catch { return null; }
};

const minutesBetweenHM = (a, b) => {
    if (!a || !b) return 0;
    const [ah, am] = a.split(':').map(Number);
    const [bh, bm] = b.split(':').map(Number);
    return Math.abs((ah * 60 + am) - (bh * 60 + bm));
};

const stampToDate = (stamp) => {
    try {
        const d = stamp?.toDate ? stamp.toDate() : null;
        return d ? format(d, 'yyyy-MM-dd') : null;
    } catch { return null; }
};

// Compara la hora que marco el dispositivo contra la que registro el servidor.
//
// Los marcajes nuevos traen su propio sello (entryAt / exitAt). Los anteriores
// no, pero casi todos tienen `updatedAt`, que en un registro cerrado ese mismo
// dia corresponde justo al momento de marcar la salida: se usa como
// aproximacion, y solo si cae en la misma fecha del registro, para no comparar
// contra una edicion posterior.
const detectClockDrift = (r) => {
    if (r.manualEdit) return null;

    const checks = [];
    const entryServer = stampToHM(r.entryAt);
    if (r.entryTime && entryServer) {
        checks.push({ campo: 'entrada', marcado: r.entryTime, servidor: entryServer, exacto: true });
    }

    let exitServer = stampToHM(r.exitAt);
    let exitExacto = true;
    if (!exitServer && r.exitTime && stampToDate(r.updatedAt) === r.date) {
        exitServer = stampToHM(r.updatedAt);
        exitExacto = false;
    }
    if (r.exitTime && exitServer) {
        checks.push({ campo: 'salida', marcado: r.exitTime, servidor: exitServer, exacto: exitExacto });
    }

    for (const c of checks) {
        const diff = minutesBetweenHM(c.marcado, c.servidor);
        if (diff > DRIFT_TOLERANCE_MIN) return { ...c, diff };
    }
    return null;
};

// ══════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════
const OvertimeCalculator = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [records, setRecords] = useState([]);
    const [userRates, setUserRates] = useState({});
    const [employeeSettings, setEmployeeSettings] = useState({});
    const [payments, setPayments] = useState([]);
    const [rawAttendanceRecords, setRawAttendanceRecords] = useState([]);
    const [globalSettings, setGlobalSettings] = useState({});
    // Seguridad de marcación: equipos autorizados y sesión única.
    const [security, setSecurity] = useState({});
    const [devices, setDevices] = useState([]);
    const [wsReady, setWsReady] = useState(false);

    const isAdmin = currentUser?.email === ADMIN_EMAIL;

    // View State
    const [activeTab, setActiveTab] = useState('asistencia');
    const [showSettings, setShowSettings] = useState(false);
    const [showHR, setShowHR] = useState(false); // Recursos Humanos / Documentación
    const [viewAsUserId, setViewAsUserId] = useState(null); // Admin: "ver como" colaborador
    const [rrhhTab, setRrhhTab] = useState('inicio'); // 'inicio' | 'control'  (para la página de Inicio del módulo RRHH)
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Payment Form
    const [paymentFile, setPaymentFile] = useState(null);
    const [isRegisteringPayment, setIsRegisteringPayment] = useState(false);

    // Overtime Form
    const [selectedUser, setSelectedUser] = useState(null);
    const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');

    // Settings State
    const [editingGlobal, setEditingGlobal] = useState({
        defaultBreakMinutes: DEFAULT_BREAK_MINUTES,
        minimumWage: DEFAULT_MIN_WAGE,
        workDays: DEFAULT_WORK_DAYS,
    });

    // Filters
    const [userFilter, setUserFilter] = useState('ALL');
    const [monthFilter, setMonthFilter] = useState(format(new Date(), 'yyyy-MM'));
    const [attUserFilter, setAttUserFilter] = useState('ALL');
    const [attMonthFilter, setAttMonthFilter] = useState(format(new Date(), 'yyyy-MM'));
    const [summaryFilterMode, setSummaryFilterMode] = useState('month'); // month, week, day, custom
    const [summaryDateFrom, setSummaryDateFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [summaryDateTo, setSummaryDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));

    // Solicitudes (colab view)
    const [mySolicitudes, setMySolicitudes] = useState([]);
    // Todas las solicitudes: el historial las usa para marcar faltas justificadas.
    const [allSolicitudes, setAllSolicitudes] = useState([]);

    // Initialize workspace
    useEffect(() => {
        if (!currentUser?.uid) return;
        let alive = true;
        setSolicitudesWorkspace(currentUser.uid);
        setOvertimeWorkspace(currentUser.uid).then(() => { if (alive) setWsReady(true); });
        return () => { alive = false; };
    }, [currentUser?.uid]);

    // Los ajustes globales viven en un doc por workspace: hay que esperar a que
    // el workspace esté resuelto o leeríamos el documento equivocado.
    useEffect(() => {
        if (!wsReady || !isAdmin) return;
        const unsub = subscribeToAllSolicitudes(setAllSolicitudes);
        return () => unsub();
    }, [wsReady, isAdmin]);

    // Este equipo se da de alta solo la primera vez; nace pendiente hasta que
    // el admin lo autoriza.
    useEffect(() => {
        if (!currentUser?.uid) return;
        setDeviceWorkspace(currentUser.uid).then(() => registerDevice(currentUser));
    }, [currentUser?.uid, currentUser]);

    useEffect(() => {
        const unsubSec = subscribeToSecuritySettings(setSecurity);
        const unsubDev = subscribeToDevices(setDevices);
        return () => { unsubSec(); unsubDev(); };
    }, []);

    useEffect(() => {
        if (!wsReady) return;
        const unsub = subscribeToGlobalSettings((data) => {
            setGlobalSettings(data);
            setEditingGlobal({
                defaultBreakMinutes: typeof data.defaultBreakMinutes === 'number' ? data.defaultBreakMinutes : DEFAULT_BREAK_MINUTES,
                minimumWage: typeof data.minimumWage === 'number' ? data.minimumWage : DEFAULT_MIN_WAGE,
                workDays: Array.isArray(data.workDays) && data.workDays.length > 0 ? data.workDays : DEFAULT_WORK_DAYS,
            });
        });
        return () => unsub();
    }, [wsReady]);

    // Subscribe my solicitudes (only for non-admin: their own)
    useEffect(() => {
        if (!currentUser?.uid) return;
        const meUser = users.find(u => u.email === currentUser.email);
        if (!meUser) return;
        const unsub = subscribeToMySolicitudes(meUser.id, setMySolicitudes);
        return () => unsub();
    }, [currentUser?.uid, users, currentUser?.email]);

    const handleCreateSolicitud = async (data) => {
        const meUser = (viewAsUserId && users.find(u => u.id === viewAsUserId)) || users.find(u => u.email === currentUser?.email);
        if (!meUser) { toast.error('Usuario no encontrado'); return; }
        try {
            await createSolicitud({
                ...data,
                userId: meUser.id,
                userEmail: meUser.email,
                userDisplayName: meUser.displayName || meUser.email,
                userPhotoURL: meUser.photoURL || null,
            });
            toast.success('Solicitud enviada');
        } catch (err) {
            console.error(err);
            toast.error('Error al enviar solicitud');
        }
    };

    const handleCancelSolicitud = async (id) => {
        if (!window.confirm('¿Cancelar esta solicitud?')) return;
        try {
            await cancelSolicitud(id);
            toast.success('Solicitud cancelada');
        } catch { toast.error('Error al cancelar'); }
    };

    // Subscriptions
    useEffect(() => {
        const unsubs = [
            subscribeToUsers(setUsers),
            subscribeToOvertimeRecords(setRecords),
            subscribeToUserRates(setUserRates),
            subscribeToPayments(setPayments),
            subscribeToAttendanceRecords(setRawAttendanceRecords),
            subscribeToEmployeeSettings(setEmployeeSettings)
        ];
        return () => unsubs.forEach(fn => fn());
    }, []);

    // Horas trabajadas recalculadas al vuelo descontando el refrigerio.
    // Se recalcula desde entrada/salida, no desde el valor guardado, así que
    // corrige también el histórico sin reescribir nada en la base de datos
    // (y no puede restar el refrigerio dos veces).
    const attendanceRecords = useMemo(() => rawAttendanceRecords.map(raw => {
        const r = { ...raw, clockDrift: detectClockDrift(raw) };
        const allowed = getBreakMinutes(employeeSettings[r.userId], globalSettings);
        const taken = takenBreakMinutes(r);
        const excess = breakExcessMinutes(r, allowed);
        if (!r.entryTime || !r.exitTime) {
            return { ...r, breakAllowedMinutes: allowed, breakTakenMinutes: taken, breakExcessMinutes: excess };
        }
        // Tardanza y salida temprana SIEMPRE se derivan del horario configurado
        // hoy, nunca del valor guardado en el registro.
        //
        // Los guardados se calcularon en el momento de marcar, con el horario
        // que hubiera entonces (o con el de por defecto si los ajustes aun no
        // habian cargado), y quedaban congelados. Por eso convivian filas
        // incoherentes: 7:33 salia "puntual" y 7:23 "23 min tarde" para la
        // misma persona con entrada a las 7:00. Derivando siempre, todas las
        // filas se miden con la misma vara.
        //
        // El horario del registro solo sirve de respaldo cuando la persona ya
        // no tiene ajustes configurados.
        const st = employeeSettings[r.userId] || {};
        const schedEntry = st.scheduledEntry || r.scheduledEntry || DEFAULT_ENTRY;
        const schedExit = st.scheduledExit || r.scheduledExit || DEFAULT_EXIT;
        const isFalta = r.status === 'falta';
        const workedHours = netWorkedHours(r.entryTime, r.exitTime, effectiveBreakMinutes(r, allowed));
        // Horario irregular: esta persona puede marcar a la hora que quiera, no
        // hay entrada/salida pactada. Solo importa si completó sus horas del día
        // (ver calcFlexibleDayMinutes) — nunca se le mide contra un reloj fijo.
        const isFlexible = st.scheduleMode === 'flexible';
        let lateMin = 0, earlyEntryMin = 0, earlyExitMin = 0, overtimeMin = 0;
        if (!isFalta) {
            if (isFlexible) {
                const workHrsPerDay = effectiveDailyHours(st, globalSettings) || DEFAULT_WORK_HOURS;
                const flex = calcFlexibleDayMinutes(workedHours, workHrsPerDay);
                earlyExitMin = flex.shortfallMinutes;
                overtimeMin = flex.overtimeMinutes;
            } else {
                lateMin = calcLateMinutes(r.entryTime, schedEntry);
                earlyEntryMin = calcEarlyEntryMinutes(r.entryTime, schedEntry);
                earlyExitMin = calcEarlyExitMinutes(r.exitTime, schedExit);
                overtimeMin = earlyEntryMin + calcOvertimeMinutes(r.exitTime, schedExit);
            }
        }
        return {
            ...r,
            scheduledEntry: schedEntry,
            scheduledExit: schedExit,
            // Tardanza y salida temprana SIEMPRE se derivan del horario configurado
            // hoy (o de las horas del día si es horario irregular), nunca del valor
            // guardado en el registro. Los guardados se calcularon en el momento de
            // marcar, con el horario que hubiera entonces, y quedaban congelados.
            // Derivando siempre, todas las filas se miden con la misma vara.
            lateMinutes: lateMin,
            earlyEntryMinutes: earlyEntryMin,
            earlyExitMinutes: earlyExitMin,
            overtimeMinutes: overtimeMin,
            workedHours,
            grossWorkedHours: calculateHours(r.entryTime, r.exitTime),
            breakAllowedMinutes: allowed,
            breakTakenMinutes: taken,
            breakExcessMinutes: excess,
        };
    }), [rawAttendanceRecords, employeeSettings, globalSettings]);

    // Personal de planilla: la lista que se usa en registro manual, filtros y
    // detección de faltas. Los instructores quedan fuera.
    const payrollUsers = useMemo(
        () => users.filter(u => isActiveUser(u) && isInPayroll(u, employeeSettings[u.id])),
        [users, employeeSettings]
    );

    // Set default user
    useEffect(() => {
        if (!currentUser || users.length === 0 || selectedUser) return;
        const me = users.find(u => u.email === currentUser.email);
        if (me) setSelectedUser(me);
    }, [currentUser, users]);

    // ── Overtime handlers ──
    const handleAddRecord = async (e) => {
        e.preventDefault();
        if (!selectedUser) { toast.error('Selecciona un colaborador'); return; }
        if (!isAdmin && selectedUser.email !== currentUser.email) { toast.error('Sin permiso'); return; }
        const rate = userRates[selectedUser.id];
        if (!rate) { toast.error('Tarifa no configurada'); return; }
        if (!date || !startTime || !endTime) { toast.error('Completa fecha y hora'); return; }
        const hours = calculateHours(startTime, endTime);
        try {
            await addOvertimeRecord({
                userId: selectedUser.id, userEmail: selectedUser.email,
                userDisplayName: selectedUser.displayName || selectedUser.email,
                userPhotoURL: selectedUser.photoURL || null,
                date, startTime, endTime, hours,
                hourlyRate: rate, totalPay: hours * rate, createdBy: currentUser.uid
            });
            toast.success('Registro agregado');
            setStartTime(''); setEndTime('');
        } catch { toast.error('Error al guardar'); }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Eliminar registro?')) {
            try { await deleteOvertimeRecord(id); toast.success('Eliminado'); } catch { toast.error('Error'); }
        }
    };

    // ── Attendance handlers ──
    // Get per-employee schedule
    const getSchedule = (userId) => {
        const s = employeeSettings[userId] || {};
        return {
            entry: s.scheduledEntry || DEFAULT_ENTRY,
            exit: s.scheduledExit || DEFAULT_EXIT,
            workHours: s.workHoursPerDay || DEFAULT_WORK_HOURS,
            // Horario irregular: no hay entrada/salida pactada, solo importan
            // las horas del día (ver calcFlexibleDayMinutes).
            flexible: s.scheduleMode === 'flexible',
        };
    };

    // Minutos de refrigerio que le tocan a un colaborador.
    const breakFor = (userId) => getBreakMinutes(employeeSettings[userId], globalSettings);

    // ¿Puede marcarse desde este equipo?
    // El admin nunca queda bloqueado: si no, un fallo de configuración dejaría
    // a todo el mundo fuera sin nadie que pueda arreglarlo.
    const deviceAllowed = () => {
        if (!security.requireTrustedDevice || isAdmin) return true;
        const id = getDeviceId();
        return !!devices.find(d => d.id === id && d.approved);
    };
    const guardDevice = () => {
        if (deviceAllowed()) return true;
        toast.error('Sólo puedes marcar desde un equipo autorizado de la empresa');
        return false;
    };

    // Datos del equipo que hace la marcación, para dejarlos en el registro.
    // Sin esto sabíamos desde dónde está abierta la sesión ahora, pero no desde
    // dónde se marcó cada día, que es lo que hace falta para auditar después.
    const markDeviceInfo = (prefix) => {
        const id = getDeviceId();
        const known = devices.find(d => d.id === id);
        const info = describeDevice();
        return {
            [`${prefix}DeviceId`]: id,
            [`${prefix}DeviceLabel`]: known?.label || info.label,
            [`${prefix}IsMobile`]: info.movil,
        };
    };

    const handleMarkEntry = async () => {
        if (!guardDevice()) return;
        const me = (viewAsUserId && users.find(u => u.id === viewAsUserId)) || users.find(u => u.email === currentUser.email);
        if (!me) return;
        // Quien no tiene acceso activo (rechazado, suspendido, de baja) no ficha.
        if (!isActiveUser(me)) { toast.error('Esta cuenta no está activa'); return; }
        if (isInstructorOnly(me)) { toast.error('Los instructores no marcan asistencia'); return; }
        const today = format(new Date(), 'yyyy-MM-dd');
        const now = format(new Date(), 'HH:mm');
        const existing = attendanceRecords.find(r => r.userId === me.id && r.date === today);
        if (existing?.entryTime) { toast.error('Ya marcaste entrada hoy'); return; }
        const schedule = getSchedule(me.id);
        const isFlexible = employeeSettings[me.id]?.scheduleMode === 'flexible';
        // Horario irregular: no hay entrada pactada contra la cual medir
        // tardanza, y tampoco se sabe aun si va a completar sus horas del día
        // (eso se decide recien al marcar salida, ver handleMarkExit).
        const late = isFlexible ? 0 : calcLateMinutes(now, schedule.entry);
        // Llegar antes de la hora pactada tambien es hora extra, igual que
        // quedarse despues de la salida (ver handleMarkExit): se acredita
        // aqui mismo porque ya sabemos entrada vs. horario, sin esperar a
        // que marque salida.
        const earlyEntry = isFlexible ? 0 : calcEarlyEntryMinutes(now, schedule.entry);
        try {
            await addAttendanceRecord({
                userId: me.id, userEmail: me.email,
                userDisplayName: me.displayName || me.email,
                userPhotoURL: me.photoURL || null,
                date: today, entryTime: now, exitTime: null,
                entryAt: serverNow(), ...markDeviceInfo('entry'),
                scheduledEntry: schedule.entry, scheduledExit: schedule.exit,
                lateMinutes: late, earlyEntryMinutes: earlyEntry, earlyExitMinutes: 0, workedHours: 0,
                overtimeMinutes: earlyEntry,
                status: late > 0 ? 'late' : 'on-time',
                createdBy: currentUser.uid
            });
            if (earlyEntry > 0) {
                const rate = userRates[me.id];
                if (rate) {
                    const otHours = Number((earlyEntry / 60).toFixed(2));
                    await addOvertimeRecord({
                        userId: me.id, userEmail: me.email,
                        userDisplayName: me.displayName || me.email,
                        userPhotoURL: me.photoURL || null,
                        date: today, startTime: now, endTime: schedule.entry,
                        hours: otHours, hourlyRate: rate, totalPay: otHours * rate,
                        autoGenerated: true, createdBy: currentUser.uid
                    });
                    toast.success(`Entrada registrada + ${earlyEntry} min de horas extra`);
                } else {
                    toast.success(`Entrada registrada (${earlyEntry} min antes, sin tarifa configurada)`);
                }
            } else {
                toast.success(isFlexible ? 'Entrada registrada' : (late > 0 ? `Entrada registrada (${late} min tarde)` : 'Entrada registrada a tiempo'));
            }
        } catch { toast.error('Error al registrar'); }
    };

    // ── Refrigerio: salida y regreso ──
    const handleMarkBreakStart = async () => {
        if (!guardDevice()) return;
        const me = (viewAsUserId && users.find(u => u.id === viewAsUserId)) || users.find(u => u.email === currentUser.email);
        if (!me) return;
        const today = format(new Date(), 'yyyy-MM-dd');
        const now = format(new Date(), 'HH:mm');
        const existing = attendanceRecords.find(r => r.userId === me.id && r.date === today);
        if (!existing?.entryTime) { toast.error('Primero marca tu entrada'); return; }
        if (existing.exitTime) { toast.error('Tu jornada ya está cerrada'); return; }
        if (existing.breakStart && !existing.breakEnd) { toast.error('Ya estás en refrigerio'); return; }
        if (existing.breakStart && existing.breakEnd) { toast.error('Ya registraste tu refrigerio de hoy'); return; }
        try {
            await updateAttendanceRecord(existing.id, { breakStart: now, breakEnd: null, breakStartAt: serverNow(), breakEndAt: null });
            toast.success(`Refrigerio iniciado a las ${formatHM12(now)}`);
        } catch { toast.error('Error al registrar'); }
    };

    const handleMarkBreakEnd = async () => {
        if (!guardDevice()) return;
        const me = (viewAsUserId && users.find(u => u.id === viewAsUserId)) || users.find(u => u.email === currentUser.email);
        if (!me) return;
        const today = format(new Date(), 'yyyy-MM-dd');
        const now = format(new Date(), 'HH:mm');
        const existing = attendanceRecords.find(r => r.userId === me.id && r.date === today);
        if (!existing?.breakStart) { toast.error('No has marcado la salida a refrigerio'); return; }
        if (existing.breakEnd) { toast.error('Ya marcaste tu regreso'); return; }
        const allowed = breakFor(me.id);
        const taken = Math.round(calculateHours(existing.breakStart, now) * 60);
        const excess = Math.max(0, taken - allowed);
        try {
            await updateAttendanceRecord(existing.id, { breakEnd: now, breakEndAt: serverNow() });
            if (excess > 0) toast(`Refrigerio de ${taken} min · ${excess} min de más se descontarán`, { icon: '⏱️' });
            else toast.success(`Refrigerio de ${taken} min registrado`);
        } catch { toast.error('Error al registrar'); }
    };

    const handleMarkExit = async () => {
        if (!guardDevice()) return;
        const me = (viewAsUserId && users.find(u => u.id === viewAsUserId)) || users.find(u => u.email === currentUser.email);
        if (!me) return;
        const today = format(new Date(), 'yyyy-MM-dd');
        const now = format(new Date(), 'HH:mm');
        const existing = attendanceRecords.find(r => r.userId === me.id && r.date === today);
        if (!existing) { toast.error('No has marcado entrada hoy'); return; }
        if (existing.exitTime) { toast.error('Ya marcaste salida hoy'); return; }
        const schedule = getSchedule(me.id);
        const settings = employeeSettings[me.id] || {};
        const isFlexible = settings.scheduleMode === 'flexible';
        const allowedBreak = breakFor(me.id);
        // Si marcó refrigerio se descuenta el real; si no, el configurado.
        const worked = netWorkedHours(existing.entryTime, now, effectiveBreakMinutes(existing, allowedBreak));
        const excess = breakExcessMinutes(existing, allowedBreak);
        // Horario irregular: no hay hora de salida pactada, se compara lo
        // trabajado contra sus horas del día (ver calcFlexibleDayMinutes).
        let earlyExit, otMinutes;
        if (isFlexible) {
            const workHrsPerDay = effectiveDailyHours(settings, globalSettings) || DEFAULT_WORK_HOURS;
            const flex = calcFlexibleDayMinutes(worked, workHrsPerDay);
            earlyExit = flex.shortfallMinutes;
            otMinutes = flex.overtimeMinutes;
        } else {
            earlyExit = calcEarlyExitMinutes(now, schedule.exit);
            otMinutes = calcOvertimeMinutes(now, schedule.exit);
        }
        try {
            await updateAttendanceRecord(existing.id, {
                exitTime: now, exitAt: serverNow(), ...markDeviceInfo('exit'),
                earlyExitMinutes: earlyExit, workedHours: worked,
                // Se suma a la hora extra ya acreditada por entrar temprano (handleMarkEntry),
                // no se reemplaza: son dos tramos distintos de la misma jornada.
                overtimeMinutes: (existing.earlyEntryMinutes || 0) + otMinutes,
                breakExcessMinutes: excess,
                status: existing.lateMinutes > 0 ? 'late' : earlyExit > 0 ? 'early-exit' : 'on-time'
            });
            // Auto-register overtime if worked past scheduled exit
            if (otMinutes > 0) {
                const rate = userRates[me.id];
                if (rate) {
                    const otHours = Number((otMinutes / 60).toFixed(2));
                    await addOvertimeRecord({
                        userId: me.id, userEmail: me.email,
                        userDisplayName: me.displayName || me.email,
                        userPhotoURL: me.photoURL || null,
                        date: today,
                        startTime: isFlexible ? existing.entryTime : schedule.exit, endTime: now,
                        hours: otHours, hourlyRate: rate, totalPay: otHours * rate,
                        autoGenerated: true, createdBy: currentUser.uid
                    });
                    toast.success(`Salida registrada + ${otMinutes} min de horas extra`);
                } else {
                    toast.success(`Salida registrada (${otMinutes} min extra, sin tarifa configurada)`);
                }
            } else {
                toast.success('Salida registrada');
            }
        } catch { toast.error('Error al registrar'); }
    };

    // Admin manual attendance
    const [manualAttUser, setManualAttUser] = useState('');
    const [manualAttDate, setManualAttDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [manualAttEntry, setManualAttEntry] = useState('');
    const [manualAttExit, setManualAttExit] = useState('');

    // Check if a record already exists for selected user+date (entry without exit)
    const existingRecord = useMemo(() => {
        if (!manualAttUser || !manualAttDate) return null;
        return attendanceRecords.find(r => r.userId === manualAttUser && r.date === manualAttDate);
    }, [manualAttUser, manualAttDate, attendanceRecords]);

    const handleAddManualAttendance = async () => {
        if (!manualAttUser || !manualAttDate) { toast.error('Selecciona colaborador y fecha'); return; }
        const u = users.find(us => us.id === manualAttUser);
        if (!u) return;
        const schedule = getSchedule(u.id);
        const settings = employeeSettings[u.id] || {};
        const isFlexible = settings.scheduleMode === 'flexible';
        const workHrsPerDay = effectiveDailyHours(settings, globalSettings) || DEFAULT_WORK_HOURS;

        // Case: record exists with entry but no exit → update with exit only
        if (existingRecord && existingRecord.entryTime && !existingRecord.exitTime) {
            if (!manualAttExit) { toast.error('Solo falta registrar la salida'); return; }
            const entryTime = existingRecord.entryTime;
            const worked = netWorkedHours(entryTime, manualAttExit, breakFor(u.id));
            let earlyExit, otMinutes;
            if (isFlexible) {
                const flex = calcFlexibleDayMinutes(worked, workHrsPerDay);
                earlyExit = flex.shortfallMinutes;
                otMinutes = flex.overtimeMinutes;
            } else {
                earlyExit = calcEarlyExitMinutes(manualAttExit, schedule.exit);
                otMinutes = calcOvertimeMinutes(manualAttExit, schedule.exit);
            }
            try {
                await updateAttendanceRecord(existingRecord.id, {
                    exitTime: manualAttExit,
                    earlyExitMinutes: earlyExit,
                    // Se suma a la hora extra ya acreditada por entrar temprano, no se reemplaza.
                    overtimeMinutes: (existingRecord.earlyEntryMinutes || 0) + otMinutes,
                    workedHours: worked,
                    status: existingRecord.lateMinutes > 0 ? 'late' : earlyExit > 0 ? 'early-exit' : 'on-time',
                });
                if (otMinutes > 0) {
                    const rate = userRates[u.id];
                    if (rate) {
                        const otHours = Number((otMinutes / 60).toFixed(2));
                        await addOvertimeRecord({
                            userId: u.id, userEmail: u.email,
                            userDisplayName: u.displayName || u.email,
                            userPhotoURL: u.photoURL || null,
                            date: manualAttDate,
                            startTime: isFlexible ? entryTime : schedule.exit, endTime: manualAttExit,
                            hours: otHours, hourlyRate: rate, totalPay: otHours * rate,
                            autoGenerated: true, createdBy: currentUser.uid
                        });
                        toast.success(`Salida registrada + ${otMinutes} min horas extra`);
                    } else {
                        toast.success('Salida registrada (sin tarifa para horas extra)');
                    }
                } else {
                    toast.success('Salida registrada correctamente');
                }
                setManualAttExit('');
                return;
            } catch { toast.error('Error al actualizar'); return; }
        }

        // Case: new record
        if (!manualAttEntry) { toast.error('Completa la hora de entrada'); return; }
        const worked = manualAttExit ? netWorkedHours(manualAttEntry, manualAttExit, breakFor(u.id)) : 0;
        let late = 0, earlyEntry = 0, earlyExit = 0, otMinutes = 0;
        if (isFlexible) {
            // Horario irregular: nada que medir contra un reloj fijo. Si ya hay
            // salida, recien ahi se sabe si completo sus horas del día.
            if (manualAttExit) {
                const flex = calcFlexibleDayMinutes(worked, workHrsPerDay);
                earlyExit = flex.shortfallMinutes;
                otMinutes = flex.overtimeMinutes;
            }
        } else {
            late = calcLateMinutes(manualAttEntry, schedule.entry);
            earlyEntry = calcEarlyEntryMinutes(manualAttEntry, schedule.entry);
            earlyExit = manualAttExit ? calcEarlyExitMinutes(manualAttExit, schedule.exit) : 0;
            otMinutes = manualAttExit ? calcOvertimeMinutes(manualAttExit, schedule.exit) : 0;
        }
        try {
            await addAttendanceRecord({
                userId: u.id, userEmail: u.email,
                userDisplayName: u.displayName || u.email,
                userPhotoURL: u.photoURL || null,
                date: manualAttDate, entryTime: manualAttEntry, exitTime: manualAttExit || null,
                scheduledEntry: schedule.entry, scheduledExit: schedule.exit,
                lateMinutes: late, earlyEntryMinutes: earlyEntry, earlyExitMinutes: earlyExit, workedHours: worked,
                // Entrar antes + salir despues son dos tramos de hora extra distintos, se suman.
                overtimeMinutes: earlyEntry + otMinutes,
                status: late > 0 ? 'late' : earlyExit > 0 ? 'early-exit' : 'on-time',
                createdBy: currentUser.uid
            });
            const rate = userRates[u.id];
            if (earlyEntry > 0) {
                if (rate) {
                    const earlyHours = Number((earlyEntry / 60).toFixed(2));
                    await addOvertimeRecord({
                        userId: u.id, userEmail: u.email,
                        userDisplayName: u.displayName || u.email,
                        userPhotoURL: u.photoURL || null,
                        date: manualAttDate, startTime: manualAttEntry, endTime: schedule.entry,
                        hours: earlyHours, hourlyRate: rate, totalPay: earlyHours * rate,
                        autoGenerated: true, createdBy: currentUser.uid
                    });
                }
            }
            if (otMinutes > 0) {
                if (rate) {
                    const otHours = Number((otMinutes / 60).toFixed(2));
                    await addOvertimeRecord({
                        userId: u.id, userEmail: u.email,
                        userDisplayName: u.displayName || u.email,
                        userPhotoURL: u.photoURL || null,
                        date: manualAttDate,
                        startTime: isFlexible ? manualAttEntry : schedule.exit, endTime: manualAttExit,
                        hours: otHours, hourlyRate: rate, totalPay: otHours * rate,
                        autoGenerated: true, createdBy: currentUser.uid
                    });
                }
            }
            const totalOtMin = earlyEntry + otMinutes;
            if (totalOtMin > 0) {
                toast.success(rate ? `Asistencia registrada + ${totalOtMin} min horas extra` : 'Asistencia registrada (sin tarifa para horas extra)');
            } else {
                toast.success('Asistencia registrada');
            }
            setManualAttEntry(''); setManualAttExit('');
        } catch { toast.error('Error'); }
    };

    // ── Marcar día como FALTA ──
    // Registra una falta para cualquier colaborador/fecha. La usa tanto el
    // formulario manual como el botón de cada fila detectada sin marcación.
    const registerFaltaFor = async (userId, dateStr) => {
        if (!isAdmin) { toast.error('Sin permiso'); return; }
        const u = users.find(us => us.id === userId);
        if (!u) return;
        const existing = attendanceRecords.find(r => r.userId === userId && r.date === dateStr);
        if (existing) { toast.error(`${u.displayName || u.email} ya tiene un registro ese día`); return; }
        if (!window.confirm(`¿Registrar FALTA de ${u.displayName || u.email} el ${dateStr}?\n\nEsto sí descuenta el día en el resumen.`)) return;
        const schedule = getSchedule(u.id);
        try {
            await addAttendanceRecord({
                userId: u.id, userEmail: u.email,
                userDisplayName: u.displayName || u.email,
                userPhotoURL: u.photoURL || null,
                date: dateStr, entryTime: null, exitTime: null,
                scheduledEntry: schedule.entry, scheduledExit: schedule.exit,
                lateMinutes: 0, earlyExitMinutes: 0, workedHours: 0,
                overtimeMinutes: 0,
                status: 'falta',
                createdBy: currentUser.uid
            });
            toast.success('Falta registrada');
        } catch (err) {
            console.error(err);
            toast.error('Error al registrar falta');
        }
    };

    const handleAddFalta = async () => {
        if (!isAdmin) { toast.error('Sin permiso'); return; }
        if (!manualAttUser || !manualAttDate) { toast.error('Selecciona colaborador y fecha'); return; }
        const u = users.find(us => us.id === manualAttUser);
        if (!u) return;
        // Si ya existe registro ese día, no permitir falta
        const existing = attendanceRecords.find(r => r.userId === manualAttUser && r.date === manualAttDate);
        if (existing) {
            toast.error(`${u.displayName || u.email} ya tiene un registro ese día`);
            return;
        }
        if (!window.confirm(`¿Marcar a ${u.displayName || u.email} como FALTA el ${manualAttDate}?`)) return;
        const schedule = getSchedule(u.id);
        try {
            await addAttendanceRecord({
                userId: u.id, userEmail: u.email,
                userDisplayName: u.displayName || u.email,
                userPhotoURL: u.photoURL || null,
                date: manualAttDate, entryTime: null, exitTime: null,
                scheduledEntry: schedule.entry, scheduledExit: schedule.exit,
                lateMinutes: 0, earlyExitMinutes: 0, workedHours: 0,
                overtimeMinutes: 0,
                status: 'falta',
                createdBy: currentUser.uid
            });
            toast.success('Falta registrada');
            setManualAttEntry(''); setManualAttExit('');
        } catch (err) {
            console.error(err);
            toast.error('Error al registrar falta');
        }
    };

    // La configuración por persona se edita ahora en Recursos Humanos.

    const handleSaveGlobal = async () => {
        if (!isAdmin) return;
        try {
            const days = Array.isArray(editingGlobal.workDays) ? editingGlobal.workDays : DEFAULT_WORK_DAYS;
            if (days.length === 0) { toast.error('Marca al menos un día laborable'); return; }
            await saveGlobalSettings({
                defaultBreakMinutes: Math.max(0, parseInt(editingGlobal.defaultBreakMinutes, 10) || 0),
                minimumWage: Math.max(0, parseFloat(editingGlobal.minimumWage) || 0),
                workDays: [...days].sort(),
            });
            toast.success('Ajustes globales guardados');
        } catch { toast.error('Error al guardar ajustes globales'); }
    };

    const handleRegisterPayment = async () => {
        if (!paymentFile) { toast.error('Adjunta comprobante'); return; }
        setIsRegisteringPayment(true);
        try {
            await registerPayment({ userId: userFilter, month: monthFilter, totalAmount: totalMoney, paidBy: currentUser.uid, status: 'PAID' }, paymentFile);
            toast.success('Pago registrado'); setShowPaymentModal(false); setPaymentFile(null);
        } catch { toast.error('Error'); } finally { setIsRegisteringPayment(false); }
    };

    const exportToExcel = () => {
        const data = filteredRecords.map(r => {
            const [y, m, d] = r.date.split('-').map(Number);
            return {
                'Colaborador': r.userDisplayName, 'Fecha': format(new Date(y, m - 1, d), 'yyyy-MM-dd'),
                'Inicio': r.startTime, 'Fin': r.endTime, 'Horas': r.hours,
                'Tarifa/Hora': (isAdmin || r.userEmail === currentUser.email) ? r.hourlyRate : '***',
                'Total': (isAdmin || r.userEmail === currentUser.email) ? r.totalPay : '***'
            };
        });
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Horas Extras");
        XLSX.writeFile(wb, "Horas_Extras_Reporte.xlsx");
    };

    // ── Filtered data ──
    // ── Combined overtime: merge manual overtime_records + attendance-derived overtime ──
    const combinedOvertimeRecords = useMemo(() => {
        // Start with manual overtime records
        const manualRecords = [...records];
        // Track which user+date combos already have a manual record
        const existingKeys = new Set(records.map(r => `${r.userId}_${r.date}`));
        // Add attendance-derived overtime for records NOT already covered
        attendanceRecords.forEach(att => {
            if (existingKeys.has(`${att.userId}_${att.date}`)) return;
            // att ya trae overtimeMinutes derivado del memo de arriba, que cubre
            // tanto horario regular (entrada adelantada + salida tardía) como
            // horario irregular (horas de más sobre las pactadas del día) — no
            // hace falta recalcularlo, así también cubre registros viejos.
            const otMin = att.overtimeMinutes || 0;
            if (otMin > 0) {
                const rate = userRates[att.userId] || 0;
                const otHours = Number((otMin / 60).toFixed(2));
                const schedule = employeeSettings[att.userId] || {};
                const isFlexible = schedule.scheduleMode === 'flexible';
                // El tramo tardío pesa más para el rango mostrado; si solo hubo
                // entrada adelantada, se muestra ese tramo en su lugar. Horario
                // irregular no tiene un tramo "de más" definido, se muestra el día
                // completo.
                const lateExitMin = isFlexible ? 0 : (att.exitTime ? calcOvertimeMinutes(att.exitTime, schedule.scheduledExit || DEFAULT_EXIT) : 0);
                const [rangeStart, rangeEnd] = isFlexible
                    ? [att.entryTime, att.exitTime]
                    : lateExitMin > 0
                        ? [schedule.scheduledExit || DEFAULT_EXIT, att.exitTime]
                        : [att.entryTime, schedule.scheduledEntry || DEFAULT_ENTRY];
                manualRecords.push({
                    id: `att-ot-${att.id}`,
                    userId: att.userId,
                    userEmail: att.userEmail,
                    userDisplayName: att.userDisplayName,
                    userPhotoURL: att.userPhotoURL,
                    date: att.date,
                    startTime: rangeStart,
                    endTime: rangeEnd,
                    hours: otHours,
                    hourlyRate: rate,
                    totalPay: otHours * rate,
                    autoGenerated: true,
                    fromAttendance: true,
                });
            }
        });
        return manualRecords;
    }, [records, attendanceRecords, userRates, employeeSettings]);

    const availableMonths = useMemo(() => {
        const months = new Set();
        const now = new Date();
        for (let i = 0; i < 6; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.add(format(d, 'yyyy-MM'));
        }
        combinedOvertimeRecords.forEach(r => { if (r.date) months.add(r.date.substring(0, 7)); });
        return Array.from(months).sort().reverse();
    }, [combinedOvertimeRecords]);

    const filteredRecords = useMemo(() => {
        const meUser = users.find(u => u.email === currentUser?.email);
        return combinedOvertimeRecords.filter(r => {
            // Non-admin users only see their own records
            if (!isAdmin && meUser && r.userId !== meUser.id) return false;
            const matchUser = userFilter === 'ALL' || r.userId === userFilter;
            const matchMonth = monthFilter === 'ALL' || r.date.startsWith(monthFilter);
            return matchUser && matchMonth;
        });
    }, [combinedOvertimeRecords, userFilter, monthFilter, isAdmin, users, currentUser]);

    const myRecords = filteredRecords.filter(r => isAdmin || r.userEmail === currentUser?.email);
    const totalHours = filteredRecords.reduce((a, c) => a + c.hours, 0);
    const totalMoney = myRecords.reduce((a, c) => a + c.totalPay, 0);
    const isSpecificView = userFilter !== 'ALL' && monthFilter !== 'ALL';
    const paymentRecord = useMemo(() => isSpecificView ? payments.find(p => p.userId === userFilter && p.month === monthFilter) : null, [payments, isSpecificView, userFilter, monthFilter]);

    const filteredAttendance = useMemo(() => {
        const meUser = users.find(u => u.email === currentUser?.email);
        return attendanceRecords.filter(r => {
            // Non-admin users only see their own records
            if (!isAdmin && meUser && r.userId !== meUser.id) return false;
            const matchUser = attUserFilter === 'ALL' || r.userId === attUserFilter;
            const matchMonth = !attMonthFilter || r.date.startsWith(attMonthFilter);
            return matchUser && matchMonth;
        });
    }, [attendanceRecords, attUserFilter, attMonthFilter, isAdmin, users, currentUser]);

    const attAvailableMonths = useMemo(() => {
        const months = new Set();
        // Always include last 6 months
        const now = new Date();
        for (let i = 0; i < 6; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.add(format(d, 'yyyy-MM'));
        }
        attendanceRecords.forEach(r => { if (r.date) months.add(r.date.substring(0, 7)); });
        return Array.from(months).sort().reverse();
    }, [attendanceRecords]);

    // Today's status for current user
    const todayRecord = useMemo(() => {
        const me = users.find(u => u.email === currentUser?.email);
        if (!me) return null;
        const today = format(new Date(), 'yyyy-MM-dd');
        return attendanceRecords.find(r => r.userId === me.id && r.date === today) || null;
    }, [attendanceRecords, users, currentUser]);

    // ── Today aggregate (header subtitle) ──
    const todayStats = useMemo(() => {
        const today = format(new Date(), 'yyyy-MM-dd');
        const recs = attendanceRecords.filter(r => r.date === today);
        return {
            active: recs.filter(r => r.entryTime && !r.exitTime).length,
            late: recs.filter(r => (r.lateMinutes || 0) > 0).length,
            withOvertime: recs.filter(r => (r.overtimeMinutes || 0) > 0 || (r.exitTime && r.exitTime > (employeeSettings[r.userId]?.scheduledExit || DEFAULT_EXIT))).length,
        };
    }, [attendanceRecords, employeeSettings]);

    // ── KPI: Horas Extra del mes actual + sparkline 7d + comparativo mes anterior ──
    const otKpis = useMemo(() => {
        const now = new Date();
        const currentMonth = format(now, 'yyyy-MM');
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevMonth = format(prev, 'yyyy-MM');
        const myEmail = currentUser?.email;
        const meUser = users.find(u => u.email === myEmail);
        const scoped = combinedOvertimeRecords.filter(r => isAdmin || (meUser && r.userId === meUser.id));
        const monthRecs = scoped.filter(r => r.date?.startsWith(currentMonth));
        const prevRecs = scoped.filter(r => r.date?.startsWith(prevMonth));
        const currentHours = monthRecs.reduce((a, c) => a + (c.hours || 0), 0);
        const currentPay = monthRecs.reduce((a, c) => a + (c.totalPay || 0), 0);
        const prevHours = prevRecs.reduce((a, c) => a + (c.hours || 0), 0);
        const pct = prevHours > 0 ? Math.round(((currentHours - prevHours) / prevHours) * 100) : null;
        // Sparkline last 7 days
        const last7 = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const ds = format(d, 'yyyy-MM-dd');
            const h = scoped.filter(r => r.date === ds).reduce((a, c) => a + (c.hours || 0), 0);
            last7.push({ date: ds, hours: h });
        }
        // Top persons by hours this month
        const byUser = new Map();
        monthRecs.forEach(r => {
            const cur = byUser.get(r.userId) || { id: r.userId, name: r.userDisplayName, photo: r.userPhotoURL, hours: 0 };
            cur.hours += r.hours || 0;
            byUser.set(r.userId, cur);
        });
        const topPersons = Array.from(byUser.values()).sort((a, b) => b.hours - a.hours).slice(0, 4);
        return { currentHours, currentPay, prevHours, pct, last7, topPersons };
    }, [combinedOvertimeRecords, users, currentUser, isAdmin]);

    // ── KPI: Tardanzas del mes (top 2 + total minutos + descuento estimado) ──
    const tardanzasKpi = useMemo(() => {
        const now = new Date();
        const currentMonth = format(now, 'yyyy-MM');
        const myEmail = currentUser?.email;
        const meUser = users.find(u => u.email === myEmail);
        const scoped = attendanceRecords.filter(r => isAdmin || (meUser && r.userId === meUser.id));
        const recs = scoped.filter(r => r.date?.startsWith(currentMonth) && (r.lateMinutes || 0) > 0);
        const totalMinutes = recs.reduce((a, c) => a + (c.lateMinutes || 0), 0);
        let totalDiscount = 0;
        recs.forEach(r => {
            const settings = employeeSettings[r.userId] || {};
            const salary = settings.monthlySalary || 0;
            // Derivado del horario + refrigerio actuales, no del valor guardado:
            // así el descuento refleja el refrigerio sin tener que re-guardar.
            const workHrsPerDay = effectiveDailyHours(settings, globalSettings) || DEFAULT_WORK_HOURS;
            if (salary === 0 || workHrsPerDay === 0) return;
            const minutesPerDay = workHrsPerDay * 60;
            const dailyRate = salary / 30;
            totalDiscount += (r.lateMinutes / minutesPerDay) * dailyRate;
        });
        const top2 = [...recs].sort((a, b) => (b.lateMinutes || 0) - (a.lateMinutes || 0)).slice(0, 2);
        return { count: recs.length, totalMinutes, totalDiscount, top2 };
    }, [attendanceRecords, employeeSettings, globalSettings, users, currentUser, isAdmin]);

    // ── KPI: Puntualidad del equipo del mes + por persona + entrada promedio ──
    const puntualidadKpi = useMemo(() => {
        const now = new Date();
        const currentMonth = format(now, 'yyyy-MM');
        const myEmail = currentUser?.email;
        const meUser = users.find(u => u.email === myEmail);
        const scoped = attendanceRecords.filter(r => isAdmin || (meUser && r.userId === meUser.id));
        const monthRecs = scoped.filter(r => r.date?.startsWith(currentMonth));
        const total = monthRecs.length;
        const onTime = monthRecs.filter(r => !r.lateMinutes || r.lateMinutes === 0).length;
        const pct = total > 0 ? Math.round((onTime / total) * 100) : 0;
        // Entrada promedio
        const entries = monthRecs.map(r => r.entryTime).filter(Boolean).map(t => {
            const [h, m] = t.split(':').map(Number);
            return h * 60 + m;
        });
        const avgMin = entries.length > 0 ? Math.round(entries.reduce((a, c) => a + c, 0) / entries.length) : 0;
        const avgEntry = entries.length === 0 ? '--:--' :
            `${String(Math.floor(avgMin / 60)).padStart(2, '0')}:${String(avgMin % 60).padStart(2, '0')}`;
        // Por usuario
        const byUser = new Map();
        monthRecs.forEach(r => {
            const cur = byUser.get(r.userId) || { id: r.userId, name: r.userDisplayName, total: 0, onTime: 0 };
            cur.total++;
            if (!r.lateMinutes || r.lateMinutes === 0) cur.onTime++;
            byUser.set(r.userId, cur);
        });
        const perUser = Array.from(byUser.values()).map(u => ({
            ...u, pct: u.total > 0 ? Math.round((u.onTime / u.total) * 100) : 0
        })).sort((a, b) => b.pct - a.pct).slice(0, 4);
        return { pct, onTime, total, avgEntry, perUser };
    }, [attendanceRecords, users, currentUser, isAdmin]);

    // ── Helper: descuento por registro de asistencia ──
    const computeRecordDiscount = useCallback((record) => {
        const settings = employeeSettings[record.userId] || {};
        const salary = settings.monthlySalary || 0;
        const workHrsPerDay = effectiveDailyHours(settings, globalSettings) || DEFAULT_WORK_HOURS;
        const lostMin = (record.lateMinutes || 0) + (record.earlyExitMinutes || 0) + (record.breakExcessMinutes || 0);
        if (lostMin === 0 || salary === 0 || workHrsPerDay === 0) return 0;
        const minutesPerDay = workHrsPerDay * 60;
        const dailyRate = salary / 30;
        return (lostMin / minutesPerDay) * dailyRate;
    }, [employeeSettings, globalSettings]);

    // ── Summary date range ──
    const summaryDateRange = useMemo(() => {
        const today = new Date();
        if (summaryFilterMode === 'month') {
            const m = parseISO(attMonthFilter + '-01');
            return { start: format(startOfMonth(m), 'yyyy-MM-dd'), end: format(endOfMonth(m), 'yyyy-MM-dd') };
        }
        if (summaryFilterMode === 'week') {
            const ws = startOfWeek(today, { weekStartsOn: 1 });
            const we = endOfWeek(today, { weekStartsOn: 1 });
            return { start: format(ws, 'yyyy-MM-dd'), end: format(we, 'yyyy-MM-dd') };
        }
        if (summaryFilterMode === 'day') {
            return { start: summaryDateFrom, end: summaryDateFrom };
        }
        // custom
        return { start: summaryDateFrom, end: summaryDateTo };
    }, [summaryFilterMode, attMonthFilter, summaryDateFrom, summaryDateTo]);

    // ── Summary data ──
    // Todo el calculo vive en services/payrollCalc para que el Resumen y la
    // Nomina no puedan dar numeros distintos.
    const summaryData = useMemo(() => {
        const { start, end } = summaryDateRange;
        if (!start || !end) return [];
        const targetUsers = attUserFilter === 'ALL' ? payrollUsers : payrollUsers.filter(u => u.id === attUserFilter);
        return targetUsers.map(u => computeEmployeePeriod({
            user: u,
            settings: employeeSettings[u.id] || {},
            global: globalSettings,
            attendanceRecords,
            overtimeRecords: combinedOvertimeRecords,
            solicitudes: allSolicitudes,
            start,
            end,
            fullMonth: summaryFilterMode === 'month',
        }));
    }, [payrollUsers, attendanceRecords, combinedOvertimeRecords, employeeSettings, globalSettings, allSolicitudes, attUserFilter, summaryDateRange, summaryFilterMode]);

    // ══════════════════════════════════════
    // SETTINGS VIEW
    // ══════════════════════════════════════
    if (showSettings) {
        if (!isAdmin) { setShowSettings(false); return null; }
        return (
            <div className="p-6 h-full overflow-y-auto bg-gray-50">
                <div className="max-w-4xl mx-auto space-y-6">
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2"><FiSettings className="text-gray-600" /> Ajustes</h1>
                        <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-gray-200 rounded-full"><FiX size={20} /></button>
                    </div>
                    {/* ── Ajustes globales del workspace ── */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h2 className="text-[15px] font-bold text-gray-800 mb-1">Ajustes generales</h2>
                        <p className="text-gray-500 text-sm mb-4">Valores por defecto para toda la empresa. Cada colaborador puede tener su propia excepción abajo.</p>
                        <div className="flex items-end gap-6 flex-wrap">
                            <div>
                                <label className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Refrigerio por defecto</label>
                                <div className="flex items-center gap-1.5">
                                    <input type="number" min="0" step="15" className="w-20 px-2 py-1.5 border border-gray-200 rounded text-right text-sm font-mono focus:ring-1 focus:ring-sky-400 outline-none"
                                        value={editingGlobal.defaultBreakMinutes}
                                        onChange={(e) => setEditingGlobal(prev => ({ ...prev, defaultBreakMinutes: e.target.value }))} />
                                    <span className="text-xs text-gray-500">min / día</span>
                                </div>
                                <p className="text-[10.5px] text-gray-400 mt-1">No se paga: se descuenta de las horas trabajadas.</p>
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Sueldo mínimo</label>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-gray-500">S/</span>
                                    <input type="number" min="0" step="10" className="w-24 px-2 py-1.5 border border-gray-200 rounded text-right text-sm font-mono focus:ring-1 focus:ring-emerald-400 outline-none"
                                        value={editingGlobal.minimumWage}
                                        onChange={(e) => setEditingGlobal(prev => ({ ...prev, minimumWage: e.target.value }))} />
                                </div>
                                <p className="text-[10.5px] text-gray-400 mt-1">Referencia: avisa si alguien está por debajo.</p>
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Días laborables</label>
                                <div className="flex items-center gap-1">
                                    {DAY_INITIALS.map((ini, dow) => {
                                        const active = (editingGlobal.workDays || DEFAULT_WORK_DAYS).includes(dow);
                                        return (
                                            <button key={dow} type="button" title={DAY_LABELS[dow]}
                                                onClick={() => setEditingGlobal(prev => {
                                                    const cur = prev.workDays || DEFAULT_WORK_DAYS;
                                                    return { ...prev, workDays: cur.includes(dow) ? cur.filter(d => d !== dow) : [...cur, dow] };
                                                })}
                                                className={`w-7 h-7 rounded-lg text-[11px] font-bold transition-colors ${active ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                                                {ini}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[10.5px] text-gray-400 mt-1">Los días desmarcados salen como "no laboral", no como falta.</p>
                            </div>
                            <button onClick={handleSaveGlobal} className="bg-gray-800 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-gray-900 flex items-center gap-1.5">
                                <FiSave size={12} /> Guardar generales
                            </button>
                        </div>
                    </div>

                    <SecurityDevices security={security} users={users} />

                    {/* La configuración por persona vive en Recursos Humanos,
                        junto al resto de su ficha. Aquí sólo quedan los valores
                        que aplican a toda la empresa. */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h2 className="text-[15px] font-bold text-gray-800 mb-1">Condiciones de cada colaborador</h2>
                        <p className="text-gray-500 text-sm mb-4">
                            El sueldo, la tarifa, el horario, la jornada, el refrigerio y si entra en planilla
                            se configuran ahora en la ficha de cada persona, dentro de <b>Recursos Humanos</b>.
                            Así no hay que buscar los mismos datos en dos sitios.
                        </p>
                        <button onClick={() => { setShowSettings(false); setShowHR(true); }}
                            className="px-4 py-2 bg-sky-600 text-white rounded-xl text-[13px] font-bold hover:bg-sky-700 flex items-center gap-2">
                            <FiUsers size={14} /> Ir a Recursos Humanos
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ══════════════════════════════════════
    // COLLABORATOR VIEW (no admin)
    // ══════════════════════════════════════
    if (!isAdmin) {
        return <CollaboratorView
            currentUser={currentUser}
            users={users}
            attendanceRecords={attendanceRecords}
            combinedOvertimeRecords={combinedOvertimeRecords}
            userRates={userRates}
            employeeSettings={employeeSettings}
            globalSettings={globalSettings}
            todayRecord={todayRecord}
            handleMarkEntry={handleMarkEntry}
            handleMarkExit={handleMarkExit}
            handleMarkBreakStart={handleMarkBreakStart}
            handleMarkBreakEnd={handleMarkBreakEnd}
            computeRecordDiscount={computeRecordDiscount}
            mySolicitudes={mySolicitudes}
            handleCreateSolicitud={handleCreateSolicitud}
            handleCancelSolicitud={handleCancelSolicitud}
            attMonthFilter={attMonthFilter}
            setAttMonthFilter={setAttMonthFilter}
        />;
    }

    // ══════════════════════════════════════
    // ADMIN "VER COMO" COLABORADOR (panel personal completo)
    // ══════════════════════════════════════
    if (isAdmin && viewAsUserId) {
        const target = users.find(u => u.id === viewAsUserId);
        if (target) {
            const todayStr = format(new Date(), 'yyyy-MM-dd');
            const targetToday = attendanceRecords.find(r => r.userId === target.id && r.date === todayStr) || null;
            const targetAsUser = { uid: target.id, email: target.email, displayName: target.displayName, photoURL: target.photoURL };
            return (
                <div className="h-full flex flex-col bg-gray-50">
                    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-3 shrink-0">
                        <span className="text-[11px] font-bold text-amber-700 uppercase whitespace-nowrap">👁 Viendo como:</span>
                        <div className="w-60"><UserSwitcher users={users} value={viewAsUserId} onChange={setViewAsUserId} /></div>
                        <button onClick={() => setViewAsUserId(null)} className="ml-auto text-xs font-bold text-amber-700 hover:underline whitespace-nowrap">← Volver a vista equipo</button>
                    </div>
                    <div className="flex-1 min-h-0">
                        <CollaboratorView
                            currentUser={targetAsUser}
                            users={users}
                            attendanceRecords={attendanceRecords}
                            combinedOvertimeRecords={combinedOvertimeRecords}
                            userRates={userRates}
                            employeeSettings={employeeSettings}
            globalSettings={globalSettings}
                            todayRecord={targetToday}
                            handleMarkEntry={handleMarkEntry}
                            handleMarkExit={handleMarkExit}
                            handleMarkBreakStart={handleMarkBreakStart}
                            handleMarkBreakEnd={handleMarkBreakEnd}
                            computeRecordDiscount={computeRecordDiscount}
                            mySolicitudes={mySolicitudes.filter(s => s.userId === target.id)}
                            handleCreateSolicitud={handleCreateSolicitud}
                            handleCancelSolicitud={handleCancelSolicitud}
                            attMonthFilter={attMonthFilter}
                            setAttMonthFilter={setAttMonthFilter}
                        />
                    </div>
                </div>
            );
        }
    }

    // ══════════════════════════════════════
    // MAIN VIEW (admin)
    // ══════════════════════════════════════
    return (
        <div className="h-full flex bg-gray-50">
            {/* === LEFT SIDEBAR (Hingenia estilo) === */}
            <aside className="hidden lg:flex w-[240px] xl:w-[260px] bg-white border-r border-gray-200 flex-col shrink-0 overflow-y-auto">
                {/* Brand */}
                <div className="px-5 pt-5 pb-5 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center shadow-md shadow-sky-500/20">
                            <span className="text-white text-[20px] font-extrabold leading-none">H</span>
                        </div>
                        <div>
                            <div className="text-[16px] font-extrabold tracking-tight leading-none text-gray-900">Hingenia</div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mt-1 font-semibold">Gestor Pro</div>
                        </div>
                    </div>
                </div>

                {/* Selector "ver como" colaborador (con buscador) */}
                <div className="mx-3 mb-5 shrink-0">
                    <UserSwitcher users={users} value={viewAsUserId} onChange={setViewAsUserId} />
                </div>

                {/* MENU label */}
                <div className="px-6 mb-2 shrink-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Menú</div>
                </div>

                {/* Nav */}
                <nav className="flex-1 px-3 pb-4 space-y-0.5">
                    <button
                        onClick={() => { setRrhhTab('inicio'); setShowHR(false); setViewAsUserId(null); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors ${rrhhTab === 'inicio' && !showHR ? 'bg-sky-100 text-sky-700 font-bold' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                        <FiHome size={16} /> <span>Inicio</span>
                    </button>
                    <button onClick={() => setShowHR(true)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors ${showHR ? 'bg-sky-100 text-sky-700 font-bold' : 'text-gray-700 hover:bg-gray-50'}`}>
                        <FiUsers size={16} /> <span>Recursos Humanos</span>
                    </button>

                    {/* Tiempo (active parent + expanded children) */}
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] bg-sky-100 text-sky-700 font-bold cursor-pointer">
                        <FiClock size={16} /> <span>Tiempo</span>
                        <FiChevronsDown size={12} className="ml-auto opacity-70" />
                    </div>
                    <div className="ml-3 py-1 relative">
                        <div className="absolute left-[13px] top-0 bottom-2 w-px bg-gray-200" />
                        <button onClick={() => { setRrhhTab('control'); setShowHR(false); }}
                            className={`w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] font-bold ${rrhhTab === 'control' && !showHR ? 'bg-sky-50 text-sky-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                            <span className={`absolute left-[13px] top-1/2 w-2 h-px ${rrhhTab === 'control' && !showHR ? 'bg-sky-600' : 'bg-gray-200'}`} />
                            Control de Personal
                        </button>
                        <button 
                            onClick={() => { setRrhhTab('calendario'); setShowHR(false); setViewAsUserId(null); }}
                            className={`w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] font-bold ${rrhhTab === 'calendario' && !showHR ? 'bg-sky-50 text-sky-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                            <span className={`absolute left-[13px] top-1/2 w-2 h-px ${rrhhTab === 'calendario' && !showHR ? 'bg-sky-600' : 'bg-gray-200'}`} />
                            Calendario laboral
                        </button>
                        <button disabled className="w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] text-gray-400 font-medium cursor-not-allowed opacity-60">
                            <span className="absolute left-[13px] top-1/2 w-2 h-px bg-gray-200" />
                            Solicitudes
                            <span className="ml-auto text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-extrabold">PRONTO</span>
                        </button>
                        <button disabled className="w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] text-gray-400 font-medium cursor-not-allowed opacity-60">
                            <span className="absolute left-[13px] top-1/2 w-2 h-px bg-gray-200" />
                            Turnos &amp; Horarios
                            <span className="ml-auto text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-extrabold">PRONTO</span>
                        </button>
                        <button
                            onClick={() => { setRrhhTab('nomina'); setShowHR(false); setViewAsUserId(null); }}
                            className={`w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] font-bold ${rrhhTab === 'nomina' && !showHR ? 'bg-sky-50 text-sky-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                            <span className={`absolute left-[13px] top-1/2 w-2 h-px ${rrhhTab === 'nomina' && !showHR ? 'bg-sky-600' : 'bg-gray-200'}`} />
                            Nómina
                        </button>
                    </div>

                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-500 font-medium hover:bg-gray-50 cursor-not-allowed opacity-70">
                        <FiHeart size={16} /> <span>Clima laboral</span>
                    </button>
                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-500 font-medium hover:bg-gray-50 cursor-not-allowed opacity-70">
                        <FiMessageSquare size={16} /> <span>Comunidad</span>
                    </button>
                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-500 font-medium hover:bg-gray-50 cursor-not-allowed opacity-70">
                        <FiBarChart2 size={16} /> <span>Reportes</span>
                    </button>

                    {/* SISTEMA */}
                    <div className="px-3 mt-5 mb-2">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Sistema</div>
                    </div>
                    {isAdmin && (
                        <button onClick={() => setShowSettings(true)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-700 font-medium hover:bg-gray-50">
                            <FiSettings size={16} /> <span>Configuración</span>
                        </button>
                    )}
                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-500 font-medium hover:bg-gray-50 cursor-not-allowed opacity-70">
                        <FiHelpCircle size={16} /> <span>Ayuda &amp; soporte</span>
                    </button>
                </nav>

                {/* PRO TIP card */}
                <div className="mx-3 mb-3 p-3.5 rounded-2xl bg-gradient-to-br from-sky-600 to-blue-700 text-white relative overflow-hidden shrink-0">
                    <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10" />
                    <div className="relative">
                        <div className="text-[11px] font-bold text-blue-200 mb-0.5">PRO TIP</div>
                        <div className="text-[12.5px] font-semibold leading-tight mb-2">Configura horarios y tarifas en Ajustes para auto-calcular descuentos y horas extra.</div>
                        {isAdmin && (
                            <button onClick={() => setShowSettings(true)} className="text-[11px] bg-white/15 hover:bg-white/25 px-2.5 py-1 rounded-md font-bold transition-colors">
                                Activar →
                            </button>
                        )}
                    </div>
                </div>

                {/* User card */}
                <div className="px-3 py-3 border-t border-gray-200 shrink-0">
                    <div className="flex items-center gap-3 px-2 py-1.5 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors">
                        <div className="relative">
                            {currentUser?.photoURL ? (
                                <img src={currentUser.photoURL} className="w-9 h-9 rounded-full object-cover" alt="" />
                            ) : (
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-600 to-blue-700 flex items-center justify-center text-white text-[12px] font-extrabold">
                                    {(currentUser?.displayName || currentUser?.email || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold text-gray-900 truncate">{currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Usuario'}</div>
                            <div className="text-[11px] text-gray-400 truncate font-medium">{isAdmin ? 'Admin · Hingenia' : 'Colaborador'}</div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* === RIGHT CONTENT (un solo scroll vertical) === */}
            <div className="flex-1 overflow-y-auto relative">
            {/* Recursos Humanos / Documentación (overlay sobre el contenido) */}
            {showHR && (
                <div className="absolute inset-0 z-30 bg-gray-50 overflow-y-auto">
                    <HRDocumentation
                        currentUser={currentUser}
                        isAdmin={isAdmin}
                        users={users}
                        employeeSettings={employeeSettings}
                        globalSettings={globalSettings}
                        attendanceRecords={attendanceRecords}
                        onClose={() => setShowHR(false)} />
                </div>
            )}

            {/* PÁGINA DE INICIO del módulo RRHH (cumpleaños de asesores + resumen) */}
            {!showHR && rrhhTab === 'inicio' && (
                <div className="h-full">
                    <RRHHHome
                        users={users}
                        isAdmin={isAdmin}
                        currentUser={currentUser}
                        attendanceRecords={attendanceRecords}
                        onSetViewAs={setViewAsUserId}
                        onOpenDocs={() => setShowHR(true)}
                        onOpenControl={() => setRrhhTab('control')}
                    />
                </div>
            )}

            {/* CALENDARIO LABORAL */}
            {!showHR && rrhhTab === 'calendario' && (
                <div className="h-full">
                    <CalendarioLaboral
                        users={users}
                        attendanceRecords={attendanceRecords}
                        isAdmin={isAdmin}
                        currentUser={currentUser}
                        globalSettings={globalSettings}
                    />
                </div>
            )}

            {/* NÓMINA */}
            {!showHR && rrhhTab === 'nomina' && (
                <div className="h-full overflow-y-auto">
                    <PayrollPage
                        currentUser={currentUser}
                        isAdmin={isAdmin}
                        users={users}
                        payrollUsers={payrollUsers}
                        employeeSettings={employeeSettings}
                        globalSettings={globalSettings}
                        attendanceRecords={attendanceRecords}
                        overtimeRecords={combinedOvertimeRecords}
                        allSolicitudes={allSolicitudes}
                    />
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold">Registrar Pago</h3>
                            <button onClick={() => setShowPaymentModal(false)} className="p-1 hover:bg-gray-100 rounded-full"><FiX /></button>
                        </div>
                        <div className="space-y-4">
                            <div className="bg-emerald-50 p-4 rounded-xl text-center">
                                <p className="text-sm text-emerald-800">Total a Pagar</p>
                                <p className="text-2xl font-bold text-emerald-600">S/ {totalMoney.toFixed(2)}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Comprobante</label>
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center cursor-pointer relative hover:bg-gray-50">
                                    <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => setPaymentFile(e.target.files[0])} />
                                    <FiUpload className="text-gray-400 w-6 h-6 mb-1" />
                                    <span className="text-xs text-gray-500">{paymentFile ? paymentFile.name : 'Click para subir'}</span>
                                </div>
                            </div>
                            <button onClick={handleRegisterPayment} disabled={isRegisteringPayment || !paymentFile}
                                className="w-full bg-sky-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-sky-700 disabled:opacity-50">
                                {isRegisteringPayment ? 'Registrando...' : 'Confirmar Pago'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* === DASHBOARD PRINCIPAL (Control de Personal tabs + Hero + header) — solo cuando rrhhTab = 'control' === */}
            {!showHR && rrhhTab === 'control' && (
            <>

            {/* Header (restaurado) */}
            <div className="px-6 lg:px-10 pt-6 pb-2">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2.5">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold capitalize">
                                {format(new Date(), "EEEE · d 'de' MMMM · yyyy", { locale: es })}
                            </span>
                            <span className="w-1 h-1 bg-gray-300 rounded-full" />
                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-bold bg-emerald-100 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En jornada
                            </span>
                        </div>
                        <h1 className="text-3xl md:text-[34px] font-extrabold leading-[1.05] tracking-tight text-gray-900">
                            ¡Hola, {((currentUser?.displayName || currentUser?.email || '').split(' ')[0] || '').split('@')[0] || 'colaborador'}!
                        </h1>
                        <p className="text-sm text-gray-500 mt-2 font-medium">
                            {isAdmin ? (
                                <>
                                    Hoy hay <span className="text-gray-900 font-bold">{todayStats.active} {todayStats.active === 1 ? 'persona activa' : 'personas activas'}</span>
                                    {' · '}<span className="text-orange-700 font-bold">{todayStats.late} {todayStats.late === 1 ? 'con tardanza' : 'con tardanza'}</span>
                                    {' · '}<span className="text-gray-900 font-bold">{todayStats.withOvertime} con horas extra</span>
                                </>
                            ) : (
                                <>Control de Personal · Asistencia, horas extra y nómina</>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <button onClick={exportToExcel} className="px-3.5 py-2 text-xs md:text-sm bg-white border border-gray-200 hover:border-gray-300 rounded-xl flex items-center gap-2 font-semibold text-gray-700 transition-colors">
                                <FiDownload size={14} /> Exportar
                            </button>
                        )}
                        {isAdmin && (
                            <button onClick={() => setShowSettings(true)} className="px-3.5 py-2 text-xs md:text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-xl flex items-center gap-2 font-bold transition-colors shadow-sm">
                                <FiSettings size={14} /> Ajustes
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* HERO (siempre visible: Live Timer + 3 KPIs) */}
            <HeroSection {...{
                currentUser, users, isAdmin, todayRecord,
                handleMarkEntry, handleMarkExit, handleMarkBreakStart, handleMarkBreakEnd, breakFor,
                otKpis, tardanzasKpi, puntualidadKpi, computeRecordDiscount,
                getSchedule: (userId) => {
                    const s = employeeSettings[userId] || {};
                    return {
                        entry: s.scheduledEntry || DEFAULT_ENTRY, exit: s.scheduledExit || DEFAULT_EXIT,
                        flexible: s.scheduleMode === 'flexible',
                        workHours: effectiveDailyHours(s, globalSettings) || DEFAULT_WORK_HOURS,
                    };
                }
            }} />

            {/* Tabs (pill style) */}
            <div className="px-6 lg:px-10 pt-4 pb-2 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    {[
                        { id: 'asistencia', label: 'Asistencia', icon: FiLogIn, count: filteredAttendance.length },
                        { id: 'horas-extra', label: 'Horas Extra', icon: FiClock, count: filteredRecords.length },
                        { id: 'resumen', label: 'Resumen', icon: FiBarChart2 },
                    ].map(tab => {
                        const active = activeTab === tab.id;
                        return (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition-all border ${
                                    active
                                        ? 'bg-sky-50 border-sky-500 text-sky-700 font-bold'
                                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                }`}>
                                <tab.icon size={14} />
                                {tab.label}
                                {typeof tab.count === 'number' && (
                                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold tabular-nums ${
                                        active ? 'bg-white text-sky-600' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Content (scroll está en el wrapper externo) */}
            <div className="px-6 lg:px-10 pt-4 pb-10">
                {activeTab === 'asistencia' && <AttendanceTab
                    {...{ users, isAdmin, filteredAttendance,
                        attUserFilter, setAttUserFilter, attMonthFilter, setAttMonthFilter, attAvailableMonths,
                        handleAddManualAttendance, handleAddFalta,
                        manualAttUser, setManualAttUser, manualAttDate, setManualAttDate,
                        manualAttEntry, setManualAttEntry, manualAttExit, setManualAttExit,
                        deleteAttendanceRecord, updateAttendanceRecord, existingRecord, employeeSettings,
                        computeRecordDiscount, userRates, breakFor, globalSettings,
                        registerFaltaFor, allSolicitudes, payrollUsers,
                        getSchedule: (userId) => {
                            const s = employeeSettings[userId] || {};
                            return { entry: s.scheduledEntry || DEFAULT_ENTRY, exit: s.scheduledExit || DEFAULT_EXIT, flexible: s.scheduleMode === 'flexible' };
                        } }} />}

                {activeTab === 'horas-extra' && <OvertimeTab
                    {...{ currentUser, users, payrollUsers, isAdmin, selectedUser, setSelectedUser, date, setDate,
                        startTime, setStartTime, endTime, setEndTime, userRates,
                        handleAddRecord, filteredRecords, handleDelete, totalHours, totalMoney,
                        userFilter, setUserFilter, monthFilter, setMonthFilter, availableMonths,
                        exportToExcel, isSpecificView, paymentRecord, showPaymentModal, setShowPaymentModal }} />}

                {activeTab === 'resumen' && <SummaryTab
                    {...{ summaryData, attMonthFilter, setAttMonthFilter, attAvailableMonths,
                        attUserFilter, setAttUserFilter, users, payrollUsers, isAdmin, currentUser,
                        summaryFilterMode, setSummaryFilterMode, summaryDateFrom, setSummaryDateFrom,
                        summaryDateTo, setSummaryDateTo, summaryDateRange }} />}
            </div>
            </>
            )}{/* fin rrhhTab === 'control' */}
            </div>{/* /flex-1 right content wrapper */}
        </div>
    );
};

// ══════════════════════════════════════
// HERO SECTION (siempre visible encima de tabs)
// ══════════════════════════════════════
const HeroSection = ({ currentUser, users, isAdmin, todayRecord,
    handleMarkEntry, handleMarkExit, handleMarkBreakStart, handleMarkBreakEnd, otKpis, tardanzasKpi, puntualidadKpi,
    computeRecordDiscount, getSchedule, breakFor }) => {

    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const me = users.find(u => u.email === currentUser?.email);
    const mySchedule = me ? getSchedule(me.id) : { entry: DEFAULT_ENTRY, exit: DEFAULT_EXIT, flexible: false };
    const myBreakMinutes = me ? breakFor(me.id) : 0;
    const [entryH, entryM] = mySchedule.entry.split(':').map(Number);
    const [exitH, exitM] = mySchedule.exit.split(':').map(Number);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const entryMinutes = entryH * 60 + entryM;
    const exitMinutes = exitH * 60 + exitM;
    // Horario irregular: puede marcar entrada a la hora que quiera, sin
    // ventana de +-horas contra un horario fijo que no tiene.
    const canMarkEntry = mySchedule.flexible || (nowMinutes >= (entryMinutes - 120) && nowMinutes <= (entryMinutes + 240));
    const isWeekendDay = now.getDay() === 0;

    const elapsedDisplay = useMemo(() => {
        if (!todayRecord?.entryTime) return '00:00:00';
        const [eh, em] = todayRecord.entryTime.split(':').map(Number);
        const startMs = new Date(now);
        startMs.setHours(eh, em, 0, 0);
        const diff = now.getTime() - startMs.getTime();
        if (diff < 0) return '00:00:00';
        const totalSec = Math.floor(diff / 1000);
        const hh = Math.floor(totalSec / 3600);
        const mm = Math.floor((totalSec % 3600) / 60);
        const ss = totalSec % 60;
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    }, [now, todayRecord]);

    // Horario irregular: no hay hora de salida pactada, la cuenta regresiva y
    // la barra de progreso se miden contra sus horas del día (desde que marcó
    // entrada), no contra un reloj fijo.
    const targetMinutes = Math.round((mySchedule.workHours || DEFAULT_WORK_HOURS) * 60);
    const [todayEntryH, todayEntryM] = (todayRecord?.entryTime || '0:0').split(':').map(Number);
    const todayEntryMinutes = todayEntryH * 60 + todayEntryM;
    const estimatedExitMinutes = mySchedule.flexible ? todayEntryMinutes + targetMinutes : exitMinutes;
    const estimatedExitStr = `${String(Math.floor((estimatedExitMinutes % 1440) / 60)).padStart(2, '0')}:${String(estimatedExitMinutes % 60).padStart(2, '0')}`;

    const remainingDisplay = useMemo(() => {
        if (!todayRecord?.entryTime || todayRecord?.exitTime) return '--:--';
        const remainingMin = estimatedExitMinutes - nowMinutes;
        if (remainingMin <= 0) return mySchedule.flexible ? 'Completo' : 'Excedido';
        const h = Math.floor(remainingMin / 60);
        const m = remainingMin % 60;
        return `${h}h ${m}m`;
    }, [todayRecord, estimatedExitMinutes, nowMinutes, mySchedule.flexible]);

    let progressPct = 0;
    if (todayRecord?.entryTime) {
        const span = estimatedExitMinutes - todayEntryMinutes;
        if (span > 0) progressPct = Math.max(0, Math.min(100, ((nowMinutes - todayEntryMinutes) / span) * 100));
    }

    const sparkPath = useMemo(() => {
        const W = 240, H = 50, pad = 4;
        const data = otKpis?.last7 || [];
        if (data.length === 0) return { line: '', area: '', last: [W - pad, H - pad] };
        const max = Math.max(1, ...data.map(d => d.hours));
        const dx = (W - 2 * pad) / Math.max(1, data.length - 1);
        const points = data.map((d, i) => {
            const x = pad + i * dx;
            const y = H - pad - (d.hours / max) * (H - 2 * pad);
            return [x, y];
        });
        const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
        const area = `${line} L${points[points.length - 1][0].toFixed(1)},${H} L${points[0][0].toFixed(1)},${H} Z`;
        return { line, area, last: points[points.length - 1] };
    }, [otKpis]);

    return (
        <div className="px-6 lg:px-10 pt-4 pb-2">
            <div className="grid grid-cols-12 gap-5">
                {/* Live Timer */}
                <div className="col-span-12 lg:col-span-5 rounded-3xl p-6 relative overflow-hidden text-white shadow-xl shadow-blue-900/10 min-h-[420px]"
                    style={{ background: 'linear-gradient(135deg, #1E293B 0%, #1E40AF 60%, #2563EB 130%)' }}>
                    <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-blue-500/30 blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-10 w-48 h-48 rounded-full bg-blue-400/20 blur-3xl pointer-events-none" />
                    <div className="relative h-full flex flex-col">
                        <div className="flex items-center justify-between mb-5">
                            <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur px-2.5 py-1 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                <span className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-200 font-bold">Tu marcación</span>
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-mono px-2 py-0.5 border border-white/15 rounded-md font-bold">
                                {me?.displayName ? me.displayName.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() : '--'}
                            </div>
                        </div>
                        {!todayRecord?.entryTime ? (
                            <>
                                <div className="font-extrabold text-[60px] md:text-[64px] tabular-nums text-white leading-none tracking-tight">
                                    {formatDate12s(now)}
                                </div>
                                <p className="text-blue-200 text-[13px] font-bold mt-3 mb-6 capitalize">
                                    {format(now, "EEEE, d 'de' MMMM", { locale: es })} · {mySchedule.flexible
                                        ? `Horario flexible · ${mySchedule.workHours}h/día`
                                        : <>Horario {formatHM12(mySchedule.entry)} – {formatHM12(mySchedule.exit)}</>}
                                </p>
                                {canMarkEntry && !isWeekendDay ? (
                                    <button onClick={handleMarkEntry} className="w-full bg-white text-blue-700 py-3 rounded-xl text-sm font-extrabold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 shadow-lg active:scale-95">
                                        <FiLogIn size={16} /> Marcar Entrada
                                    </button>
                                ) : (
                                    <div className="bg-orange-500/20 border border-orange-400/30 rounded-xl px-4 py-3">
                                        <p className="text-orange-200 text-xs font-bold flex items-center gap-2"><FiAlertTriangle size={14} /> Fuera de horario</p>
                                        <p className="text-orange-200/80 text-[11px] mt-1">
                                            {isWeekendDay ? 'Hoy es domingo.' : `Tu entrada es a las ${formatHM12(mySchedule.entry)}.`} Contacta al admin para registro manual.
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : !todayRecord?.exitTime ? (
                            <>
                                <div className="font-extrabold text-[60px] md:text-[64px] tabular-nums text-white leading-none tracking-tight">
                                    {elapsedDisplay.split(':').map((p, i, a) => (
                                        <React.Fragment key={i}>
                                            {p}
                                            {i < a.length - 1 && <span className="text-white/40">:</span>}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2 mt-3 mb-6">
                                    <FiLogIn size={14} className="text-blue-300" />
                                    <span className="text-blue-300 text-[13px] font-bold">Entrada registrada a las {formatHM12(todayRecord.entryTime)}</span>
                                    {todayRecord.lateMinutes > 0 && (
                                        <span className="text-orange-300 text-[11px] font-bold ml-1">· {todayRecord.lateMinutes} min tarde</span>
                                    )}
                                </div>
                                <div className="mb-6">
                                    <div className="flex justify-between text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2 font-bold">
                                        <span>{formatHM12(todayRecord.entryTime)}</span>
                                        <span className="text-blue-300">ahora · {formatDate12(now)}</span>
                                        <span>{formatHM12(estimatedExitStr)}</span>
                                    </div>
                                    <div className="h-1.5 bg-white/10 rounded-full relative">
                                        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-300 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                                        <div className="absolute top-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-lg" style={{ left: `${progressPct}%`, transform: 'translate(-50%, -50%)', boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.5)' }} />
                                    </div>
                                    <div className="grid grid-cols-3 mt-4 gap-2">
                                        <div>
                                            <div className="text-[18px] font-extrabold tabular-nums text-white leading-none">{formatHM12(todayRecord.entryTime)}</div>
                                            <div className="text-[9.5px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Entrada</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-[18px] font-extrabold tabular-nums text-blue-300 leading-none">{remainingDisplay}</div>
                                            <div className="text-[9.5px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Restante</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[18px] font-extrabold tabular-nums text-white leading-none">{formatHM12(estimatedExitStr)}</div>
                                            <div className="text-[9.5px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Salida est.</div>
                                        </div>
                                    </div>
                                </div>
                                <BreakControls
                                    record={todayRecord}
                                    allowedMinutes={myBreakMinutes}
                                    onStart={handleMarkBreakStart}
                                    onEnd={handleMarkBreakEnd} />

                                <button onClick={handleMarkExit} className="w-full bg-white text-blue-700 py-3 rounded-xl text-sm font-extrabold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 shadow-lg active:scale-95">
                                    <FiLogOut size={16} /> Marcar Salida
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="font-extrabold text-[36px] md:text-[44px] tabular-nums text-white leading-none tracking-tight flex items-center gap-2">
                                    <FiCheckCircle className="text-emerald-300" size={36} />
                                    Jornada cerrada
                                </div>
                                <p className="text-blue-200 text-[13px] font-bold mt-3 mb-6 capitalize">{format(now, "EEEE, d 'de' MMMM", { locale: es })}</p>
                                <div className="grid grid-cols-3 gap-3 bg-white/[0.08] backdrop-blur rounded-2xl p-4 border border-white/10">
                                    <div>
                                        <div className="text-[22px] font-extrabold tabular-nums text-white leading-none">{formatHM12(todayRecord.entryTime)}</div>
                                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Entrada</div>
                                    </div>
                                    <div className="text-center border-x border-white/10 px-2">
                                        <div className="text-[22px] font-extrabold tabular-nums text-white leading-none">{formatHM12(todayRecord.exitTime)}</div>
                                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Salida</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[22px] font-extrabold tabular-nums text-blue-300 leading-none">{todayRecord.workedHours?.toFixed(1)}h</div>
                                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Trabajadas</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* KPI Stack (7/12) */}
                <div className="col-span-12 lg:col-span-7 grid grid-cols-1 gap-5">
                    {/* KPI 1: Horas Extra */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-6 relative overflow-hidden hover:border-sky-200 transition-colors">
                        <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-br from-blue-100 to-transparent rounded-bl-full pointer-events-none" />
                        <div className="relative">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-blue-100 text-blue-800 mb-2">
                                        <FiZap size={11} /> Prioridad uno
                                    </span>
                                    <h3 className="text-[15px] font-bold text-gray-900">Horas Extra acumuladas</h3>
                                    <p className="text-[12px] text-gray-500 font-medium capitalize">
                                        {format(new Date(), "MMMM yyyy", { locale: es })} · {isAdmin ? 'Equipo completo' : 'Tus horas'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-end gap-5 flex-wrap">
                                <div>
                                    <div className="text-[44px] font-extrabold leading-none tracking-tight text-gray-900 tabular-nums">
                                        {Math.floor(otKpis.currentHours)}<span className="text-[22px] text-gray-400">.{String(Math.round((otKpis.currentHours % 1) * 10))}</span>
                                    </div>
                                    <div className="text-[10px] text-gray-500 mt-1.5 uppercase tracking-[0.18em] font-bold">horas en el mes</div>
                                </div>
                                <div className="flex-1 min-w-[140px] self-stretch flex flex-col justify-end pb-1">
                                    <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-1 font-bold">Últimos 7 días</div>
                                    <svg viewBox="0 0 240 50" className="w-full h-12">
                                        <defs>
                                            <linearGradient id="otGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#2563EB" stopOpacity="0.35" />
                                                <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                                            </linearGradient>
                                        </defs>
                                        <path d={sparkPath.area} fill="url(#otGrad)" />
                                        <path d={sparkPath.line} stroke="#2563EB" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx={sparkPath.last[0]} cy={sparkPath.last[1]} r="3.5" fill="#2563EB" />
                                        <circle cx={sparkPath.last[0]} cy={sparkPath.last[1]} r="6" fill="#2563EB" opacity="0.25" />
                                    </svg>
                                </div>
                                <div className="border-l border-gray-200 pl-5 py-1">
                                    <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-0.5 font-bold">A pagar</div>
                                    <div className="text-[24px] font-extrabold text-sky-700 leading-none tabular-nums">
                                        S/&nbsp;{Math.floor(otKpis.currentPay)}<span className="text-[15px] text-gray-500">.{String(Math.round((otKpis.currentPay % 1) * 100)).padStart(2, '0')}</span>
                                    </div>
                                    {otKpis.pct !== null && (
                                        <div className="flex items-center gap-1.5 mt-2">
                                            <span className={`inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded-full font-bold ${otKpis.pct >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                                                <FiTrendingUp size={11} />{otKpis.pct >= 0 ? '+' : ''}{otKpis.pct}%
                                            </span>
                                            <span className="text-[10.5px] text-gray-500 font-medium">vs mes anterior</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {otKpis.topPersons.length > 0 && (
                                <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {otKpis.topPersons.map((p, i) => (
                                        <div key={p.id}>
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <div className={`w-2.5 h-2.5 rounded-sm ${['bg-sky-600','bg-blue-500','bg-blue-400','bg-blue-300'][i] || 'bg-blue-300'}`} />
                                                <span className="text-[10px] uppercase tracking-[0.1em] text-gray-500 font-bold truncate">{p.name?.split(' ')[0]}</span>
                                            </div>
                                            <div className="text-[14px] font-extrabold tabular-nums text-gray-900">{p.hours.toFixed(1)}h</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* KPI 2 + 3 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Tardanzas */}
                        <div className="bg-white border border-gray-200 rounded-3xl p-5 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100 to-transparent rounded-bl-full pointer-events-none" />
                            <div className="relative">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-orange-100 text-orange-800 mb-2">
                                    <FiAlertTriangle size={11} /> Descuentos
                                </span>
                                <h3 className="text-[14px] font-bold text-gray-900">Tardanzas del mes</h3>
                                <div className="flex items-end gap-3 mt-3 mb-4">
                                    <div>
                                        <div className="text-[34px] font-extrabold tabular-nums text-gray-900 leading-none">
                                            {tardanzasKpi.totalMinutes}<span className="text-[13px] text-gray-400 ml-1">min</span>
                                        </div>
                                        <div className="text-[10px] text-gray-500 mt-1.5 uppercase tracking-[0.18em] font-bold">{tardanzasKpi.count} {tardanzasKpi.count === 1 ? 'incidencia' : 'incidencias'}</div>
                                    </div>
                                    <div className="flex-1 border-l border-gray-200 pl-3">
                                        <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-0.5 font-bold">Descontado</div>
                                        <div className="text-[18px] font-extrabold text-red-600 leading-none tabular-nums">- S/ {tardanzasKpi.totalDiscount.toFixed(2)}</div>
                                    </div>
                                </div>
                                {tardanzasKpi.top2.length > 0 ? (
                                    <div className="pt-3 border-t border-gray-100">
                                        <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-2 font-bold flex items-center justify-between">
                                            <span>Top tardanzas</span>
                                            <span>min · S/</span>
                                        </div>
                                        <div className="space-y-2">
                                            {tardanzasKpi.top2.map(r => {
                                                const disc = computeRecordDiscount(r);
                                                return (
                                                    <div key={r.id} className="flex items-center gap-2.5">
                                                        {r.userPhotoURL
                                                            ? <img src={r.userPhotoURL} alt="" className="w-7 h-7 rounded-full object-cover" />
                                                            : <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-[10px] font-extrabold">{(r.userDisplayName || '?').charAt(0)}</div>}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-[12px] font-bold text-gray-900 truncate">{r.userDisplayName?.split(' ').slice(0, 2).join(' ')}</div>
                                                            <div className="text-[10px] text-gray-400 font-medium">{format(parseISO(r.date), 'd MMM', { locale: es })}</div>
                                                        </div>
                                                        <div className="text-right shrink-0">
                                                            <div className="text-[11.5px] font-extrabold tabular-nums text-orange-700">{r.lateMinutes} min</div>
                                                            {disc > 0 && <div className="text-[10px] font-bold tabular-nums text-red-600">-S/ {disc.toFixed(2)}</div>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="pt-3 border-t border-gray-100 text-[11px] text-emerald-700 font-bold flex items-center gap-1.5">
                                        <FiCheckCircle size={12} /> Sin tardanzas este mes
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Puntualidad */}
                        <div className="bg-white border border-gray-200 rounded-3xl p-5 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-100 to-transparent rounded-bl-full pointer-events-none" />
                            <div className="relative">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-100 text-emerald-800 mb-2">
                                    <FiCheckCircle size={11} /> Entrada
                                </span>
                                <h3 className="text-[14px] font-bold text-gray-900">Puntualidad {isAdmin ? 'del equipo' : ''}</h3>
                                <div className="flex items-end gap-3 mt-3 mb-4">
                                    <div>
                                        <div className="text-[34px] font-extrabold tabular-nums text-gray-900 leading-none">
                                            {puntualidadKpi.pct}<span className="text-[13px] text-gray-400">%</span>
                                        </div>
                                        <div className="text-[10px] text-gray-500 mt-1.5 uppercase tracking-[0.18em] font-bold">{puntualidadKpi.onTime} / {puntualidadKpi.total} a tiempo</div>
                                    </div>
                                    <div className="flex-1 border-l border-gray-200 pl-3">
                                        <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-0.5 font-bold">Entrada prom.</div>
                                        <div className="text-[18px] font-extrabold text-gray-900 leading-none tabular-nums">{puntualidadKpi.avgEntry}</div>
                                    </div>
                                </div>
                                {puntualidadKpi.perUser.length > 0 ? (
                                    <div className="pt-3 border-t border-gray-100">
                                        <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-2 font-bold">Por persona</div>
                                        <div className="space-y-2">
                                            {puntualidadKpi.perUser.map(u => (
                                                <div key={u.id}>
                                                    <div className="flex justify-between text-[11.5px] mb-1">
                                                        <span className="font-semibold text-gray-900 truncate">{u.name?.split(' ')[0]}</span>
                                                        <span className={`font-extrabold tabular-nums ${u.pct >= 80 ? 'text-emerald-600' : u.pct >= 50 ? 'text-orange-600' : 'text-red-600'}`}>{u.pct}%</span>
                                                    </div>
                                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className={`h-full rounded-full transition-all ${u.pct >= 80 ? 'bg-emerald-500' : u.pct >= 50 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${u.pct}%` }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="pt-3 border-t border-gray-100 text-[11px] text-gray-400 italic">Sin marcaciones este mes</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ══════════════════════════════════════
// TAB: ASISTENCIA
// ══════════════════════════════════════
const AttendanceTab = ({ users, isAdmin, filteredAttendance,
    attUserFilter, setAttUserFilter, attMonthFilter, setAttMonthFilter, attAvailableMonths,
    handleAddManualAttendance, handleAddFalta,
    manualAttUser, setManualAttUser, manualAttDate, setManualAttDate,
    manualAttEntry, setManualAttEntry, manualAttExit, setManualAttExit, deleteAttendanceRecord,
    updateAttendanceRecord, existingRecord, employeeSettings, getSchedule,
    computeRecordDiscount, userRates, breakFor, globalSettings,
    registerFaltaFor, allSolicitudes, payrollUsers = [] }) => {

    const [editingRow, setEditingRow] = useState(null); // { id, entry, exit }
    const [estadoFilter, setEstadoFilter] = useState('todos');

    const handleSaveRowEdit = async (record) => {
        if (!editingRow) return;
        const schedule = getSchedule(record.userId);
        const settings = employeeSettings[record.userId] || {};
        const isFlexible = settings.scheduleMode === 'flexible';
        const entry = editingRow.entry || record.entryTime;
        const exit = editingRow.exit || null;
        const breakStart = editingRow.breakStart || null;
        const breakEnd = editingRow.breakEnd || null;
        const allowedBreak = breakFor(record.userId);
        const draft = { breakStart, breakEnd };
        const worked = (entry && exit) ? netWorkedHours(entry, exit, effectiveBreakMinutes(draft, allowedBreak)) : 0;
        let late = 0, earlyEntry = 0, earlyExit = 0, otMinutes = 0;
        if (isFlexible) {
            // Horario irregular: se compara lo trabajado contra sus horas del
            // día, no contra una hora de entrada/salida pactada.
            if (entry && exit) {
                const workHrsPerDay = effectiveDailyHours(settings, globalSettings) || DEFAULT_WORK_HOURS;
                const flex = calcFlexibleDayMinutes(worked, workHrsPerDay);
                earlyExit = flex.shortfallMinutes;
                otMinutes = flex.overtimeMinutes;
            }
        } else {
            late = entry ? calcLateMinutes(entry, schedule.entry) : 0;
            earlyEntry = entry ? calcEarlyEntryMinutes(entry, schedule.entry) : 0;
            earlyExit = exit ? calcEarlyExitMinutes(exit, schedule.exit) : 0;
            otMinutes = exit ? calcOvertimeMinutes(exit, schedule.exit) : 0;
        }
        try {
            await updateAttendanceRecord(record.id, {
                entryTime: entry, exitTime: exit,
                breakStart, breakEnd,
                manualEdit: true, // corregido a mano: no lo pise el sello del servidor
                breakExcessMinutes: breakExcessMinutes(draft, allowedBreak),
                lateMinutes: late, earlyEntryMinutes: earlyEntry, earlyExitMinutes: earlyExit,
                workedHours: worked, overtimeMinutes: earlyEntry + otMinutes,
                status: late > 0 ? 'late' : earlyExit > 0 ? 'early-exit' : 'on-time',
            });
            toast.success('Registro actualizado');
            setEditingRow(null);
        } catch { toast.error('Error al actualizar'); }
    };

    // Filter by estado on top of filteredAttendance
    const visibleRecords = filteredAttendance.filter(r => {
        if (estadoFilter === 'todos') return true;
        if (estadoFilter === 'falta') return r.status === 'falta';
        if (r.status === 'falta') return false;
        if (estadoFilter === 'puntual') return !r.lateMinutes || r.lateMinutes === 0;
        if (estadoFilter === 'tarde') return (r.lateMinutes || 0) > 0;
        return true;
    });

    // Recorre el mes completo, no sólo los días con registro: así una ausencia
    // se ve como FALTA en vez de desaparecer, y los días de descanso salen
    // marcados como no laborales para que nadie los confunda con una falta.
    const recordsByDate = useMemo(() => {
        const groups = [];
        if (!attMonthFilter) return groups;
        const [y, m] = attMonthFilter.split('-').map(Number);
        if (!y || !m) return groups;

        const byDate = new Map();
        for (const r of visibleRecords) {
            if (!byDate.has(r.date)) byDate.set(r.date, []);
            byDate.get(r.date).push(r);
        }

        // Sólo se considera ausente a quien está configurado como colaborador
        // (tiene ajustes de sueldo/horario). Si no, saldría media agenda de la app.
        const roster = payrollUsers.filter(u => employeeSettings[u.id] && (attUserFilter === 'ALL' || u.id === attUserFilter));
        const includeMissing = estadoFilter === 'todos' || estadoFilter === 'falta';
        const includeNonWorking = estadoFilter === 'todos';

        const start = startOfMonth(new Date(y, m - 1));
        const end = endOfMonth(new Date(y, m - 1));
        const todayStr = format(new Date(), 'yyyy-MM-dd');

        for (const d of eachDayOfInterval({ start, end })) {
            const ds = format(d, 'yyyy-MM-dd');
            if (ds > todayStr) continue; // el futuro todavía no es falta
            const records = byDate.get(ds) || [];
            const working = isWorkingDay(d, globalSettings);
            const holidayName = getHolidayName(ds, globalSettings);

            if (!working || (holidayName && records.length === 0)) {
                if (includeNonWorking || records.length > 0) {
                    groups.push({ date: ds, dow: d.getDay(), nonWorking: true, holidayName, records, missing: [] });
                }
                continue;
            }

            // El día en curso no se marca como falta: todavía pueden marcar.
            let missing = [];
            if (includeMissing && ds < todayStr && !holidayName) {
                const present = new Set(records.map(r => r.userId));
                missing = roster.filter(u => !present.has(u.id));
            }
            if (records.length === 0 && missing.length === 0) continue;
            groups.push({ date: ds, dow: d.getDay(), nonWorking: false, holidayName, records, missing });
        }
        return groups.reverse(); // lo más reciente primero
    }, [visibleRecords, payrollUsers, employeeSettings, attUserFilter, attMonthFilter, estadoFilter, globalSettings]);

    // Justificaciones ya enviadas, para no ofrecer registrar una falta que el
    // colaborador ya explicó.
    const justifiedKeys = useMemo(() => {
        const set = new Set();
        (allSolicitudes || []).forEach(sol => {
            if (sol.type === 'justificacion' && sol.date && sol.status !== 'rechazada') set.add(`${sol.userId}|${sol.date}`);
        });
        return set;
    }, [allSolicitudes]);

    // Footer totals
    const totals = useMemo(() => {
        let hrs = 0, ot = 0, otPay = 0, disc = 0;
        for (const r of visibleRecords) {
            hrs += r.workedHours || 0;
            const otH = (r.overtimeMinutes || 0) / 60;
            ot += otH;
            otPay += otH * (userRates[r.userId] || 0);
            disc += computeRecordDiscount(r);
        }
        return { hrs, ot, otPay, disc };
    }, [visibleRecords, userRates, computeRecordDiscount]);

    return (
        <div className="space-y-5">
            {/* === HERO removido: ahora vive arriba de los tabs en HeroSection === */}

            {/* === ADMIN MANUAL ENTRY === */}
            {isAdmin && (
                <div className="bg-white border border-gray-200 border-l-4 border-l-sky-600 rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center">
                            <FiLock size={14} />
                        </div>
                        <div>
                            <h3 className="text-[13px] font-bold leading-tight text-gray-900">Registro manual</h3>
                            <p className="text-[11.5px] text-gray-500 leading-tight mt-0.5 font-medium">Solo administradores · queda registrado en el log de auditoría</p>
                        </div>
                    </div>
                    {existingRecord && existingRecord.entryTime && !existingRecord.exitTime && (
                        <div className="mb-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                            <FiAlertTriangle size={14} className="text-amber-500 shrink-0" />
                            <span><b>{existingRecord.userDisplayName}</b> ya marcó entrada ({formatHM12(existingRecord.entryTime)}) el {existingRecord.date} pero no registró salida. Solo completa la <b>Salida</b> para actualizar.</span>
                        </div>
                    )}
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Colaborador</label>
                            <UserPicker
                                users={payrollUsers}
                                value={manualAttUser}
                                onChange={setManualAttUser}
                                placeholder="Seleccionar..."
                                buttonClassName="px-3 py-2 text-[13px] bg-gray-50 border border-gray-200 rounded-xl hover:border-sky-300 focus:outline-none focus:border-sky-600" />
                        </div>
                        <div className="col-span-6 md:col-span-3">
                            <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Fecha</label>
                            <input type="date" value={manualAttDate} onChange={e => setManualAttDate(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold text-gray-900 focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                            <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Entrada</label>
                            {existingRecord && existingRecord.entryTime && !existingRecord.exitTime ? (
                                <div className="w-full px-3 py-2.5 text-[13px] bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl font-mono font-bold text-center">{formatHM12(existingRecord.entryTime)}</div>
                            ) : (
                                <div className="w-full px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-xl flex justify-center">
                                    <Time12Input allowEmpty value={manualAttEntry} onChange={setManualAttEntry} />
                                </div>
                            )}
                        </div>
                        <div className="col-span-6 md:col-span-2">
                            <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Salida</label>
                            <div className="w-full px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-xl flex justify-center">
                                <Time12Input allowEmpty value={manualAttExit} onChange={setManualAttExit} />
                            </div>
                        </div>
                        <div className="col-span-6 md:col-span-1 flex items-end gap-1.5">
                            <button onClick={handleAddManualAttendance} className="flex-1 px-3 py-2.5 text-[13px] bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-xl flex items-center justify-center gap-1.5 transition-colors"
                                title={existingRecord && existingRecord.entryTime && !existingRecord.exitTime ? 'Actualizar salida' : 'Agregar registro'}>
                                {existingRecord && existingRecord.entryTime && !existingRecord.exitTime ? <FiCheck size={14} /> : <FiPlus size={14} />}
                            </button>
                            <button onClick={handleAddFalta} className="px-2.5 py-2.5 text-[11px] bg-white border border-red-300 hover:bg-red-50 text-red-700 font-bold rounded-xl flex items-center justify-center gap-1 transition-colors"
                                title="Marcar como falta (sin entrada/salida)">
                                <FiX size={13} />
                            </button>
                        </div>
                    </div>
                    <p className="text-[10.5px] text-gray-400 mt-2 font-medium">
                        <span className="inline-flex items-center gap-1"><FiPlus size={10} className="text-sky-600" /> Agregar</span>
                        <span className="mx-2">·</span>
                        <span className="inline-flex items-center gap-1"><FiX size={10} className="text-red-600" /> Marcar falta (sin entrada/salida)</span>
                    </p>
                </div>
            )}

            {/* === FILTERS === */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold mr-1">Filtrar</div>
                    {isAdmin && (
                        <UserPicker
                            users={payrollUsers}
                            value={attUserFilter}
                            onChange={setAttUserFilter}
                            allowAll allValue="ALL" allLabel="Todos"
                            className="min-w-[190px]"
                            buttonClassName="bg-white border border-gray-200 rounded-full pl-1.5 pr-2.5 py-1 text-[12.5px] hover:border-sky-300" />
                    )}
                    <div className="inline-flex items-center bg-white border border-gray-200 rounded-full pl-3 pr-1 py-1 hover:border-sky-300 transition-colors">
                        <FiCalendar size={12} className="text-gray-500 mr-1.5" />
                        <select value={attMonthFilter} onChange={e => setAttMonthFilter(e.target.value)} className="text-[12.5px] bg-transparent border-0 focus:ring-0 focus:outline-none font-semibold text-gray-700 pr-1 capitalize cursor-pointer">
                            {attAvailableMonths.map(m => <option key={m} value={m}>{format(parseISO(m + '-01'), 'MMMM yyyy', { locale: es })}</option>)}
                        </select>
                    </div>
                    <div className="inline-flex items-center bg-white border border-gray-200 rounded-full pl-3 pr-1 py-1 hover:border-sky-300 transition-colors">
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${estadoFilter === 'puntual' ? 'bg-emerald-500' : estadoFilter === 'tarde' ? 'bg-orange-500' : 'bg-gray-300'}`} />
                        <select value={estadoFilter} onChange={e => setEstadoFilter(e.target.value)} className="text-[12.5px] bg-transparent border-0 focus:ring-0 focus:outline-none font-semibold text-gray-700 pr-1 cursor-pointer">
                            <option value="todos">Todos los estados</option>
                            <option value="puntual">Puntual</option>
                            <option value="tarde">Tarde</option>
                            <option value="falta">Faltas</option>
                        </select>
                    </div>
                    {(attUserFilter !== 'ALL' || estadoFilter !== 'todos') && (
                        <button onClick={() => { setAttUserFilter('ALL'); setEstadoFilter('todos'); }} className="px-3 py-1.5 text-[12px] bg-white border border-gray-200 border-dashed rounded-full inline-flex items-center gap-1.5 hover:border-red-400 hover:text-red-600 text-gray-500 transition-colors font-semibold">
                            <FiX size={11} /> Limpiar
                        </button>
                    )}
                </div>
                <div className="text-[12px] text-gray-500 font-semibold">
                    <span className="font-extrabold text-gray-900 tabular-nums">{visibleRecords.length}</span> registros
                </div>
            </div>

            {/* === TABLE === */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Colaborador</th>
                                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Fecha</th>
                                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-sky-700 font-extrabold bg-sky-50/60">
                                    <span className="inline-flex items-center gap-1.5"><FiLogIn size={11} /> Entrada</span>
                                </th>
                                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Salida</th>
                                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Trabajadas</th>
                                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-sky-700 font-extrabold bg-sky-50/60">
                                    <span className="inline-flex items-center gap-1.5"><FiClock size={11} /> H. Extra</span>
                                </th>
                                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Estado</th>
                                {isAdmin && (
                                    <th className="text-right px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-sky-700 font-extrabold bg-sky-50/60">
                                        <span className="inline-flex items-center justify-end gap-1.5"><FiMinusCircle size={11} /> Descuento</span>
                                    </th>
                                )}
                                {isAdmin && <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold w-20">Acc.</th>}
                            </tr>
                        </thead>
                        <tbody className="text-[13px]">
                            {recordsByDate.length === 0 ? (
                                <tr><td colSpan={isAdmin ? 9 : 7} className="text-center py-12 text-gray-400 text-xs italic">No hay registros de asistencia</td></tr>
                            ) : recordsByDate.map((group, gi) => {
                                const [gy, gm, gd] = group.date.split('-').map(Number);
                                const groupDate = new Date(gy, gm - 1, gd);
                                return (
                                    <React.Fragment key={group.date}>
                                        <tr>
                                            <td colSpan={isAdmin ? 9 : 7} className={`px-5 ${gi > 0 ? 'pt-5' : 'pt-3'} pb-2 text-[10px] uppercase tracking-[0.18em] font-extrabold border-b border-gray-200 ${group.nonWorking ? 'bg-slate-100/70 text-slate-400' : 'bg-gray-50 text-gray-400'}`}>
                                                <span className="inline-flex items-center gap-2 capitalize">
                                                    <span className={`w-1.5 h-1.5 rounded-full ${group.nonWorking ? 'bg-slate-300' : 'bg-gray-300'}`} />
                                                    {format(groupDate, "d 'de' MMMM · EEEE", { locale: es })}
                                                    {group.holidayName ? (
                                                        <span className="normal-case tracking-normal font-semibold text-[10.5px] text-amber-600">
                                                            · Feriado: {group.holidayName} {group.records.length > 0 ? '(trabajado: pago doble)' : '(pagado, no cuenta como falta)'}
                                                        </span>
                                                    ) : group.nonWorking && (
                                                        <span className="normal-case tracking-normal font-semibold text-[10.5px] text-slate-400">
                                                            · Día no laboral (no cuenta como falta)
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                        </tr>

                                        {/* Colaboradores sin ninguna marcación ese día */}
                                        {(group.missing || []).map(u => {
                                            const already = justifiedKeys.has(`${u.id}|${group.date}`);
                                            return (
                                                <tr key={`falta-${group.date}-${u.id}`} className="border-b border-gray-100 bg-red-50/40 hover:bg-red-50/70 transition-colors">
                                                    <td className="px-5 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-[11px] overflow-hidden shrink-0 opacity-70">
                                                                {u.photoURL ? <img src={u.photoURL} alt="" className="w-full h-full object-cover grayscale" /> : (u.displayName || u.email).charAt(0).toUpperCase()}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="font-bold text-[13px] text-gray-700 truncate">{u.displayName || u.email}</div>
                                                                <div className="text-[10.5px] text-gray-400">Sin registro</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-3 text-[12px] text-gray-500 font-medium capitalize">
                                                        {format(groupDate, 'd MMM', { locale: es })}
                                                    </td>
                                                    <td colSpan={4} className="px-3 py-3 text-center text-[12px] text-gray-400 italic">
                                                        No marcó entrada ni salida
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        {already ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] font-bold bg-amber-100 text-amber-700">
                                                                <FiFileText size={10} /> Justificada
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] font-bold bg-red-100 text-red-700">
                                                                <FiX size={10} /> Falta
                                                            </span>
                                                        )}
                                                    </td>
                                                    {isAdmin && <td className="px-3 py-3 text-right text-gray-300 text-[12px]">—</td>}
                                                    {isAdmin && (
                                                        <td className="px-5 py-3 text-right">
                                                            <button onClick={() => registerFaltaFor(u.id, group.date)}
                                                                className="px-2 py-1 rounded-lg text-[10.5px] font-bold border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
                                                                title="Registrar la falta para que descuente en el resumen">
                                                                Registrar
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}

                                        {group.nonWorking && group.records.length === 0 && (group.missing || []).length === 0 && (
                                            <tr className="border-b border-gray-100">
                                                <td colSpan={isAdmin ? 9 : 7} className="px-5 py-2.5 text-center text-[11.5px] text-slate-400 font-medium bg-slate-50/40">
                                                    <span className="inline-flex items-center gap-1.5"><FiCoffee size={11} /> {group.holidayName ? 'Feriado · día pagado sin descuento' : 'Descanso · sin jornada programada'}</span>
                                                </td>
                                            </tr>
                                        )}

                                        {group.records.map(r => {
                                            const isEditing = editingRow?.id === r.id;
                                            const isLate = (r.lateMinutes || 0) > 0;
                                            const otMin = r.overtimeMinutes || 0;
                                            const otHrs = otMin / 60;
                                            const rate = userRates[r.userId] || 0;
                                            const otPay = otHrs * rate;
                                            const discount = computeRecordDiscount(r);
                                            const settings = employeeSettings[r.userId] || {};
                                            const workedPct = r.workedHours ? Math.min(100, (r.workedHours / (settings.workHoursPerDay || DEFAULT_WORK_HOURS)) * 100) : 0;
                                            const earlyMin = r.earlyExitMinutes || 0;

                                            return (
                                                <tr key={r.id} className={`border-b border-gray-100 transition-colors ${isEditing ? 'bg-sky-50/60' : isLate ? 'bg-orange-50/30 hover:bg-orange-50/50' : 'hover:bg-gray-50/50'}`}>
                                                    {/* Colaborador */}
                                                    <td className="px-5 py-3.5">
                                                        <div className="flex items-center gap-3">
                                                            {r.userPhotoURL
                                                                ? <img src={r.userPhotoURL} alt="" className="w-9 h-9 rounded-full object-cover" />
                                                                : <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-[11px] font-extrabold">{(r.userDisplayName || '?').charAt(0).toUpperCase()}</div>}
                                                            <div className="min-w-0">
                                                                <div className="font-bold text-gray-900 text-[13px] truncate">{r.userDisplayName}</div>
                                                                {isAdmin && settings.monthlySalary > 0 && (
                                                                    <div className="text-[11px] text-gray-400 font-medium">S/ {settings.monthlySalary}/mes</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {/* Fecha */}
                                                    <td className="px-3 py-3.5 text-gray-500 font-mono text-[12px] font-semibold">
                                                        <div>{format(parseISO(r.date), 'd MMM', { locale: es })}</div>
                                                        <div className="text-[10px] text-gray-400 capitalize">{format(parseISO(r.date), 'EEEE', { locale: es })}</div>
                                                    </td>
                                                    {/* Entrada */}
                                                    <td className={`px-3 py-3.5 ${isLate ? 'bg-orange-50' : 'bg-sky-50/30'}`}>
                                                        {isEditing ? (
                                                            <Time12Input allowEmpty value={editingRow.entry} onChange={(v) => setEditingRow(prev => ({ ...prev, entry: v }))} />
                                                        ) : (
                                                            <>
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`w-1.5 h-1.5 rounded-full ${isLate ? 'bg-orange-500' : 'bg-emerald-500'}`} />
                                                                    <span className={`font-mono font-extrabold text-[13px] ${isLate ? 'text-orange-700' : 'text-gray-900'}`}>{r.entryTime ? formatHM12(r.entryTime) : '-'}</span>
                                                                </div>
                                                                {r.entryTime && (
                                                                    <div className={`text-[10px] font-mono font-bold mt-0.5 flex items-center gap-1 ${isLate ? 'text-orange-700' : (r.earlyEntryMinutes || 0) > 0 ? 'text-sky-600' : 'text-emerald-600'}`}>
                                                                        {isLate
                                                                            ? <><FiAlertTriangle size={9} /> {r.lateMinutes} min tarde</>
                                                                            : (r.earlyEntryMinutes || 0) > 0
                                                                                ? <>{r.earlyEntryMinutes} min antes</>
                                                                                : 'puntual'}
                                                                    </div>
                                                                )}
                                                                {r.entryDeviceLabel && (
                                                                    <div className={`text-[9.5px] font-semibold mt-1 flex items-center gap-1 truncate ${r.entryIsMobile ? 'text-amber-600' : 'text-gray-400'}`}
                                                                        title={`Marcó entrada desde: ${r.entryDeviceLabel}`}>
                                                                        {r.entryIsMobile ? <FiSmartphone size={9} className="shrink-0" /> : <FiMonitor size={9} className="shrink-0" />}
                                                                        <span className="truncate">{r.entryDeviceLabel}</span>
                                                                    </div>
                                                                )}
                                                                {r.clockDrift?.campo === 'entrada' && (
                                                                    <div className="text-[9.5px] font-bold text-red-600 mt-1 flex items-center gap-1"
                                                                        title={`El dispositivo marcó ${formatHM12(r.clockDrift.marcado)} pero el servidor lo registró a las ${formatHM12(r.clockDrift.servidor)}.`}>
                                                                        <FiAlertTriangle size={9} /> servidor: {formatHM12(r.clockDrift.servidor)}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                    {/* Salida */}
                                                    <td className="px-3 py-3.5">
                                                        {isEditing ? (
                                                            <Time12Input allowEmpty value={editingRow.exit} onChange={(v) => setEditingRow(prev => ({ ...prev, exit: v }))} />
                                                        ) : r.exitTime ? (
                                                            <>
                                                                <span className="font-mono font-semibold text-gray-900 text-[13px]">{formatHM12(r.exitTime)}</span>
                                                                {earlyMin > 0 && <div className="text-[10px] font-mono font-bold text-orange-600 mt-0.5">-{earlyMin} min</div>}
                                                                {r.exitDeviceLabel && (
                                                                    <div className={`text-[9.5px] font-semibold mt-1 flex items-center gap-1 truncate ${r.exitIsMobile ? 'text-amber-600' : 'text-gray-400'}`}
                                                                        title={`Marcó salida desde: ${r.exitDeviceLabel}`}>
                                                                        {r.exitIsMobile ? <FiSmartphone size={9} className="shrink-0" /> : <FiMonitor size={9} className="shrink-0" />}
                                                                        <span className="truncate">{r.exitDeviceLabel}</span>
                                                                    </div>
                                                                )}
                                                                {r.clockDrift?.campo === 'salida' && (
                                                                    <div className="text-[9.5px] font-bold text-red-600 mt-1 flex items-center gap-1"
                                                                        title={`El dispositivo marcó ${formatHM12(r.clockDrift.marcado)} pero el servidor lo registró a las ${formatHM12(r.clockDrift.servidor)}${r.clockDrift.exacto ? '' : ' (aproximado: sello de la última escritura del día)'}. Diferencia: ${r.clockDrift.diff} min.`}>
                                                                        <FiAlertTriangle size={9} />
                                                                        servidor: {formatHM12(r.clockDrift.servidor)}{r.clockDrift.exacto ? '' : ' ~'}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="font-mono text-gray-300 text-[13px]">—</span>
                                                                <div className="text-[10px] text-gray-400 mt-0.5 font-medium">sin marcar</div>
                                                            </>
                                                        )}
                                                    </td>
                                                    {/* Trabajadas */}
                                                    <td className="px-3 py-3.5">
                                                        {isEditing ? (
                                                            <div className="space-y-1">
                                                                <div className="text-[9px] uppercase tracking-wide text-gray-400 font-bold flex items-center gap-1">
                                                                    <FiCoffee size={9} /> Refrigerio
                                                                </div>
                                                                <Time12Input allowEmpty value={editingRow.breakStart} onChange={(v) => setEditingRow(prev => ({ ...prev, breakStart: v }))} />
                                                                <Time12Input allowEmpty value={editingRow.breakEnd} onChange={(v) => setEditingRow(prev => ({ ...prev, breakEnd: v }))} />
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {r.workedHours ? (
                                                                    <div className="font-mono font-bold text-gray-900 text-[13px]">{r.workedHours.toFixed(1)}h</div>
                                                                ) : r.entryTime ? (
                                                                    <span className="font-mono text-gray-300 text-[13px]">—</span>
                                                                ) : null}

                                                                {/* Refrigerio: horas marcadas, duración y exceso */}
                                                                {r.breakStart ? (
                                                                    <div className={`mt-1 ${(r.breakExcessMinutes || 0) > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                                                        <div className="text-[10px] font-mono font-semibold flex items-center gap-1 whitespace-nowrap">
                                                                            <FiCoffee size={9} className="shrink-0" />
                                                                            {formatHM12(r.breakStart)}
                                                                            {r.breakEnd ? ` → ${formatHM12(r.breakEnd)}` : ' → en curso'}
                                                                        </div>
                                                                        {r.breakTakenMinutes !== null && r.breakTakenMinutes !== undefined && (
                                                                            <div className="text-[9.5px] font-bold mt-0.5">
                                                                                {r.breakTakenMinutes} min
                                                                                <span className="text-gray-400 font-medium"> de {r.breakAllowedMinutes}</span>
                                                                                {(r.breakExcessMinutes || 0) > 0 && ` · +${r.breakExcessMinutes} de más`}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : r.entryTime && r.breakAllowedMinutes > 0 ? (
                                                                    <div className="text-[9.5px] text-gray-300 font-medium mt-1 flex items-center gap-1 whitespace-nowrap">
                                                                        <FiCoffee size={9} className="shrink-0" />
                                                                        {r.breakAllowedMinutes} min · sin marcar
                                                                    </div>
                                                                ) : null}

                                                                {r.workedHours ? (
                                                                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1.5">
                                                                        <div className="h-full bg-sky-600 rounded-full" style={{ width: `${workedPct}%` }} />
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </td>
                                                    {/* H. Extra */}
                                                    <td className="px-3 py-3.5 bg-sky-50/30">
                                                        {otHrs > 0 ? (
                                                            <>
                                                                <span className="font-mono font-extrabold text-sky-700 text-[13px]">+{otHrs.toFixed(2)}h</span>
                                                                {otPay > 0 && <div className="text-[10px] font-mono font-bold text-sky-600 mt-0.5">S/ {otPay.toFixed(2)}</div>}
                                                            </>
                                                        ) : <span className="font-mono text-gray-300">—</span>}
                                                    </td>
                                                    {/* Estado */}
                                                    <td className="px-3 py-3.5">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold ${
                                                            r.status === 'falta' ? 'bg-red-100 text-red-800' :
                                                            isLate ? 'bg-orange-100 text-orange-800' :
                                                            earlyMin > 0 ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-emerald-100 text-emerald-800'
                                                        }`}>
                                                            {r.status === 'falta' ? <><FiX size={10} /> Falta</> :
                                                             isLate ? <><FiAlertTriangle size={10} /> Tarde</> :
                                                             earlyMin > 0 ? <><FiAlertTriangle size={10} /> Salida temp.</> :
                                                             <><FiCheck size={10} /> Puntual</>}
                                                        </span>
                                                    </td>
                                                    {/* Descuento (admin only) */}
                                                    {isAdmin && (
                                                        <td className={`px-3 py-3.5 text-right ${discount > 0 ? 'bg-red-50' : 'bg-sky-50/30'}`}>
                                                            {discount > 0 ? (
                                                                <>
                                                                    <div className="font-mono font-extrabold text-red-700 text-[12.5px]">- S/ {discount.toFixed(2)}</div>
                                                                    <div className="text-[10px] font-mono font-semibold text-red-500 mt-0.5">por {(r.lateMinutes || 0) + (r.earlyExitMinutes || 0)} min</div>
                                                                </>
                                                            ) : <span className="text-gray-300 font-mono">—</span>}
                                                        </td>
                                                    )}
                                                    {/* Acc */}
                                                    {isAdmin && (
                                                        <td className="px-5 py-3.5 text-right">
                                                            <div className="inline-flex items-center gap-0.5">
                                                                {isEditing ? (
                                                                    <>
                                                                        <button onClick={() => handleSaveRowEdit(r)} className="p-1.5 hover:bg-emerald-50 rounded-lg text-emerald-600" title="Guardar"><FiCheck size={14} /></button>
                                                                        <button onClick={() => setEditingRow(null)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400" title="Cancelar"><FiX size={14} /></button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <button onClick={() => setEditingRow({ id: r.id, entry: r.entryTime || '', exit: r.exitTime || '', breakStart: r.breakStart || '', breakEnd: r.breakEnd || '' })} className="p-1.5 hover:bg-sky-50 rounded-lg text-gray-400 hover:text-sky-600" title="Editar"><FiSettings size={13} /></button>
                                                                        <button onClick={() => { if (window.confirm('¿Eliminar?')) deleteAttendanceRecord(r.id); }} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600" title="Eliminar"><FiTrash2 size={13} /></button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Footer totals */}
                {visibleRecords.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50 px-5 py-3.5 flex items-center justify-between flex-wrap gap-3">
                        <div className="text-[12px] text-gray-500 font-semibold">
                            Mostrando <span className="font-extrabold text-gray-900 tabular-nums">{visibleRecords.length}</span> registros
                        </div>
                        <div className="flex items-center gap-4 flex-wrap text-[12px]">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Σ Horas</span>
                                <span className="font-mono font-extrabold text-gray-900">{totals.hrs.toFixed(1)}h</span>
                            </div>
                            <div className="w-px h-4 bg-gray-200" />
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] uppercase tracking-[0.15em] text-sky-700 font-bold">Σ Extra</span>
                                <span className="font-mono font-extrabold text-sky-700">+{totals.ot.toFixed(1)}h</span>
                                {totals.otPay > 0 && <span className="text-[11px] text-sky-600 font-bold">· S/ {totals.otPay.toFixed(2)}</span>}
                            </div>
                            {isAdmin && totals.disc > 0 && (
                                <>
                                    <div className="w-px h-4 bg-gray-200" />
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] uppercase tracking-[0.15em] text-red-600 font-bold">Σ Descuento</span>
                                        <span className="font-mono font-extrabold text-red-600">- S/ {totals.disc.toFixed(2)}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ══════════════════════════════════════
// TAB: HORAS EXTRA (existing)
// ══════════════════════════════════════
const OvertimeTab = ({ currentUser, users, payrollUsers = [], isAdmin, selectedUser, setSelectedUser, date, setDate,
    startTime, setStartTime, endTime, setEndTime, userRates,
    handleAddRecord, filteredRecords, handleDelete, totalHours, totalMoney,
    userFilter, setUserFilter, monthFilter, setMonthFilter, availableMonths,
    exportToExcel, isSpecificView, paymentRecord, setShowPaymentModal }) => {

    // Stats
    const uniqueCollaborators = useMemo(() => new Set(filteredRecords.map(r => r.userId)).size, [filteredRecords]);
    const avgRate = useMemo(() => {
        const rates = filteredRecords.map(r => r.hourlyRate || 0).filter(Boolean);
        return rates.length > 0 ? rates.reduce((a, c) => a + c, 0) / rates.length : 0;
    }, [filteredRecords]);
    const previewHours = (startTime && endTime) ? calculateHours(startTime, endTime) : 0;
    const previewPay = previewHours * (selectedUser ? (userRates[selectedUser.id] || 0) : 0);

    // Group by date for separators
    const recordsByDate = useMemo(() => {
        const sorted = [...filteredRecords].sort((a, b) => b.date.localeCompare(a.date));
        const groups = [];
        const seen = new Map();
        for (const r of sorted) {
            if (!seen.has(r.date)) {
                const idx = groups.push({ date: r.date, records: [] }) - 1;
                seen.set(r.date, idx);
            }
            groups[seen.get(r.date)].records.push(r);
        }
        return groups;
    }, [filteredRecords]);

    return (
    <div className="space-y-5">
        {/* === KPIs ROW === */}
        <div className="grid grid-cols-12 gap-5">
            {/* Total Horas */}
            <div className="col-span-12 md:col-span-5 bg-white border border-gray-200 rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-br from-blue-100 to-transparent rounded-bl-full pointer-events-none" />
                <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-blue-100 text-blue-800">
                            <FiClock size={11} /> Total acumulado
                        </span>
                        <span className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.15em] capitalize">
                            {monthFilter === 'ALL' ? 'Histórico' : format(parseISO(monthFilter + '-01'), 'MMMM yyyy', { locale: es })}
                        </span>
                    </div>
                    <div className="text-[48px] font-extrabold leading-none tracking-tight text-gray-900 tabular-nums">
                        {Math.floor(totalHours)}<span className="text-[26px] text-gray-400">.{String(Math.round((totalHours % 1) * 10))}</span>
                        <span className="text-[19px] text-gray-500 font-bold ml-2">hrs</span>
                    </div>
                    <p className="text-[13px] text-gray-500 mt-3 font-medium">
                        Equivalente a <span className="text-gray-900 font-bold">{(totalHours / 8).toFixed(1)} jornadas</span> de 8h
                    </p>
                </div>
            </div>

            {/* Total a pagar */}
            <div className="col-span-12 md:col-span-4 bg-gradient-to-br from-sky-50 to-blue-100 border border-blue-200 rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-sky-600/10 blur-2xl pointer-events-none" />
                <div className="relative">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-blue-200/60 text-blue-900">
                        <FiDollarSign size={11} /> A pagar
                    </span>
                    <div className="text-[42px] font-extrabold text-blue-900 tabular-nums leading-none tracking-tight mt-3">
                        S/&nbsp;{Math.floor(totalMoney)}<span className="text-[24px] text-blue-700">.{String(Math.round((totalMoney % 1) * 100)).padStart(2, '0')}</span>
                    </div>
                    {isSpecificView && (
                        <div className="mt-3 flex items-center gap-2">
                            {paymentRecord ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-extrabold bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                                    <FiCheckCircle size={11} /> PAGADO
                                </span>
                            ) : (
                                <>
                                    <span className="inline-flex items-center gap-1 text-[11px] font-extrabold bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                                        <FiClock size={11} /> PENDIENTE
                                    </span>
                                    {isAdmin && (
                                        <button onClick={() => setShowPaymentModal(true)} className="text-[11px] font-bold bg-orange-500 hover:bg-orange-600 text-white px-2.5 py-1 rounded-full transition-colors">
                                            Marcar pagado
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Resumen */}
            <div className="col-span-12 md:col-span-3 bg-white border border-gray-200 rounded-3xl p-5">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.15em]">Resumen</span>
                </div>
                <div className="space-y-2.5">
                    <div className="flex items-end justify-between">
                        <span className="text-[12px] text-gray-500 font-semibold">Registros</span>
                        <span className="text-[18px] font-extrabold tabular-nums text-gray-900">{filteredRecords.length}</span>
                    </div>
                    <div className="h-px bg-gray-100" />
                    <div className="flex items-end justify-between">
                        <span className="text-[12px] text-gray-500 font-semibold">Tarifa prom.</span>
                        <span className="text-[18px] font-extrabold tabular-nums text-gray-900">S/ {avgRate.toFixed(2)}</span>
                    </div>
                    <div className="h-px bg-gray-100" />
                    <div className="flex items-end justify-between">
                        <span className="text-[12px] text-gray-500 font-semibold">Colaboradores</span>
                        <span className="text-[18px] font-extrabold tabular-nums text-gray-900">{uniqueCollaborators}</span>
                    </div>
                </div>
            </div>
        </div>

        {/* === NUEVO REGISTRO === */}
        <div className="bg-white border border-gray-200 border-l-4 border-l-sky-600 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center">
                    <FiPlus size={14} />
                </div>
                <div>
                    <h3 className="text-[13px] font-bold leading-tight text-gray-900">Registrar horas extra</h3>
                    <p className="text-[11.5px] text-gray-500 leading-tight mt-0.5 font-medium">Selecciona colaborador y rango horario · el monto se calcula automáticamente</p>
                </div>
            </div>
            <form onSubmit={handleAddRecord} className="grid grid-cols-12 gap-3">
                <div className="col-span-12 md:col-span-3">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Colaborador</label>
                    {isAdmin ? (
                        <UserPicker
                            users={payrollUsers}
                            value={selectedUser?.id || ''}
                            onChange={(id) => setSelectedUser(payrollUsers.find(u => u.id === id))}
                            placeholder="Seleccionar..."
                            buttonClassName="px-3 py-2 text-[13px] bg-gray-50 border border-gray-200 rounded-xl hover:border-sky-300 focus:outline-none focus:border-sky-600" />
                    ) : (
                        <div className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-semibold text-gray-700 truncate">
                            {currentUser?.displayName || currentUser?.email}
                        </div>
                    )}
                </div>
                <div className="col-span-6 md:col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Tarifa</label>
                    <div className={`px-3 py-2.5 text-[13px] rounded-xl font-mono font-extrabold flex items-center justify-between ${
                        selectedUser && userRates[selectedUser.id]
                            ? 'bg-sky-50 border border-sky-200 text-sky-900'
                            : 'bg-gray-50 border border-gray-200 text-gray-400'
                    }`}>
                        <span>{selectedUser && userRates[selectedUser.id] ? `S/ ${parseFloat(userRates[selectedUser.id]).toFixed(2)}` : 'S/ —'}</span>
                        <span className="text-[10px] font-bold opacity-70">/hr</span>
                    </div>
                </div>
                <div className="col-span-6 md:col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Fecha</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                </div>
                <div className="col-span-4 md:col-span-1">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Inicio</label>
                    <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full px-2 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono text-center font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                </div>
                <div className="col-span-4 md:col-span-1">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Fin</label>
                    <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full px-2 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono text-center font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                </div>
                <div className="col-span-4 md:col-span-1">
                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Total</label>
                    <div className={`w-full px-2 py-2.5 text-[13px] rounded-xl font-mono font-extrabold text-center ${
                        previewHours > 0 ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-gray-50 border border-gray-200 text-gray-300'
                    }`}>
                        {previewHours > 0 ? `${previewHours.toFixed(1)}h` : '0.0h'}
                    </div>
                </div>
                <div className="col-span-12 md:col-span-2 flex items-end">
                    <button type="submit" disabled={!selectedUser || !userRates[selectedUser?.id] || !startTime || !endTime}
                        className="w-full px-3 py-2.5 text-[13px] bg-sky-600 hover:bg-sky-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold rounded-xl flex items-center justify-center gap-1.5 transition-colors">
                        <FiCheck size={14} /> Registrar
                        {previewPay > 0 && <span className="text-[11px] opacity-80 ml-1">· S/ {previewPay.toFixed(2)}</span>}
                    </button>
                </div>
            </form>
        </div>

        {/* === FILTERS === */}
        <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold mr-1">Filtrar</div>
                {isAdmin && (
                    <UserPicker
                        users={payrollUsers}
                        value={userFilter}
                        onChange={setUserFilter}
                        allowAll allValue="ALL" allLabel="Todos"
                        className="min-w-[190px]"
                        buttonClassName="bg-white border border-gray-200 rounded-full pl-1.5 pr-2.5 py-1 text-[12.5px] hover:border-sky-300" />
                )}
                <div className="inline-flex items-center bg-white border border-gray-200 rounded-full pl-3 pr-1 py-1 hover:border-sky-300 transition-colors">
                    <FiCalendar size={12} className="text-gray-500 mr-1.5" />
                    <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="text-[12.5px] bg-transparent border-0 focus:ring-0 focus:outline-none font-semibold text-gray-700 pr-1 capitalize cursor-pointer">
                        <option value="ALL">Histórico</option>
                        {availableMonths.map(m => <option key={m} value={m}>{format(parseISO(m + '-01'), 'MMMM yyyy', { locale: es })}</option>)}
                    </select>
                </div>
                {(userFilter !== 'ALL' || monthFilter === 'ALL') && (
                    <button onClick={() => { setUserFilter('ALL'); setMonthFilter(format(new Date(), 'yyyy-MM')); }} className="px-3 py-1.5 text-[12px] bg-white border border-gray-200 border-dashed rounded-full inline-flex items-center gap-1.5 hover:border-red-400 hover:text-red-600 text-gray-500 transition-colors font-semibold">
                        <FiX size={11} /> Limpiar
                    </button>
                )}
            </div>
            <div className="flex items-center gap-3">
                <span className="text-[12px] text-gray-500 font-semibold"><span className="font-extrabold text-gray-900 tabular-nums">{filteredRecords.length}</span> registros</span>
                <button onClick={exportToExcel} className="px-3.5 py-1.5 text-[12.5px] bg-white border border-gray-200 hover:border-sky-300 rounded-full flex items-center gap-2 font-semibold text-gray-700 transition-colors">
                    <FiDownload size={13} /> Exportar
                </button>
            </div>
        </div>

        {/* === TABLE === */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Colaborador</th>
                            <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Fecha</th>
                            <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Horario</th>
                            <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Duración</th>
                            <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Tarifa</th>
                            <th className="text-right px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-sky-700 font-extrabold bg-sky-50/60">Monto</th>
                            <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold w-20">Acc.</th>
                        </tr>
                    </thead>
                    <tbody className="text-[13px]">
                        {recordsByDate.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-12 text-gray-400 text-xs italic">No hay registros</td></tr>
                        ) : recordsByDate.map((group, gi) => {
                            const [gy, gm, gd] = group.date.split('-').map(Number);
                            const groupDate = new Date(gy, gm - 1, gd);
                            return (
                                <React.Fragment key={group.date}>
                                    {gi > 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-5 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] text-gray-400 font-extrabold bg-gray-50 border-b border-gray-200">
                                                <span className="inline-flex items-center gap-2 capitalize">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                                                    {format(groupDate, "d 'de' MMMM · EEEE", { locale: es })}
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                    {group.records.map(record => {
                                        const isMyRecord = isAdmin || record.userEmail === currentUser?.email;
                                        return (
                                            <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                                                <td className="px-5 py-3.5">
                                                    <div className="flex items-center gap-3">
                                                        {record.userPhotoURL
                                                            ? <img src={record.userPhotoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                                                            : <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-[11px] font-extrabold">{(record.userDisplayName || '?').charAt(0).toUpperCase()}</div>}
                                                        <div className="font-bold text-gray-900 text-[13px]">{record.userDisplayName}</div>
                                                        {record.fromAttendance && (
                                                            <span className="text-[10px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded">AUTO</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3.5 text-gray-500 font-mono text-[12px] font-semibold">
                                                    {format(parseISO(record.date), 'd MMM yyyy', { locale: es })}
                                                </td>
                                                <td className="px-3 py-3.5">
                                                    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-md font-mono text-[12px] font-bold text-gray-900">
                                                        {record.startTime} → {record.endTime}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3.5">
                                                    <span className="font-mono font-extrabold text-gray-900 text-[13px]">{record.hours.toFixed(2)}h</span>
                                                </td>
                                                <td className="px-3 py-3.5">
                                                    <span className="font-mono text-gray-500 text-[12.5px] font-semibold">S/ {(record.hourlyRate || 0).toFixed(2)}</span>
                                                </td>
                                                <td className="px-3 py-3.5 text-right bg-sky-50/30">
                                                    <span className="font-mono font-extrabold text-sky-700 text-[13px]">
                                                        <MoneyMask visible={isMyRecord}>S/ {record.totalPay.toFixed(2)}</MoneyMask>
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3.5 text-right">
                                                    {(isAdmin || isMyRecord) && !record.fromAttendance && (
                                                        <button onClick={() => handleDelete(record.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600" title="Eliminar">
                                                            <FiTrash2 size={13} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {filteredRecords.length > 0 && (
                <div className="border-t border-gray-200 bg-gray-50 px-5 py-3.5 flex items-center justify-between flex-wrap gap-3">
                    <div className="text-[12px] text-gray-500 font-semibold">
                        Mostrando <span className="font-extrabold text-gray-900 tabular-nums">{filteredRecords.length}</span> registros
                    </div>
                    <div className="flex items-center gap-4 flex-wrap text-[12px]">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Σ Horas</span>
                            <span className="font-mono font-extrabold text-gray-900">{totalHours.toFixed(2)}h</span>
                        </div>
                        <div className="w-px h-4 bg-gray-200" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-sky-700 font-bold">Σ Monto</span>
                            <span className="font-mono font-extrabold text-sky-700">S/ {totalMoney.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
); };

// ══════════════════════════════════════
// CALCULADORA DEL DÍA: cómo se llegó a cada monto
// ══════════════════════════════════════
const CalcToggle = ({ open, onClick }) => (
    <button type="button" onClick={onClick} aria-expanded={open}
        title="Ver cómo se calcula" aria-label="Ver cómo se calcula"
        className="flex items-center justify-center w-[22px] h-[22px] rounded-md justify-self-end transition-colors"
        style={{ background: open ? 'var(--brand-soft)' : 'transparent', color: open ? 'var(--brand)' : 'var(--ink-3)' }}>
        <Calculator size={13} />
    </button>
);

const formatDuration = (minutes) => `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;

const CalcLine = ({ label, formula, amount, tone = 'var(--ink)', strong }) => (
    <div className="flex items-baseline justify-between gap-3 py-[3px]">
        <div className="min-w-0">
            <span style={{ color: 'var(--ink-2)', fontWeight: strong ? 700 : 600 }}>{label}</span>
            {formula && <div className="text-[12px]" style={{ color: 'var(--ink-3)' }}>{formula}</div>}
        </div>
        <span className="tabular-nums whitespace-nowrap" style={{ color: tone, fontWeight: strong ? 800 : 700 }}>{amount}</span>
    </div>
);

const DayCalc = ({ s, date }) => {
    const att = s.attendanceRecords.find(r => r.date === date) || null;
    const otRecords = s.overtimeRecords.filter(r => r.date === date);
    const isFalta = (s.faltaRecords || []).some(r => r.date === date) || (!att && (s.detectedFaltaDates || []).includes(date));
    const holidayWork = (s.holidaysWorked || []).find(h => h.date === date);
    const perMin = s.perMinuteRate || 0;
    const dayRate = s.dayRate || 0;
    const hourValue = perMin * 60;
    const { discounts, dayNet, lunch } = computeDayBreakdown({ att, otRecords, isFalta, perMinuteRate: perMin, dayRate, isFlexible: s.isFlexible, holidayPay: holidayWork?.amount || 0 });
    const money = (n) => `S/ ${n.toFixed(2)}`;
    const minuteFormula = (m) => `${m} min × S/ ${perMin.toFixed(4)}`;
    const discountLine = (d) => {
        if (d.kind === 'falta') return { label: 'Falta', formula: 'Se descuenta el día completo' };
        if (d.kind === 'late') return {
            label: 'Tardanza',
            formula: `${formatHM12(att.entryTime)} − ${formatHM12(att.scheduledEntry || s.schedEntry)} = ${minuteFormula(d.minutes)}`,
        };
        if (d.kind === 'early') return {
            label: s.isFlexible ? 'Horas faltantes' : 'Salida temprana',
            formula: s.isFlexible ? minuteFormula(d.minutes) : `${formatHM12(att.scheduledExit || s.schedExit)} − ${formatHM12(att.exitTime)} = ${minuteFormula(d.minutes)}`,
        };
        return { label: 'Exceso de refrigerio', formula: `${att.breakTakenMinutes} − ${att.breakAllowedMinutes} = ${minuteFormula(d.minutes)}` };
    };
    const heading = 'text-[10.5px] font-bold uppercase tracking-[0.05em] mb-1';
    return (
        <div className="rounded-[10px] p-3.5 my-1.5 text-[13px] tabular-nums" style={{ background: 'var(--panel-2)', border: '1px solid var(--line)' }}>
            <div className={heading} style={{ color: 'var(--ink-3)' }}>Valor del tiempo</div>
            <CalcLine label="Por día" formula={`Sueldo ${money(dayRate * 30)} ÷ 30 días`} amount={money(dayRate)} />
            <CalcLine label="Por hora" formula={`${money(dayRate)} ÷ ${s.workHrsPerDay} h de jornada`} amount={money(hourValue)} />
            <CalcLine label="Por minuto" formula={`${money(hourValue)} ÷ 60`} amount={`S/ ${perMin.toFixed(4)}`} />

            <div className="mt-2.5 pt-2" style={{ borderTop: '1px solid var(--line)' }}>
                <div className={heading} style={{ color: 'var(--ink-3)' }}>
                    Este día · {format(parseISO(date), 'dd MMM', { locale: es })}
                </div>
                <CalcLine label="Sueldo del día" amount={`+ ${money(dayRate)}`} />
                {discounts.map(d => {
                    const line = discountLine(d);
                    return <CalcLine key={d.kind} label={line.label} formula={line.formula} amount={`− ${money(d.amount)}`} tone="var(--bad)" />;
                })}
                {otRecords.map((ot, i) => (
                    <CalcLine key={ot.id || i} label="Hora extra"
                        formula={`${formatHM12(ot.startTime)} → ${formatHM12(ot.endTime)} = ${(ot.hours || 0).toFixed(2)} h × S/ ${(ot.hourlyRate || 0).toFixed(2)}`}
                        amount={`+ ${money(ot.totalPay || 0)}`} tone="var(--ok)" />
                ))}
                {holidayWork && (
                    <CalcLine label={`Feriado trabajado · ${holidayWork.name}`}
                        formula={holidayWork.pendingExit ? 'Sin salida marcada: no se puede calcular' : `${money(dayRate)} × ${Math.round(holidayWork.fraction * 100)}% de la jornada`}
                        amount={`+ ${money(holidayWork.amount)}`} tone="var(--ok)" />
                )}
                <div className="mt-1 pt-1" style={{ borderTop: '1px solid var(--line)' }}>
                    <CalcLine label="Total del día" amount={money(dayNet)} strong />
                </div>
            </div>

            {lunch && (
                <div className="mt-2.5 pt-2" style={{ borderTop: '1px solid var(--line)' }}>
                    <div className={heading} style={{ color: 'var(--ink-3)' }}>Refrigerio</div>
                    <CalcLine label="Tiempo marcado" formula={`${formatHM12(att.entryTime)} → ${formatHM12(att.exitTime)}`} amount={formatDuration(lunch.grossMinutes)} />
                    <CalcLine label="Horas netas"
                        formula={lunch.taken === null ? `Sin marcar: se asumen ${lunch.allowed} min` : `Marcó ${lunch.taken} min de ${lunch.allowed} min`}
                        amount={formatDuration(lunch.netMinutes)} />
                    {lunch.unrecognizedMinutes > 0 && (
                        <div className="mt-1.5 rounded-md px-2 py-1.5 leading-snug" style={{ background: 'var(--warn-soft)', color: 'var(--ink-2)' }}>
                            {lunch.taken === null ? 'Si no tomó refrigerio, ' : 'Lo que no tomó, '}
                            {lunch.unrecognizedMinutes} min equivalen a <b>{money(lunch.unrecognizedValue)}</b> que no se le pagan. Es informativo: no cambia el total del día.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ══════════════════════════════════════
// EQUIPO Y AVISOS DE UN REGISTRO DE ASISTENCIA
// ══════════════════════════════════════
const shortDevice = (label) => (label || '').replace(/^Windows · /, '');

const attendanceNotices = (att, s, users) => {
    const notices = [];
    if (att.createdBy && att.createdBy !== s.user.id) {
        const who = users.find(u => u.id === att.createdBy);
        const name = who?.email === ADMIN_EMAIL ? 'super admin' : (who?.displayName || who?.email || 'otra cuenta');
        const created = stampToHM(att.createdAt);
        const createdDate = stampToDate(att.createdAt);
        const when = created
            ? ` · ${createdDate && createdDate !== att.date ? `${format(parseISO(createdDate), 'dd MMM', { locale: es })} ` : ''}${formatHM12(created)}`
            : '';
        notices.push({ tone: 'warn', text: `Registrado por ${name}${when}` });
    }
    if (att.manualEdit) {
        notices.push({ tone: 'warn', text: 'Editado a mano' });
        const entrySrv = stampToHM(att.entryAt);
        if (entrySrv && minutesBetweenHM(entrySrv, att.entryTime) > DRIFT_TOLERANCE_MIN) notices.push({ tone: 'bad', text: `Marcó entrada ${formatHM12(entrySrv)}` });
        const exitSrv = stampToHM(att.exitAt);
        if (exitSrv && minutesBetweenHM(exitSrv, att.exitTime) > DRIFT_TOLERANCE_MIN) notices.push({ tone: 'bad', text: `Marcó salida ${formatHM12(exitSrv)}` });
    }
    if (att.clockDrift) {
        notices.push({ tone: 'bad', text: `Servidor ${att.clockDrift.campo}: ${formatHM12(att.clockDrift.servidor)}${att.clockDrift.exacto ? '' : ' ~'}` });
    }
    return notices;
};

const AttendanceSource = ({ att, s, users }) => {
    const entry = shortDevice(att.entryDeviceLabel);
    const exit = shortDevice(att.exitDeviceLabel);
    const notices = attendanceNotices(att, s, users);
    const toneStyle = {
        warn: { background: 'var(--warn-soft)', color: 'var(--warn)' },
        bad: { background: 'var(--bad-soft)', color: 'var(--bad)' },
    };
    return (
        <div className="min-w-0 pl-2">
            <div className="flex items-center gap-1 truncate" style={{ color: 'var(--ink-2)' }} title={`Entrada: ${entry || 'sin equipo'} · Salida: ${exit || 'sin equipo'}`}>
                <FiMonitor size={12} className="shrink-0" style={{ color: 'var(--ink-3)' }} />
                <span className="truncate">
                    {!entry && !exit ? <span style={{ color: 'var(--ink-3)' }}>Sin equipo</span>
                        : entry === exit ? entry
                            : `${entry || 'sin equipo'} → ${exit || 'sin equipo'}`}
                </span>
            </div>
            {notices.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-0.5">
                    {notices.map((n, i) => (
                        <span key={i} className="px-1.5 rounded text-[11px] font-semibold leading-[18px]" style={toneStyle[n.tone]}>{n.text}</span>
                    ))}
                </div>
            )}
        </div>
    );
};

// ══════════════════════════════════════
// CABECERA DE SECCION DEL DETALLE
// ══════════════════════════════════════
const SECTION_TONES = {
    brand: { soft: 'var(--brand-soft)', solid: 'var(--brand)' },
    warn: { soft: 'var(--warn-soft)', solid: 'var(--warn)' },
    bad: { soft: 'var(--bad-soft)', solid: 'var(--bad)' },
    ok: { soft: 'var(--ok-soft)', solid: 'var(--ok)' },
};

const SectionHeader = ({ icon: Icon, tone, title, subtitle, amount, open, onClick }) => {
    const c = SECTION_TONES[tone];
    return (
        <button type="button" onClick={onClick} aria-expanded={open}
            className="w-full flex items-center gap-3 px-5 py-2.5 text-left transition-opacity hover:opacity-90"
            style={{ background: c.soft }}>
            <span className="flex items-center justify-center w-[27px] h-[27px] rounded-lg shrink-0 text-white" style={{ background: c.solid }}>
                <Icon size={14} />
            </span>
            <span className="flex-1 min-w-0">
                <span className="block text-[13.5px] font-bold tracking-[-0.015em]" style={{ color: 'var(--ink)' }}>{title}</span>
                <span className="block text-[11.5px] truncate" style={{ color: 'var(--ink-2)' }}>{subtitle}</span>
            </span>
            {amount && <span className="text-[13.5px] font-bold tabular-nums whitespace-nowrap" style={{ color: c.solid }}>{amount}</span>}
            <ChevronDown size={16} className={`shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--ink-3)' }} />
        </button>
    );
};

// ══════════════════════════════════════
// TAB: RESUMEN
// ══════════════════════════════════════
const SummaryTab = ({ summaryData, attMonthFilter, setAttMonthFilter, attAvailableMonths, attUserFilter, setAttUserFilter, users, payrollUsers = [], isAdmin, currentUser,
    summaryFilterMode, setSummaryFilterMode, summaryDateFrom, setSummaryDateFrom, summaryDateTo, setSummaryDateTo, summaryDateRange }) => {
    const [expandedSections, setExpandedSections] = useState({});
    const [stateFilter, setStateFilter] = useState('todos'); // todos / activos / inactivos / tardanzas
    const toggleSection = (userId, section) => {
        const key = `${userId}-${section}`;
        setExpandedSections(prev => ({ ...prev, [key]: !(prev[key] ?? isSingle) }));
    };
    const isSectionOpen = (userId, section) => expandedSections[`${userId}-${section}`] ?? isSingle;
    // Calculadoras abiertas: se pueden tener varias para comparar días.
    const [openCalcs, setOpenCalcs] = useState({});
    const toggleCalc = (key) => setOpenCalcs(prev => ({ ...prev, [key]: !prev[key] }));

    const allMonths = useMemo(() => {
        const months = new Set();
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.add(format(d, 'yyyy-MM'));
        }
        attAvailableMonths.forEach(m => months.add(m));
        return Array.from(months).sort().reverse();
    }, [attAvailableMonths]);

    const filterModes = [
        { id: 'day', label: 'Día' },
        { id: 'week', label: 'Semana' },
        { id: 'month', label: 'Mes' },
        { id: 'custom', label: 'Personalizado' },
    ];

    const rangeLabelText = summaryDateRange ? `${format(parseISO(summaryDateRange.start), 'd MMM', { locale: es })} — ${format(parseISO(summaryDateRange.end), 'd MMM yyyy', { locale: es })}` : '';

    // Scoped data (respect user role) + state classification
    const scopedData = useMemo(() => {
        return summaryData
            .filter(s => isAdmin || s.user.email === currentUser?.email)
            .map(s => ({
                ...s,
                stateClass: s.daysWorked === 0 ? 'inactivos' : s.lateCount > 0 ? 'tardanzas' : 'activos'
            }));
    }, [summaryData, isAdmin, currentUser]);

    // Counts per state
    const stateCounts = useMemo(() => ({
        todos: scopedData.length,
        activos: scopedData.filter(s => s.daysWorked > 0).length,
        inactivos: scopedData.filter(s => s.daysWorked === 0).length,
        tardanzas: scopedData.filter(s => s.lateCount > 0).length,
    }), [scopedData]);

    // Apply state filter
    const filteredData = useMemo(() => {
        if (stateFilter === 'todos') return scopedData;
        if (stateFilter === 'activos') return scopedData.filter(s => s.daysWorked > 0);
        if (stateFilter === 'inactivos') return scopedData.filter(s => s.daysWorked === 0);
        if (stateFilter === 'tardanzas') return scopedData.filter(s => s.lateCount > 0);
        return scopedData;
    }, [scopedData, stateFilter]);

    // Con una sola persona filtrada: detalle a la izquierda y datos generales a la derecha.
    const isSingle = attUserFilter !== 'ALL' && filteredData.length === 1;

    // Totals across all scoped users
    const totals = useMemo(() => ({
        totalOTHours: scopedData.reduce((a, s) => a + s.totalOTHours, 0),
        totalOTPay: scopedData.reduce((a, s) => a + s.totalOTPay, 0),
        totalDiscount: scopedData.reduce((a, s) => a + s.discount, 0),
        totalNet: scopedData.reduce((a, s) => a + s.netPay, 0),
    }), [scopedData]);

    return (
    <div className="space-y-5">
        {/* === PERIOD HEADER === */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 font-bold">Período</div>
                    <div className="flex bg-gray-50 border border-gray-200 rounded-xl p-1">
                        {filterModes.map(m => (
                            <button key={m.id} onClick={() => setSummaryFilterMode(m.id)}
                                className={`px-3 py-1.5 text-[12px] rounded-lg font-bold transition-all ${
                                    summaryFilterMode === m.id
                                        ? 'bg-white text-sky-700 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-900'
                                }`}>
                                {m.label}
                            </button>
                        ))}
                    </div>
                    {summaryFilterMode === 'month' && (
                        <div className="inline-flex items-center bg-white border border-gray-200 rounded-xl pl-3 pr-1 py-1.5 hover:border-sky-300 transition-colors">
                            <FiCalendar size={13} className="text-sky-600 mr-1.5" />
                            <select value={attMonthFilter} onChange={e => setAttMonthFilter(e.target.value)} className="text-[12.5px] bg-transparent border-0 focus:ring-0 focus:outline-none font-bold text-gray-900 pr-1 capitalize cursor-pointer">
                                {allMonths.map(m => <option key={m} value={m}>{format(parseISO(m + '-01'), 'MMMM yyyy', { locale: es })}</option>)}
                            </select>
                        </div>
                    )}
                    {summaryFilterMode === 'day' && (
                        <input type="date" value={summaryDateFrom} onChange={e => setSummaryDateFrom(e.target.value)}
                            className="text-[12.5px] bg-white border border-gray-200 rounded-xl px-3 py-1.5 font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                    )}
                    {summaryFilterMode === 'week' && (
                        <span className="text-[12px] text-sky-800 font-bold bg-sky-50 border border-sky-200 px-2.5 py-1.5 rounded-xl">
                            Semana actual
                        </span>
                    )}
                    {summaryFilterMode === 'custom' && (
                        <div className="flex items-center gap-1.5">
                            <input type="date" value={summaryDateFrom} onChange={e => setSummaryDateFrom(e.target.value)}
                                className="text-[12.5px] bg-white border border-gray-200 rounded-xl px-2.5 py-1.5 font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                            <span className="text-gray-400 text-xs">a</span>
                            <input type="date" value={summaryDateTo} onChange={e => setSummaryDateTo(e.target.value)}
                                className="text-[12.5px] bg-white border border-gray-200 rounded-xl px-2.5 py-1.5 font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                        </div>
                    )}
                    <span className="text-[11px] text-gray-400 font-bold font-mono">{rangeLabelText}</span>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold">Total H. Extra</div>
                        <div className="font-extrabold text-gray-900 tabular-nums text-[18px]">{totals.totalOTHours.toFixed(1)}h</div>
                    </div>
                    <div className="w-px h-8 bg-gray-200" />
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-sky-700 font-bold">Total a Pagar</div>
                        <div className="font-extrabold text-sky-700 tabular-nums text-[20px]">S/ {totals.totalNet.toFixed(2)}</div>
                    </div>
                </div>
            </div>
        </div>

        {/* === FILTERS === */}
        {isAdmin && (
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold mr-1">Filtrar</div>
                    {[
                        { id: 'todos', label: 'Todos', dot: 'bg-gray-300' },
                        { id: 'activos', label: 'Activos', dot: 'bg-emerald-500' },
                        { id: 'inactivos', label: 'Sin marcaciones', dot: 'bg-gray-300' },
                        { id: 'tardanzas', label: 'Con tardanzas', dot: 'bg-orange-500' },
                    ].map(p => {
                        const active = stateFilter === p.id;
                        return (
                            <button key={p.id} onClick={() => setStateFilter(p.id)}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${
                                    active
                                        ? 'bg-sky-600 border-sky-600 text-white'
                                        : 'bg-white border-gray-200 text-gray-700 hover:border-sky-300'
                                }`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : p.dot}`} />
                                {p.label}
                                <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full font-bold tabular-nums ${
                                    active ? 'bg-white/20' : 'bg-gray-100 text-gray-500'
                                }`}>{stateCounts[p.id]}</span>
                            </button>
                        );
                    })}
                    <UserPicker
                        users={payrollUsers}
                        value={attUserFilter}
                        onChange={setAttUserFilter}
                        allowAll allValue="ALL" allLabel="Todos"
                        className="min-w-[190px]"
                        buttonClassName="bg-white border border-gray-200 rounded-full pl-1.5 pr-2.5 py-1 text-[12.5px] hover:border-sky-300" />
                </div>
                <span className="text-[12px] text-gray-500 font-semibold">
                    <span className="font-extrabold text-gray-900 tabular-nums">{filteredData.length}</span> colaboradores
                </span>
            </div>
        )}

        {filteredData.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm bg-white border border-gray-200 rounded-2xl">No hay datos para este período</div>
        ) : (
            <div className={`grid grid-cols-1 gap-5 ${isSingle ? '' : 'lg:grid-cols-2'}`}>
                {filteredData.map(s => {
                    const hasActivity = s.daysWorked > 0;
                    const hasTardanzas = s.lateCount > 0;
                    const cardClass = `bg-white rounded-2xl overflow-hidden border ${hasTardanzas ? 'border-orange-200' : 'border-gray-200'}`;
                    const attGrid = isSingle ? 'grid-cols-[66px_76px_76px_44px_112px_minmax(0,1fr)]' : 'grid-cols-[1fr_74px_74px_46px_108px]';
                    const summaryBlock = (
                        <div className="p-6">
                            {/* Header */}
                            <div className="flex items-start gap-4 mb-5">
                                <div className="relative">
                                    {s.user.photoURL
                                        ? <img src={s.user.photoURL} alt="" className="w-12 h-12 rounded-full object-cover" />
                                        : <div className="w-12 h-12 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-[14px] font-extrabold">{(s.user.displayName || '?').charAt(0).toUpperCase()}</div>}
                                    <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 border-2 border-white rounded-full ${hasActivity ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-[15px] font-extrabold text-gray-900 truncate">{s.user.displayName || s.user.email}</h3>
                                    <p className="text-[12.5px] text-gray-500 font-medium truncate">{s.user.email}</p>
                                    <p className="text-[12px] text-gray-500 font-medium tabular-nums mt-0.5">
                                        {formatHM12(s.schedEntry)} – {formatHM12(s.schedExit)} · {s.workHrsPerDay}h/día
                                    </p>
                                </div>
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold whitespace-nowrap ${
                                    hasTardanzas ? 'bg-orange-100 text-orange-800' :
                                    hasActivity ? 'bg-emerald-100 text-emerald-800' :
                                    'bg-gray-100 text-gray-600'
                                }`}>
                                    {hasTardanzas ? <><FiAlertTriangle size={11} /> Con tardanzas</> :
                                     hasActivity ? <><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Activo</> :
                                     <><span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Sin actividad</>}
                                </span>
                            </div>

                            {/* Stats grid */}
                            <div className={`grid ${isSingle ? 'grid-cols-2' : 'grid-cols-5'} gap-3 mb-5 p-4 rounded-xl ${hasTardanzas ? 'bg-orange-50/40' : 'bg-gray-50'}`}>
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.05em] text-gray-400 font-bold">Días</div>
                                    <div className={`text-[20px] font-extrabold tabular-nums mt-1 ${hasActivity ? 'text-gray-900' : 'text-gray-400'}`}>
                                        {s.daysWorked}<span className="text-[12px] text-gray-400">/{s.workDays}</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.05em] text-gray-400 font-bold">Horas</div>
                                    <div className={`text-[20px] font-extrabold tabular-nums mt-1 ${hasActivity ? 'text-gray-900' : 'text-gray-400'}`}>{s.totalWorkedHrs.toFixed(1)}h</div>
                                </div>
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.05em] text-gray-400 font-bold">Tardanzas</div>
                                    <div className={`text-[20px] font-extrabold tabular-nums mt-1 ${hasTardanzas ? 'text-orange-600' : 'text-emerald-600'}`}>{s.lateCount}{hasTardanzas ? 'x' : ''}</div>
                                    <div className={`text-[11.5px] font-bold ${hasTardanzas ? 'text-orange-600/80' : 'text-emerald-600/70'}`}>
                                        {hasTardanzas ? `${s.totalLateMin} min` : hasActivity ? 'perfecto' : '—'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.05em] text-gray-400 font-bold">Faltas</div>
                                    <div className={`text-[20px] font-extrabold tabular-nums mt-1 ${(s.faltasCount || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>{s.faltasCount || 0}</div>
                                </div>
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.05em] text-gray-400 font-bold">H. Extra</div>
                                    <div className={`text-[20px] font-extrabold tabular-nums mt-1 ${s.totalOTHours > 0 ? 'text-sky-700' : 'text-gray-400'}`}>{s.totalOTHours.toFixed(1)}h</div>
                                    {s.totalOTPay > 0 && <div className="text-[11.5px] font-bold text-sky-600">S/ {s.totalOTPay.toFixed(2)}</div>}
                                </div>
                            </div>

                            {/* Calculation breakdown */}
                            <div className="space-y-2 text-[13.5px] mb-4 tabular-nums">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-500 font-semibold">Sueldo {summaryFilterMode === 'month' ? 'base' : 'proporcional'}</span>
                                    <span className={`font-bold tabular-nums ${s.salary > 0 ? 'text-gray-900' : 'text-gray-400'}`}>S/ {s.salary.toFixed(2)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className={`font-semibold flex items-center gap-1 ${s.discount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                        <FiMinusCircle size={13} /> Descuento {(s.faltasCount || 0) > 0 ? `tardanzas + ${s.faltasCount} ${s.faltasCount === 1 ? 'falta' : 'faltas'}` : 'tardanzas'}
                                    </span>
                                    <span className={`font-bold tabular-nums ${s.discount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                        {s.discount > 0 ? `- S/ ${s.discount.toFixed(2)}` : 'S/ 0.00'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className={`font-semibold flex items-center gap-1 ${s.totalOTPay > 0 ? 'text-sky-700' : 'text-gray-400'}`}>
                                        <FiPlusCircle size={13} /> Horas extra
                                    </span>
                                    <span className={`font-bold tabular-nums ${s.totalOTPay > 0 ? 'text-sky-700' : 'text-gray-400'}`}>
                                        {s.totalOTPay > 0 ? `+ S/ ${s.totalOTPay.toFixed(2)}` : 'S/ 0.00'}
                                    </span>
                                </div>
                                {(s.holidaysWorked || []).length > 0 && (
                                    <div className="flex items-center justify-between">
                                        <span className="font-semibold flex items-center gap-1 text-sky-700">
                                            <FiPlusCircle size={13} /> Feriados trabajados ({s.holidaysWorked.length})
                                        </span>
                                        <span className="font-bold tabular-nums text-sky-700">+ S/ {(s.holidayPay || 0).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="border-t border-gray-200 pt-3 mt-3 flex items-center justify-between">
                                    <span className="text-[14px] font-extrabold text-gray-900">Neto a pagar</span>
                                    <span className={`font-extrabold tabular-nums text-[22px] ${s.netPay > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>S/ {s.netPay.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    );
                    const detailBlock = (
                        <div className="border-t border-gray-100 divide-y divide-gray-50">
                            {/* Attendance detail */}
                            {s.attendanceRecords?.length > 0 && (
                                <div>
                                    <SectionHeader icon={FiList} tone="brand" title="Asistencia"
                                        subtitle={`${s.attendanceRecords.length} días marcados`}
                                        amount={`${s.totalWorkedHrs.toFixed(1)} h`}
                                        open={isSectionOpen(s.user.id, 'attendance')} onClick={() => toggleSection(s.user.id, 'attendance')} />
                                    {isSectionOpen(s.user.id, 'attendance') && (
                                        <div className="px-5 pt-1 pb-3 tabular-nums overflow-x-auto">
                                            <div className={isSingle ? 'overflow-x-auto' : ''}>
                                            <div className={isSingle ? 'min-w-[640px]' : ''}>
                                            <div className={`grid ${attGrid} gap-1 text-[11px] font-bold text-gray-400 uppercase py-1 border-b border-gray-100`}>
                                                <span>Fecha</span>
                                                <span className="text-center">Entrada</span>
                                                <span className="text-center">Salida</span>
                                                <span className="text-right">Horas</span>
                                                <span className="text-right">Estado</span>
                                                {isSingle && <span className="pl-2">Equipo y registro</span>}
                                            </div>
                                            {[...s.attendanceRecords].sort((a, b) => b.date.localeCompare(a.date)).map((att, i) => {
                                                const holiday = (s.holidaysWorked || []).find(h => h.date === att.date);
                                                return (
                                                <div key={i} className={`grid ${attGrid} gap-1 text-[13px] text-gray-600 py-1 items-start ${isSingle ? 'border-b border-gray-50' : ''}`}>
                                                    <span className="">
                                                        {format(parseISO(att.date), 'dd MMM', { locale: es })}
                                                        {holiday && <span className="block text-[10.5px] font-semibold text-amber-600" title={holiday.name}>Feriado</span>}
                                                    </span>
                                                    <span className="text-center ">{att.entryTime ? formatHM12(att.entryTime) : '-'}</span>
                                                    <span className={`text-center ${(att.overtimeMinutes || 0) > 0 ? 'text-red-500' : ''}`}>{att.exitTime ? formatHM12(att.exitTime) : '-'}</span>
                                                    <span className="text-right font-bold">{(att.workedHours || 0).toFixed(1)}</span>
                                                    <span className={`text-right text-[12px] font-medium ${att.lateMinutes > 0 ? 'text-orange-500' : att.earlyExitMinutes > 0 ? 'text-yellow-600' : (att.earlyEntryMinutes || 0) > 0 ? 'text-sky-600' : 'text-emerald-500'}`}>
                                                        {att.lateMinutes > 0 ? `Tarde ${att.lateMinutes}m` : att.earlyExitMinutes > 0 ? `Salió ${att.earlyExitMinutes}m antes` : (att.earlyEntryMinutes || 0) > 0 ? `Llegó ${att.earlyEntryMinutes}m antes` : 'Puntual'}
                                                    </span>
                                                    {isSingle && <AttendanceSource att={att} s={s} users={users} />}
                                                </div>
                                                );
                                            })}
                                            </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Feriados */}
                            {((s.holidaysWorked || []).length > 0 || (s.holidaysOff || []).length > 0) && (
                                <div>
                                    <SectionHeader icon={FiCalendar} tone="warn" title="Feriados"
                                        subtitle={`${(s.holidaysWorked || []).length} trabajados · ${(s.holidaysOff || []).length} no trabajados`}
                                        amount={(s.holidayPay || 0) > 0 ? `+ S/ ${s.holidayPay.toFixed(2)}` : null}
                                        open={isSectionOpen(s.user.id, 'holidays')} onClick={() => toggleSection(s.user.id, 'holidays')} />
                                    {isSectionOpen(s.user.id, 'holidays') && (
                                        <div className="px-5 pt-1 pb-3 tabular-nums overflow-x-auto">
                                            <div className="grid grid-cols-[1fr_104px_100px_24px] gap-1 text-[11px] font-bold text-gray-400 uppercase py-1 border-b border-gray-100">
                                                <span>Fecha / Feriado</span>
                                                <span className="text-center">Estado</span>
                                                <span className="text-right">Adicional</span>
                                                <span />
                                            </div>
                                            {(s.holidaysWorked || []).map(h => {
                                                const calcKey = `${s.user.id}-holiday-${h.date}`;
                                                return (
                                                    <React.Fragment key={`hw-${h.date}`}>
                                                        <div className="grid grid-cols-[1fr_104px_100px_24px] gap-1 text-[13px] text-gray-600 py-1.5">
                                                            <span className="truncate"><span className="">{format(parseISO(h.date), 'dd MMM', { locale: es })}</span> · {h.name}</span>
                                                            <span className="text-center text-[12px] font-medium text-sky-700">{h.pendingExit ? 'Sin salida' : 'Trabajado'}</span>
                                                            <span className="text-right font-medium text-emerald-600">+ S/ {h.amount.toFixed(2)}</span>
                                                            <CalcToggle open={!!openCalcs[calcKey]} onClick={() => toggleCalc(calcKey)} />
                                                        </div>
                                                        {openCalcs[calcKey] && <DayCalc s={s} date={h.date} />}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {(s.holidaysOff || []).map(h => (
                                                <div key={`ho-${h.date}`} className="grid grid-cols-[1fr_104px_100px_24px] gap-1 text-[13px] text-gray-600 py-1.5">
                                                    <span className="truncate"><span className="">{format(parseISO(h.date), 'dd MMM', { locale: es })}</span> · {h.name}</span>
                                                    <span className="text-center text-[12px] font-medium text-gray-400">No trabajado</span>
                                                    <span className="text-right text-gray-400">Sin descuento</span>
                                                    <span />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Discounts detail (late arrivals + early exits) */}
                            {(s.lateCount > 0 || s.totalEarlyMin > 0 || s.faltasCount > 0 || (s.totalBreakExcessMin || 0) > 0) && (() => {
                                const lateRecords = s.attendanceRecords.filter(r => r.lateMinutes > 0);
                                const earlyRecords = s.attendanceRecords.filter(r => r.earlyExitMinutes > 0);
                                const breakRecords = s.attendanceRecords.filter(r => (r.breakExcessMinutes || 0) > 0);
                                // Fechas de falta: las registradas y las detectadas sin marcación.
                                const faltaDates = [
                                    ...(s.faltaRecords || []).map(r => r.date),
                                    ...(s.detectedFaltaDates || []),
                                ].sort((a, b) => b.localeCompare(a));
                                const dayDiscount = s.dayRate || 0;
                                const totalDiscountItems = lateRecords.length + earlyRecords.length + faltaDates.length + breakRecords.length;
                                return (
                                <div>
                                    <SectionHeader icon={FiAlertTriangle} tone="bad" title="Descuentos"
                                        subtitle={`${totalDiscountItems} incidencias${faltaDates.length > 0 ? ` · ${faltaDates.length} ${faltaDates.length === 1 ? 'falta' : 'faltas'}` : ''}${(s.totalLateMin + s.totalEarlyMin) > 0 ? ` · ${s.totalLateMin + s.totalEarlyMin} min` : ''}`}
                                        amount={`- S/ ${s.discount.toFixed(2)}`}
                                        open={isSectionOpen(s.user.id, 'descuentos')} onClick={() => toggleSection(s.user.id, 'descuentos')} />
                                    {isSectionOpen(s.user.id, 'descuentos') && (
                                        <div className="px-5 pt-1 pb-3 tabular-nums overflow-x-auto">
                                            <div className="grid grid-cols-[1fr_82px_78px_64px_88px_24px] gap-1 text-[11px] font-bold text-gray-400 uppercase py-1 border-b border-gray-100">
                                                <span>Fecha / Tipo</span>
                                                <span className="text-center">Programado</span>
                                                <span className="text-center">Real</span>
                                                <span className="text-right">Minutos</span>
                                                <span className="text-right">Descuento</span>
                                                <span />
                                            </div>
                                            {/* Late arrivals */}
                                            {lateRecords.length > 0 && (
                                                <div className="text-[11px] font-bold text-orange-500 uppercase pt-1.5 pb-0.5">Tardanzas ({lateRecords.length})</div>
                                            )}
                                            {[...lateRecords].sort((a, b) => b.date.localeCompare(a.date)).map((att, i) => {
                                                const perMinDiscount = s.perMinuteRate || 0;
                                                const calcKey = `${s.user.id}-late-${att.date}`;
                                                return (
                                                    <React.Fragment key={`late-${i}`}>
                                                    <div className="grid grid-cols-[1fr_82px_78px_64px_88px_24px] gap-1 text-[13px] text-gray-600 py-1.5">
                                                        <span className="">{format(parseISO(att.date), 'dd MMM', { locale: es })}</span>
                                                        <span className="text-center text-gray-400">{formatHM12(att.scheduledEntry || s.schedEntry)}</span>
                                                        <span className="text-center text-orange-600 font-medium">{formatHM12(att.entryTime)}</span>
                                                        <span className="text-right font-bold text-orange-600">{att.lateMinutes}m</span>
                                                        <span className="text-right font-medium text-red-500">- S/ {(att.lateMinutes * perMinDiscount).toFixed(2)}</span>
                                                        <CalcToggle open={!!openCalcs[calcKey]} onClick={() => toggleCalc(calcKey)} />
                                                    </div>
                                                    {openCalcs[calcKey] && <DayCalc s={s} date={att.date} />}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {/* Early exits */}
                                            {earlyRecords.length > 0 && (
                                                <div className="text-[11px] font-bold text-yellow-600 uppercase pt-2 pb-0.5">Salidas tempranas ({earlyRecords.length})</div>
                                            )}
                                            {[...earlyRecords].sort((a, b) => b.date.localeCompare(a.date)).map((att, i) => {
                                                const perMinDiscount = s.perMinuteRate || 0;
                                                const calcKey = `${s.user.id}-early-${att.date}`;
                                                return (
                                                    <React.Fragment key={`early-${i}`}>
                                                    <div className="grid grid-cols-[1fr_82px_78px_64px_88px_24px] gap-1 text-[13px] text-gray-600 py-1.5">
                                                        <span className="">{format(parseISO(att.date), 'dd MMM', { locale: es })}</span>
                                                        <span className="text-center text-gray-400">{formatHM12(att.scheduledExit || s.schedExit)}</span>
                                                        <span className="text-center text-yellow-600 font-medium">{formatHM12(att.exitTime)}</span>
                                                        <span className="text-right font-bold text-yellow-600">{att.earlyExitMinutes}m</span>
                                                        <span className="text-right font-medium text-red-500">- S/ {(att.earlyExitMinutes * perMinDiscount).toFixed(2)}</span>
                                                        <CalcToggle open={!!openCalcs[calcKey]} onClick={() => toggleCalc(calcKey)} />
                                                    </div>
                                                    {openCalcs[calcKey] && <DayCalc s={s} date={att.date} />}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {/* Exceso de refrigerio */}
                                            {breakRecords.length > 0 && (
                                                <div className="text-[11px] font-bold text-amber-600 uppercase pt-2 pb-0.5">
                                                    Exceso de refrigerio ({breakRecords.length})
                                                </div>
                                            )}
                                            {[...breakRecords].sort((a, b) => b.date.localeCompare(a.date)).map((att, i) => {
                                                const perMinDiscount = s.perMinuteRate || 0;
                                                const calcKey = `${s.user.id}-break-${att.date}`;
                                                return (
                                                    <React.Fragment key={`break-${i}`}>
                                                    <div className="grid grid-cols-[1fr_82px_78px_64px_88px_24px] gap-1 text-[13px] text-gray-600 py-1.5">
                                                        <span className="">{format(parseISO(att.date), 'dd MMM', { locale: es })}</span>
                                                        <span className="text-center text-gray-400">{att.breakAllowedMinutes}m</span>
                                                        <span className="text-center text-amber-600 font-medium">{att.breakTakenMinutes}m</span>
                                                        <span className="text-right font-bold text-amber-600">+{att.breakExcessMinutes}m</span>
                                                        <span className="text-right font-medium text-red-500">- S/ {(att.breakExcessMinutes * perMinDiscount).toFixed(2)}</span>
                                                        <CalcToggle open={!!openCalcs[calcKey]} onClick={() => toggleCalc(calcKey)} />
                                                    </div>
                                                    {openCalcs[calcKey] && <DayCalc s={s} date={att.date} />}
                                                    </React.Fragment>
                                                );
                                            })}

                                            {/* Faltas: descuentan el día completo */}
                                            {faltaDates.length > 0 && (
                                                <div className="text-[11px] font-bold text-red-600 uppercase pt-2 pb-0.5">
                                                    Faltas ({faltaDates.length}) · día completo
                                                </div>
                                            )}
                                            {faltaDates.map((fd, i) => {
                                                const registrada = (s.faltaRecords || []).some(r => r.date === fd);
                                                const calcKey = `${s.user.id}-falta-${fd}`;
                                                return (
                                                    <React.Fragment key={`falta-${i}`}>
                                                    <div className="grid grid-cols-[1fr_82px_78px_64px_88px_24px] gap-1 text-[13px] text-gray-600 py-1.5">
                                                        <span className="">{format(parseISO(fd), 'dd MMM', { locale: es })}</span>
                                                        <span className="col-span-2 text-center text-[11px] text-gray-400 italic">
                                                            {registrada ? 'falta registrada' : 'sin marcación'}
                                                        </span>
                                                        <span className="text-right font-bold text-red-600">1 día</span>
                                                        <span className="text-right font-medium text-red-500">- S/ {dayDiscount.toFixed(2)}</span>
                                                        <CalcToggle open={!!openCalcs[calcKey]} onClick={() => toggleCalc(calcKey)} />
                                                    </div>
                                                    {openCalcs[calcKey] && <DayCalc s={s} date={fd} />}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {(s.justifiedFaltaDates || []).length > 0 && (
                                                <div className="text-[11px] text-amber-600 pt-2 font-medium">
                                                    {s.justifiedFaltaDates.length} falta(s) con justificación aprobada: no se descuentan.
                                                </div>
                                            )}
                                            <div className="flex justify-between pt-1.5 mt-1 border-t border-gray-100 text-[13px] font-bold">
                                                <span className="text-gray-600">Total descuento</span>
                                                <span className="text-red-500">- S/ {s.discount.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                );
                            })()}

                            {/* Overtime detail */}
                            {s.overtimeRecords?.length > 0 && (
                                <div>
                                    <SectionHeader icon={FiClock} tone="ok" title="Horas extra"
                                        subtitle={`${s.overtimeRecords.length} registros · ${s.totalOTHours.toFixed(1)} h`}
                                        amount={`+ S/ ${s.totalOTPay.toFixed(2)}`}
                                        open={isSectionOpen(s.user.id, 'overtime')} onClick={() => toggleSection(s.user.id, 'overtime')} />
                                    {isSectionOpen(s.user.id, 'overtime') && (
                                        <div className="px-5 pt-1 pb-3 tabular-nums overflow-x-auto">
                                            <div className="grid grid-cols-[1fr_156px_60px_84px_24px] gap-1 text-[11px] font-bold text-gray-400 uppercase py-1 border-b border-gray-100">
                                                <span>Fecha</span>
                                                <span className="text-center">Horario</span>
                                                <span className="text-right">Horas</span>
                                                <span className="text-right">Pago</span>
                                                <span />
                                            </div>
                                            {[...s.overtimeRecords].sort((a, b) => b.date.localeCompare(a.date)).map((ot, i) => {
                                                const calcKey = `${s.user.id}-ot-${ot.id || i}`;
                                                return (
                                                <React.Fragment key={i}>
                                                <div className="grid grid-cols-[1fr_156px_60px_84px_24px] gap-1 text-[13px] text-gray-600 py-1.5">
                                                    <span className="">{format(parseISO(ot.date), 'dd MMM', { locale: es })}</span>
                                                    <span className="text-center text-gray-500 whitespace-nowrap">{formatHM12(ot.startTime)} – {formatHM12(ot.endTime)}</span>
                                                    <span className="text-right font-bold text-blue-600">{ot.hours?.toFixed(2)}h</span>
                                                    <span className="text-right font-medium text-emerald-600">S/ {ot.totalPay?.toFixed(2)}</span>
                                                    <CalcToggle open={!!openCalcs[calcKey]} onClick={() => toggleCalc(calcKey)} />
                                                </div>
                                                {openCalcs[calcKey] && <DayCalc s={s} date={ot.date} />}
                                                </React.Fragment>
                                                );
                                            })}
                                            <div className="flex justify-between pt-1.5 mt-1 border-t border-gray-100 text-[13px] font-bold">
                                                <span className="text-gray-600">Total horas extra</span>
                                                <span className="text-blue-600">{s.totalOTHours.toFixed(1)}h = <span className="text-emerald-600">S/ {s.totalOTPay.toFixed(2)}</span></span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                    if (isSingle) {
                        return (
                            <div key={s.user.id} className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_372px] gap-5 items-start">
                                <div className={`${cardClass} order-2 2xl:order-1`}>{detailBlock}</div>
                                <div className={`${cardClass} order-1 2xl:order-2 2xl:sticky 2xl:top-4`}>{summaryBlock}</div>
                            </div>
                        );
                    }
                    return <div key={s.user.id} className={cardClass}>{summaryBlock}{detailBlock}</div>;
                })}
            </div>
        )}
    </div>
    );
};

// ══════════════════════════════════════
// COLLAB TAB: RECIBO (payslip)
// ══════════════════════════════════════
const CollabReciboTab = ({ myPayslip, mySalary, myRate, attMonthFilter, me, mySchedule, myWorkHrs, employeeSettings }) => {
    const periodLabel = attMonthFilter ? format(parseISO(attMonthFilter + '-01'), "MMMM yyyy", { locale: es }) : '';
    const position = employeeSettings?.[me?.id]?.position || 'Colaborador';
    const dailyRate = mySalary > 0 ? mySalary / 30 : 0;
    const perMin = (mySalary > 0 && myWorkHrs > 0) ? (dailyRate / (myWorkHrs * 60)) : 0;

    return (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="p-7 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-sky-50/40 flex items-start justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-sky-600 flex items-center justify-center shadow-md shadow-sky-500/30">
                        <span className="text-white text-[22px] font-extrabold leading-none">H</span>
                    </div>
                    <div>
                        <div className="text-[12px] uppercase tracking-[0.18em] text-gray-500 font-bold">Recibo del período</div>
                        <h2 className="text-[22px] font-extrabold text-gray-900 leading-tight capitalize">{periodLabel}</h2>
                        <p className="text-[12px] text-gray-400 font-semibold mt-0.5 font-mono">Emitido {format(new Date(), 'd MMM yyyy', { locale: es })}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button disabled className="px-3.5 py-2 text-[12.5px] bg-white border border-gray-200 rounded-xl flex items-center gap-2 font-semibold text-gray-400 cursor-not-allowed">
                        <FiDownload size={14} /> Descargar PDF
                        <span className="text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-extrabold">PRONTO</span>
                    </button>
                </div>
            </div>
            <div className="p-7 grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-gray-200">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-1.5">Colaborador</div>
                    <div className="flex items-center gap-3">
                        {me?.photoURL ? (
                            <img src={me.photoURL} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white text-[13px] font-extrabold">
                                {(me?.displayName || '?').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div>
                            <div className="text-[14px] font-extrabold text-gray-900">{me?.displayName || me?.email}</div>
                            <div className="text-[11px] text-gray-500 font-semibold truncate">{me?.email}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-1.5">Cargo &amp; horario</div>
                    <div className="text-[13px] font-bold text-gray-900">{position}</div>
                    <div className="text-[11px] text-gray-500 font-mono font-semibold mt-0.5">{formatHM12(mySchedule.entry)} – {formatHM12(mySchedule.exit)} · {myWorkHrs}h/día</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-1.5">Estado del período</div>
                    <div className="text-[13px] font-bold text-gray-900">{myPayslip.daysWorked} / {myPayslip.workDays} días</div>
                    <div className="text-[11px] text-emerald-700 font-semibold mt-0.5">Cierra al final del mes</div>
                </div>
            </div>
            <div className="p-7 grid grid-cols-12 gap-6">
                <div className="col-span-12 lg:col-span-7">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-3">Conceptos del período</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-extrabold mb-2 flex items-center gap-2">
                        <FiPlusCircle size={13} /> Ingresos
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                        <div className="flex justify-between items-center py-2.5 border-b border-emerald-100">
                            <div>
                                <div className="font-bold text-gray-900 text-[13px]">Sueldo base</div>
                                <div className="text-[11px] text-gray-500 font-medium">{myPayslip.daysWorked} de {myPayslip.workDays} días · S/ {dailyRate.toFixed(2)}/día</div>
                            </div>
                            <div className="font-mono font-extrabold text-gray-900 text-[15px]">S/ {mySalary.toFixed(2)}</div>
                        </div>
                        {myPayslip.totalOTPay > 0 && (
                            <div className="flex justify-between items-center py-2.5 border-b border-emerald-100">
                                <div>
                                    <div className="font-bold text-gray-900 text-[13px]">Horas extra</div>
                                    <div className="text-[11px] text-gray-500 font-medium">{myPayslip.totalOTHours.toFixed(1)}h × S/ {myRate.toFixed(2)}/hr · {myPayslip.otRecords.length} registros</div>
                                </div>
                                <div className="font-mono font-extrabold text-emerald-600 text-[15px]">+ S/ {myPayslip.totalOTPay.toFixed(2)}</div>
                            </div>
                        )}
                        {(myPayslip.holidayPay || 0) > 0 && (
                            <div className="flex justify-between items-center py-2.5 border-b border-emerald-100">
                                <div>
                                    <div className="font-bold text-gray-900 text-[13px]">Feriados trabajados</div>
                                    <div className="text-[11px] text-gray-500 font-medium">{myPayslip.holidaysWorked.length} {myPayslip.holidaysWorked.length === 1 ? 'día' : 'días'} · pago doble</div>
                                </div>
                                <div className="font-mono font-extrabold text-emerald-600 text-[15px]">+ S/ {myPayslip.holidayPay.toFixed(2)}</div>
                            </div>
                        )}
                        <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-emerald-200">
                            <span className="text-[12px] font-extrabold uppercase tracking-wider text-emerald-800">Subtotal ingresos</span>
                            <span className="font-mono font-extrabold text-emerald-700 text-[17px]">S/ {(mySalary + myPayslip.totalOTPay + (myPayslip.holidayPay || 0)).toFixed(2)}</span>
                        </div>
                    </div>
                    {myPayslip.discount > 0 && (
                        <>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-red-600 font-extrabold mb-2 flex items-center gap-2">
                                <FiMinusCircle size={13} /> Descuentos
                            </div>
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                                {(myPayslip.lateCount > 0 || myPayslip.totalLateMin + myPayslip.totalEarlyMin > 0) && (
                                    <div className="flex justify-between items-center py-2.5 border-b border-red-100">
                                        <div>
                                            <div className="font-bold text-gray-900 text-[13px]">Tardanzas</div>
                                            <div className="text-[11px] text-gray-500 font-medium">{myPayslip.lateCount} {myPayslip.lateCount === 1 ? 'incidencia' : 'incidencias'} · {myPayslip.totalLateMin + myPayslip.totalEarlyMin} min total</div>
                                        </div>
                                    </div>
                                )}
                                {(myPayslip.totalBreakExcessMin || 0) > 0 && (
                                    <div className="flex justify-between items-center py-2.5 border-b border-red-100">
                                        <div>
                                            <div className="font-bold text-gray-900 text-[13px]">Exceso de refrigerio</div>
                                            <div className="text-[11px] text-gray-500 font-medium">{myPayslip.totalBreakExcessMin} min de más sobre el tiempo permitido</div>
                                        </div>
                                    </div>
                                )}
                                {(myPayslip.faltasCount || 0) > 0 && (
                                    <div className="flex justify-between items-center py-2.5 border-b border-red-100">
                                        <div>
                                            <div className="font-bold text-gray-900 text-[13px]">Faltas</div>
                                            <div className="text-[11px] text-gray-500 font-medium">
                                                {myPayslip.faltasCount} {myPayslip.faltasCount === 1 ? 'día completo' : 'días completos'}
                                                {(myPayslip.faltaDates || []).length > 0 && ` · ${myPayslip.faltaDates.map(d => format(parseISO(d), 'd MMM', { locale: es })).join(', ')}`}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-red-200">
                                    <span className="text-[12px] font-extrabold uppercase tracking-wider text-red-800">Subtotal descuentos</span>
                                    <span className="font-mono font-extrabold text-red-600 text-[17px]">− S/ {myPayslip.discount.toFixed(2)}</span>
                                </div>
                            </div>
                        </>
                    )}
                    {myPayslip.lateRecords.length > 0 && (
                        <details className="group mt-3">
                            <summary className="cursor-pointer flex items-center gap-2 text-[12px] text-gray-500 hover:text-gray-900 font-bold py-2 px-3 hover:bg-gray-50 rounded-lg">
                                <FiList size={13} /> Ver detalle de tardanzas ({myPayslip.lateRecords.length})
                                <FiChevronsDown size={11} className="ml-auto group-open:rotate-180 transition-transform" />
                            </summary>
                            <div className="pl-9 pt-2 space-y-1.5 text-[11.5px] font-mono">
                                {myPayslip.lateRecords.map(r => (
                                    <div key={r.id} className="flex justify-between">
                                        <span className="text-gray-500">{format(parseISO(r.date), 'd MMM', { locale: es })} · entrada {formatHM12(r.entryTime)}</span>
                                        <span className="font-bold text-red-600">− S/ {((r.lateMinutes || 0) * perMin).toFixed(2)} ({r.lateMinutes} min)</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                    {myPayslip.otRecords.length > 0 && (
                        <details className="group mt-1">
                            <summary className="cursor-pointer flex items-center gap-2 text-[12px] text-gray-500 hover:text-gray-900 font-bold py-2 px-3 hover:bg-gray-50 rounded-lg">
                                <FiClock size={13} /> Ver detalle de horas extra ({myPayslip.otRecords.length})
                                <FiChevronsDown size={11} className="ml-auto group-open:rotate-180 transition-transform" />
                            </summary>
                            <div className="pl-9 pt-2 space-y-1.5 text-[11.5px] font-mono">
                                {[...myPayslip.otRecords].sort((a, b) => b.date.localeCompare(a.date)).map(r => (
                                    <div key={r.id} className="flex justify-between">
                                        <span className="text-gray-500">{format(parseISO(r.date), 'd MMM', { locale: es })} · {formatHM12(r.startTime)} → {formatHM12(r.endTime)}</span>
                                        <span className="font-bold text-sky-700">+ S/ {r.totalPay.toFixed(2)} ({r.hours.toFixed(2)}h)</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
                <div className="col-span-12 lg:col-span-5">
                    <div className="sticky top-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-3">Resumen final</div>
                        <div className="rounded-3xl p-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #10B981 0%, #047857 100%)' }}>
                            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
                            <div className="relative text-white">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-200 font-extrabold mb-2">Neto a recibir</div>
                                <div className="text-[44px] font-extrabold tabular-nums leading-none tracking-tight">
                                    S/ {Math.floor(myPayslip.net)}<span className="text-[24px] text-white/70">.{String(Math.round((myPayslip.net % 1) * 100)).padStart(2, '0')}</span>
                                </div>
                                <div className="mt-4 pt-4 border-t border-white/20">
                                    <div className="flex justify-between text-[12px] font-semibold mb-1.5">
                                        <span className="text-white/70">Ingresos</span>
                                        <span className="font-mono font-bold">S/ {(mySalary + myPayslip.totalOTPay + (myPayslip.holidayPay || 0)).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-[12px] font-semibold">
                                        <span className="text-white/70">Descuentos</span>
                                        <span className="font-mono font-bold">{myPayslip.discount > 0 ? `− S/ ${myPayslip.discount.toFixed(2)}` : 'S/ 0.00'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="px-7 py-4 bg-gray-50 border-t border-gray-200 text-[11px] text-gray-500 font-medium flex items-start gap-2">
                <FiAlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>Este recibo es un cálculo en tiempo real basado en tus marcaciones y puede variar hasta el cierre del período. Si encuentras algún error, contacta a tu administrador.</span>
            </div>
        </div>
    );
};

// ══════════════════════════════════════
// COLLAB TAB: SOLICITUDES
// ══════════════════════════════════════
const CollabSolicitudesTab = ({ mySolicitudes, setSolicitudModal, handleCancelSolicitud }) => {
    const TYPE_META = {
        vacaciones: { label: 'Vacaciones', icon: FiCalendar, bg: 'bg-blue-100', text: 'text-blue-700' },
        olvido: { label: 'Olvido marcación', icon: FiAlertTriangle, bg: 'bg-orange-100', text: 'text-orange-700' },
        permiso: { label: 'Permiso', icon: FiCheckCircle, bg: 'bg-emerald-100', text: 'text-emerald-700' },
        home_office: { label: 'Home office', icon: FiHome, bg: 'bg-purple-100', text: 'text-purple-700' },
    };
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <button onClick={() => setSolicitudModal({ type: 'vacaciones' })} className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-sky-300 hover:shadow-md transition-all">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center mb-3"><FiCalendar size={20} /></div>
                    <h4 className="text-[14px] font-extrabold text-gray-900 mb-1">Solicitar vacaciones</h4>
                    <p className="text-[11.5px] text-gray-500 font-medium leading-tight">Pide días libres de tu saldo anual</p>
                </button>
                <button onClick={() => setSolicitudModal({ type: 'olvido' })} className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-sky-300 hover:shadow-md transition-all">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center mb-3"><FiAlertTriangle size={20} /></div>
                    <h4 className="text-[14px] font-extrabold text-gray-900 mb-1">Reportar olvido</h4>
                    <p className="text-[11.5px] text-gray-500 font-medium leading-tight">¿Olvidaste marcar entrada o salida?</p>
                </button>
                <button onClick={() => setSolicitudModal({ type: 'permiso' })} className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-sky-300 hover:shadow-md transition-all">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3"><FiCheckCircle size={20} /></div>
                    <h4 className="text-[14px] font-extrabold text-gray-900 mb-1">Permiso personal</h4>
                    <p className="text-[11.5px] text-gray-500 font-medium leading-tight">Cita médica, trámite, etc.</p>
                </button>
                <button onClick={() => setSolicitudModal({ type: 'home_office' })} className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-sky-300 hover:shadow-md transition-all">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center mb-3"><FiHome size={20} /></div>
                    <h4 className="text-[14px] font-extrabold text-gray-900 mb-1">Home office</h4>
                    <p className="text-[11.5px] text-gray-500 font-medium leading-tight">Trabajar desde casa un día</p>
                </button>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-[15px] font-extrabold text-gray-900">Mis solicitudes</h3>
                    <span className="text-[12px] text-gray-500 font-semibold"><span className="font-extrabold text-gray-900 tabular-nums">{mySolicitudes.length}</span> en total</span>
                </div>
                {mySolicitudes.length === 0 ? (
                    <div className="px-5 py-12 text-center text-gray-400 text-[13px] italic">No tienes solicitudes aún. Usa las opciones de arriba para enviar una.</div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {mySolicitudes.map(s => {
                            const meta = TYPE_META[s.type] || TYPE_META.permiso;
                            const Icon = meta.icon;
                            const statusBadge = s.status === 'pendiente' ? 'bg-orange-100 text-orange-800'
                                : s.status === 'aprobada' ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-red-100 text-red-800';
                            const StatusIcon = s.status === 'pendiente' ? FiClock : s.status === 'aprobada' ? FiCheck : FiX;
                            const createdAt = s.createdAt?.toDate?.();
                            return (
                                <div key={s.id} className="p-5 flex items-center gap-4 flex-wrap hover:bg-gray-50/50">
                                    <div className={`w-10 h-10 rounded-xl ${meta.bg} ${meta.text} flex items-center justify-center flex-shrink-0`}>
                                        <Icon size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="text-[14px] font-extrabold text-gray-900">{meta.label}</span>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold ${statusBadge}`}>
                                                <StatusIcon size={10} /> {s.status === 'pendiente' ? 'Pendiente' : s.status === 'aprobada' ? 'Aprobada' : 'Rechazada'}
                                            </span>
                                        </div>
                                        <p className="text-[12px] text-gray-500 font-medium">
                                            {s.type === 'vacaciones' && s.dateFrom && s.dateTo && (
                                                <>{format(parseISO(s.dateFrom), 'd MMM', { locale: es })} al {format(parseISO(s.dateTo), 'd MMM yyyy', { locale: es })} · {s.daysCount || ''} días</>
                                            )}
                                            {s.type === 'olvido' && s.date && (
                                                <>{format(parseISO(s.date), 'd MMM yyyy', { locale: es })} · falta marcar {s.missingMark === 'entry' ? 'entrada' : 'salida'}{s.proposedTime ? ` (~${s.proposedTime})` : ''}</>
                                            )}
                                            {s.type === 'permiso' && s.date && (
                                                <>{format(parseISO(s.date), 'd MMM yyyy', { locale: es })}{s.hourFrom ? ` · ${s.hourFrom}–${s.hourTo}` : ''}</>
                                            )}
                                            {s.type === 'justificacion' && s.date && (
                                                <>{format(parseISO(s.date), 'd MMM yyyy', { locale: es })} · justificación de falta</>
                                            )}
                                            {s.type === 'home_office' && s.date && (
                                                <>{format(parseISO(s.date), 'd MMM yyyy', { locale: es })}</>
                                            )}
                                        </p>
                                        {s.reason && <p className="text-[12px] text-gray-600 italic mt-1">"{s.reason}"</p>}
                                        {s.rejectionReason && (
                                            <p className="text-[11px] text-red-600 font-semibold mt-1">Motivo del rechazo: {s.rejectionReason}</p>
                                        )}
                                        <div className="text-[10.5px] text-gray-400 font-mono font-semibold mt-1">
                                            Enviada {createdAt ? format(createdAt, "d MMM 'a las' HH:mm", { locale: es }) : 'recién'}
                                        </div>
                                    </div>
                                    {s.status === 'pendiente' && (
                                        <button onClick={() => handleCancelSolicitud(s.id)} className="text-[12px] text-gray-500 hover:text-red-600 font-bold px-3 py-1.5 hover:bg-red-50 rounded-lg transition-colors">
                                            Cancelar
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// ══════════════════════════════════════
// COLLAB TAB: EXTRAS (mis horas extra)
// ══════════════════════════════════════
const CollabExtrasTab = ({ myOvertimeMonth, myPayslip, myRate, mySalary }) => (
    <div className="space-y-5">
        <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 md:col-span-7 bg-white border border-gray-200 rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-br from-blue-100 to-transparent rounded-bl-full pointer-events-none" />
                <div className="relative">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-blue-100 text-blue-800 mb-3">
                        <FiClock size={11} /> Mi acumulado
                    </span>
                    <div className="flex items-end gap-7 flex-wrap">
                        <div>
                            <div className="text-[48px] font-extrabold tabular-nums text-gray-900 leading-none">
                                {myPayslip.totalOTHours.toFixed(1)}<span className="text-[24px] text-gray-400 ml-1">h</span>
                            </div>
                            <p className="text-[13px] text-gray-500 mt-3 font-medium">en <span className="text-gray-900 font-bold">{myOvertimeMonth.length} registros</span></p>
                        </div>
                        <div className="flex-1 min-w-[180px] border-l border-gray-200 pl-7">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 mb-1 font-bold">Monto total</div>
                            <div className="text-[32px] font-extrabold text-sky-700 leading-none tabular-nums">S/ {myPayslip.totalOTPay.toFixed(2)}</div>
                            <div className="text-[11px] text-gray-500 mt-2 font-semibold">Se acreditará junto al sueldo del mes</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-span-12 md:col-span-5 bg-gradient-to-br from-sky-50 to-blue-100 border border-blue-200 rounded-3xl p-6">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-blue-200/60 text-blue-900">
                    <FiCheckCircle size={11} /> ¿Cómo funciona?
                </span>
                <h3 className="text-[14px] font-extrabold text-gray-900 mt-3 mb-2">Tu tarifa hora extra</h3>
                <div className="bg-white rounded-xl p-4 border border-blue-200 flex items-center justify-between">
                    <div>
                        <div className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">Por hora</div>
                        <div className="text-[26px] font-extrabold text-blue-900 tabular-nums leading-tight">S/ {myRate.toFixed(2)}</div>
                    </div>
                    <div className="text-right text-[11px] text-gray-500 font-semibold leading-tight">
                        Sueldo S/{mySalary}<br />÷ horas mes<br />= tarifa base
                    </div>
                </div>
                <p className="text-[11.5px] text-gray-500 font-medium mt-3">Toda hora después de tu salida cuenta como extra y se calcula automáticamente.</p>
            </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Fecha</th>
                            <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Horario</th>
                            <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Duración</th>
                            <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Tarifa</th>
                            <th className="text-right px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-sky-700 font-extrabold bg-sky-50/60">Mi pago</th>
                            <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Estado</th>
                        </tr>
                    </thead>
                    <tbody className="text-[13px]">
                        {myOvertimeMonth.length === 0 ? (
                            <tr><td colSpan="6" className="text-center py-12 text-gray-400 text-xs italic">Sin horas extra este mes</td></tr>
                        ) : [...myOvertimeMonth].sort((a, b) => b.date.localeCompare(a.date)).map(r => (
                            <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                                <td className="px-5 py-3.5">
                                    <div className="font-bold text-gray-900">{format(parseISO(r.date), 'd MMM yyyy', { locale: es })}</div>
                                    <div className="text-[11px] text-gray-400 font-semibold capitalize">{format(parseISO(r.date), 'EEEE', { locale: es })}</div>
                                </td>
                                <td className="px-3 py-3.5">
                                    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-md font-mono text-[12px] font-bold text-gray-900">
                                        {formatHM12(r.startTime)} → {formatHM12(r.endTime)}
                                    </span>
                                </td>
                                <td className="px-3 py-3.5">
                                    <span className="font-mono font-extrabold text-gray-900">{r.hours.toFixed(2)}h</span>
                                    <div className="text-[10px] text-gray-400 font-mono">{Math.round(r.hours * 60)} min</div>
                                </td>
                                <td className="px-3 py-3.5"><span className="font-mono text-gray-500 font-semibold">S/ {(r.hourlyRate || 0).toFixed(2)} /h</span></td>
                                <td className="px-3 py-3.5 text-right bg-sky-50/30">
                                    <span className="font-mono font-extrabold text-sky-700 text-[15px]">S/ {r.totalPay.toFixed(2)}</span>
                                </td>
                                <td className="px-5 py-3.5 text-right">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-emerald-100 text-emerald-800">
                                        <FiCheck size={10} /> Aprobada
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {myOvertimeMonth.length > 0 && (
                <div className="border-t border-gray-200 bg-sky-50 px-5 py-3.5 flex items-center justify-between flex-wrap gap-3">
                    <div className="text-[12.5px] text-sky-900 font-bold">Total acumulado este mes</div>
                    <div className="flex items-center gap-4 flex-wrap text-[12px]">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Σ Horas</span>
                            <span className="font-mono font-extrabold text-gray-900">{myPayslip.totalOTHours.toFixed(1)}h</span>
                        </div>
                        <div className="w-px h-4 bg-blue-200" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-sky-700 font-bold">Σ A pagarme</span>
                            <span className="font-mono font-extrabold text-sky-700 text-[15px]">S/ {myPayslip.totalOTPay.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
);

// ══════════════════════════════════════
// COLLAB TAB: HISTORIAL (calendar heatmap + lista)
// ══════════════════════════════════════
// ── Pestaña "Mis descuentos" del colaborador ───────────────────────────────
// Reúne todo lo que le resta del sueldo: llegar tarde, salir antes de su hora
// y pasarse del refrigerio. Cada línea con su motivo, sus minutos y su importe,
// para que no haya que preguntar de dónde salió un descuento.
const CollabDescuentosTab = ({ myAttendanceMonth, computeRecordDiscount, mySchedule }) => {
    const items = useMemo(() => {
        const out = [];
        for (const r of myAttendanceMonth) {
            const perMin = (() => {
                const total = (r.lateMinutes || 0) + (r.earlyExitMinutes || 0) + (r.breakExcessMinutes || 0);
                const disc = computeRecordDiscount ? computeRecordDiscount(r) : 0;
                return total > 0 ? disc / total : 0;
            })();
            if ((r.lateMinutes || 0) > 0) {
                out.push({
                    key: `${r.id}-late`, date: r.date, tipo: 'Tardanza',
                    detalle: `Entrada ${formatHM12(r.entryTime)} · programada ${formatHM12(r.scheduledEntry || mySchedule?.entry)}`,
                    minutos: r.lateMinutes, monto: r.lateMinutes * perMin,
                    color: 'text-orange-600', chip: 'bg-orange-100 text-orange-700',
                });
            }
            if ((r.earlyExitMinutes || 0) > 0) {
                out.push({
                    key: `${r.id}-early`, date: r.date, tipo: 'Salida temprana',
                    detalle: `Salida ${formatHM12(r.exitTime)} · programada ${formatHM12(r.scheduledExit || mySchedule?.exit)}`,
                    minutos: r.earlyExitMinutes, monto: r.earlyExitMinutes * perMin,
                    color: 'text-yellow-700', chip: 'bg-yellow-100 text-yellow-800',
                });
            }
            if ((r.breakExcessMinutes || 0) > 0) {
                out.push({
                    key: `${r.id}-break`, date: r.date, tipo: 'Exceso de refrigerio',
                    detalle: `${formatHM12(r.breakStart)} → ${formatHM12(r.breakEnd)} · ${r.breakTakenMinutes} min de ${r.breakAllowedMinutes}`,
                    minutos: r.breakExcessMinutes, monto: r.breakExcessMinutes * perMin,
                    color: 'text-amber-700', chip: 'bg-amber-100 text-amber-800',
                });
            }
            if (r.status === 'falta') {
                out.push({
                    key: `${r.id}-falta`, date: r.date, tipo: 'Falta',
                    detalle: 'Día completo sin marcación',
                    minutos: null, monto: computeRecordDiscount ? 0 : 0,
                    color: 'text-red-600', chip: 'bg-red-100 text-red-700',
                });
            }
        }
        return out.sort((a, b) => b.date.localeCompare(a.date));
    }, [myAttendanceMonth, computeRecordDiscount, mySchedule]);

    const totalMin = items.reduce((a, c) => a + (c.minutos || 0), 0);
    const totalMonto = items.reduce((a, c) => a + (c.monto || 0), 0);
    const porTipo = items.reduce((acc, c) => { acc[c.tipo] = (acc[c.tipo] || 0) + 1; return acc; }, {});

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">Incidencias</div>
                    <div className="text-[22px] font-extrabold text-gray-900 tabular-nums">{items.length}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">Minutos perdidos</div>
                    <div className="text-[22px] font-extrabold text-orange-600 tabular-nums">{totalMin}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">Te descuentan</div>
                    <div className="text-[22px] font-extrabold text-red-600 tabular-nums">− S/ {totalMonto.toFixed(2)}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-2">Tipos</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {Object.keys(porTipo).length === 0
                            ? <span className="text-[12px] text-emerald-600 font-bold">Sin incidencias</span>
                            : Object.entries(porTipo).map(([t, n]) => (
                                <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t.split(' ')[0]} {n}</span>
                            ))}
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                    <h3 className="text-[15px] font-extrabold text-gray-900">Detalle de mis descuentos</h3>
                    <p className="text-[12px] text-gray-500 mt-0.5">
                        Se calcula sobre el valor de tu minuto de trabajo. Si crees que hay un error, avísale al admin.
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">
                                <th className="text-left px-5 py-3">Fecha</th>
                                <th className="text-left px-3 py-3">Motivo</th>
                                <th className="text-left px-3 py-3">Detalle</th>
                                <th className="text-right px-3 py-3">Minutos</th>
                                <th className="text-right px-5 py-3 text-red-600">Descuento</th>
                            </tr>
                        </thead>
                        <tbody className="text-[13px]">
                            {items.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-14 text-emerald-600 text-[13px] font-bold">
                                    Ningún descuento este mes. Bien ahí.
                                </td></tr>
                            ) : items.map(it => (
                                <tr key={it.key} className="border-b border-gray-100 hover:bg-gray-50/50">
                                    <td className="px-5 py-3.5">
                                        <div className="font-bold text-gray-900">{format(parseISO(it.date), 'd MMM', { locale: es })}</div>
                                        <div className="text-[11px] text-gray-400 capitalize">{format(parseISO(it.date), 'EEEE', { locale: es })}</div>
                                    </td>
                                    <td className="px-3 py-3.5">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold ${it.chip}`}>{it.tipo}</span>
                                    </td>
                                    <td className="px-3 py-3.5 text-[12px] text-gray-500 font-mono">{it.detalle}</td>
                                    <td className={`px-3 py-3.5 text-right font-mono font-bold ${it.color}`}>
                                        {it.minutos !== null ? `${it.minutos} min` : 'día'}
                                    </td>
                                    <td className="px-5 py-3.5 text-right font-mono font-extrabold text-red-600">
                                        {it.monto > 0 ? `- S/ ${it.monto.toFixed(2)}` : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {items.length > 0 && (
                            <tfoot>
                                <tr className="bg-gray-50 border-t-2 border-gray-200 font-extrabold text-[12.5px]">
                                    <td className="px-5 py-3 text-gray-700" colSpan={3}>Total</td>
                                    <td className="px-3 py-3 text-right font-mono text-orange-600">{totalMin} min</td>
                                    <td className="px-5 py-3 text-right font-mono text-red-600">− S/ {totalMonto.toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

const CollabHistorialTab = ({ calendarData, myAttendanceMonth, setSolicitudModal, mySolicitudes = [], computeRecordDiscount }) => {
    const { days, offset } = calendarData;
    const totalDays = days.filter(d => !d.isWeekend && !d.isFuture).length;
    const worked = days.filter(d => d.state === 'puntual' || d.state === 'extra' || d.state === 'tarde').length;
    const totalHrs = myAttendanceMonth.reduce((a, c) => a + (c.workedHours || 0), 0);
    const faltaDays = days.filter(d => d.state === 'falta');
    const justifiedDates = new Set(
        (mySolicitudes || [])
            .filter(sol => sol.type === 'justificacion' && sol.date && sol.status !== 'rechazada')
            .map(sol => sol.date)
    );

    const stateClass = {
        future: 'bg-gray-100 text-gray-300',
        libre: 'bg-gray-50 text-gray-400 border border-dashed border-gray-300',
        puntual: 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200',
        extra: 'bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200',
        tarde: 'bg-orange-100 text-orange-700 cursor-pointer hover:bg-orange-200',
        falta: 'bg-red-100 text-red-700 cursor-pointer hover:bg-red-200',
    };

    return (
        <div className="space-y-5">
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="text-[16px] font-extrabold text-gray-900 mb-1">Mi calendario</h3>
                <p className="text-[12.5px] text-gray-500 font-medium mb-5">Haz clic en un día para ver detalle de tu marcación</p>
                <div className="grid grid-cols-7 gap-2 mb-2">
                    {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
                        <div key={d} className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold text-center">{d}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                    {Array.from({ length: offset }).map((_, i) => <div key={`empty-${i}`} />)}
                    {days.map(d => (
                        <div key={d.date}
                            onClick={() => { if (d.state === 'falta') setSolicitudModal({ type: 'justificacion', date: d.date }); }}
                            title={d.state === 'falta' ? `${d.date} · sin marcación — clic para justificar` : `${d.date} · ${d.state}`}
                            className={`aspect-square rounded-lg flex flex-col items-center justify-center text-[11px] font-bold transition-all relative ${stateClass[d.state] || 'bg-gray-50'} ${d.isToday ? 'ring-2 ring-sky-600 ring-offset-2' : ''}`}>
                            <span className="text-[13px]">{d.day}</span>
                            {d.badge && <span className="text-[9px] opacity-80">{d.badge}</span>}
                            {justifiedDates.has(d.date) && <span className="absolute top-0.5 right-1 text-[8px]">✓</span>}
                        </div>
                    ))}
                </div>

                {/* Faltas del mes: el sitio donde el colaborador las justifica */}
                {faltaDays.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-3">
                            <FiAlertTriangle size={14} className="text-red-500" />
                            <h4 className="text-[13px] font-extrabold text-gray-900">
                                {faltaDays.length} {faltaDays.length === 1 ? 'día sin marcación' : 'días sin marcación'}
                            </h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {faltaDays.map(d => {
                                const done = justifiedDates.has(d.date);
                                return (
                                    <div key={d.date} className={`flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-xl border ${done ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                                        <span className={`text-[12px] font-bold capitalize ${done ? 'text-amber-700' : 'text-red-700'}`}>
                                            {format(parseISO(d.date), "d 'de' MMM", { locale: es })}
                                        </span>
                                        {done ? (
                                            <span className="text-[10.5px] font-bold text-amber-600 px-2">Justificada</span>
                                        ) : (
                                            <button onClick={() => setSolicitudModal({ type: 'justificacion', date: d.date })}
                                                className="px-2.5 py-1 rounded-lg bg-white border border-red-300 text-red-600 text-[10.5px] font-bold hover:bg-red-100 transition-colors">
                                                Justificar
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className="flex items-center justify-between mt-5 pt-5 border-t border-gray-100 flex-wrap gap-3">
                    <div className="flex items-center gap-3 text-[11.5px] font-semibold flex-wrap">
                        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-100" /><span className="text-gray-500">Puntual</span></div>
                        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-100" /><span className="text-gray-500">Con HE</span></div>
                        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-orange-100" /><span className="text-gray-500">Tardanza</span></div>
                        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100" /><span className="text-gray-500">Sin marcar</span></div>
                        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-50 border border-dashed border-gray-300" /><span className="text-gray-500">No laborable</span></div>
                    </div>
                    <div className="flex items-center gap-4 text-[12px]">
                        <div><span className="text-gray-500 font-semibold">Días trabajados: </span><span className="font-extrabold tabular-nums text-gray-900">{worked}/{totalDays}</span></div>
                        <div><span className="text-gray-500 font-semibold">Total horas: </span><span className="font-extrabold tabular-nums text-gray-900">{totalHrs.toFixed(1)}h</span></div>
                    </div>
                </div>
            </div>
            <div>
                <h3 className="text-[15px] font-extrabold text-gray-900 mb-3">Mis últimas marcaciones</h3>
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Fecha</th>
                                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Entrada</th>
                                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Salida</th>
                                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Trabajadas</th>
                                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-sky-700 font-extrabold">H. Extra</th>
                                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Estado</th>
                                    <th className="text-right px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-red-600 font-bold">Descuento</th>
                                    <th className="text-right px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Acción</th>
                                </tr>
                            </thead>
                            <tbody className="text-[13px]">
                                {myAttendanceMonth.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center py-12 text-gray-400 text-xs italic">Sin marcaciones este mes</td></tr>
                                ) : [...myAttendanceMonth].sort((a, b) => b.date.localeCompare(a.date)).map(r => {
                                    const isLate = (r.lateMinutes || 0) > 0;
                                    const earlyMin = r.earlyExitMinutes || 0;
                                    const discount = computeRecordDiscount ? computeRecordDiscount(r) : 0;
                                    const otHrs = (r.overtimeMinutes || 0) / 60;
                                    const today = format(new Date(), 'yyyy-MM-dd');
                                    const isToday = r.date === today;
                                    return (
                                        <tr key={r.id} className={`border-b border-gray-100 hover:bg-gray-50/50 ${isLate ? 'bg-orange-50/20' : ''}`}>
                                            <td className="px-5 py-3.5">
                                                <div className="font-bold text-gray-900">{format(parseISO(r.date), 'd MMM', { locale: es })}</div>
                                                <div className="text-[11px] text-gray-400 font-semibold capitalize">{isToday ? 'Hoy' : format(parseISO(r.date), 'EEEE', { locale: es })}</div>
                                            </td>
                                            <td className="px-3 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    <span className={`w-1.5 h-1.5 rounded-full ${isLate ? 'bg-orange-500' : 'bg-emerald-500'}`} />
                                                    <span className={`font-mono font-extrabold ${isLate ? 'text-orange-700' : 'text-gray-900'}`}>{r.entryTime ? formatHM12(r.entryTime) : '-'}</span>
                                                </div>
                                                {isLate && <div className="text-[10px] text-orange-700 font-mono font-bold mt-0.5">{r.lateMinutes} min tarde</div>}
                                                {!isLate && (r.earlyEntryMinutes || 0) > 0 && <div className="text-[10px] text-sky-700 font-mono font-bold mt-0.5">{r.earlyEntryMinutes} min antes</div>}
                                            </td>
                                            <td className="px-3 py-3.5">
                                                {r.exitTime ? (
                                                    <>
                                                        <span className={`font-mono font-semibold ${earlyMin > 0 ? 'text-yellow-700' : 'text-gray-900'}`}>{formatHM12(r.exitTime)}</span>
                                                        {earlyMin > 0 && <div className="text-[10px] text-yellow-700 font-mono font-bold mt-0.5">{earlyMin} min antes</div>}
                                                    </>
                                                ) : isToday ? (
                                                    <span className="text-gray-400 italic text-[12px]">en curso...</span>
                                                ) : (
                                                    <>
                                                        <span className="font-mono text-gray-300">—</span>
                                                        <div className="text-[10px] text-gray-400 mt-0.5 font-medium">olvidaste marcar</div>
                                                    </>
                                                )}
                                            </td>
                                            <td className="px-3 py-3.5">
                                                {r.workedHours ? <span className="font-mono font-bold text-gray-900">{r.workedHours.toFixed(1)}h</span> : <span className="font-mono text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-3.5">
                                                {otHrs > 0 ? <span className="font-mono font-extrabold text-sky-700">+{otHrs.toFixed(2)}h</span> : <span className="font-mono text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-3.5">
                                                {!r.exitTime && !isToday ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-orange-100 text-orange-800"><FiAlertTriangle size={10} /> Sin cierre</span>
                                                ) : earlyMin > 0 && !isLate ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-yellow-100 text-yellow-800"><FiLogOut size={10} /> Salida temprana</span>
                                                ) : isLate ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-orange-100 text-orange-800"><FiAlertTriangle size={10} /> Tarde</span>
                                                ) : isToday && !r.exitTime ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-blue-100 text-blue-800"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" /> En curso</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-emerald-100 text-emerald-800"><FiCheck size={10} /> Puntual</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3.5 text-right">
                                                {discount > 0
                                                    ? <span className="font-mono font-bold text-red-600">- S/ {discount.toFixed(2)}</span>
                                                    : <span className="font-mono text-gray-300">—</span>}
                                            </td>
                                            <td className="px-5 py-3.5 text-right">
                                                {!r.exitTime && !isToday && (
                                                    <button onClick={() => setSolicitudModal({ type: 'olvido' })} className="text-[11.5px] font-bold text-sky-700 hover:underline">Reportar olvido →</button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ══════════════════════════════════════
// COLLABORATOR VIEW (vista personal completa)
// ══════════════════════════════════════
const CollaboratorView = ({ currentUser, users, attendanceRecords, combinedOvertimeRecords, globalSettings,
    userRates, employeeSettings, todayRecord, handleMarkEntry, handleMarkExit,
    handleMarkBreakStart, handleMarkBreakEnd,
    computeRecordDiscount,
    mySolicitudes, handleCreateSolicitud, handleCancelSolicitud,
    attMonthFilter, setAttMonthFilter }) => {

    const me = users.find(u => u.email === currentUser?.email);
    const [activeTab, setActiveTab] = useState('historial');
    const [solicitudModal, setSolicitudModal] = useState(null);
    const [showHR, setShowHR] = useState(false); // Mis documentos
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const getSchedule = useCallback((userId) => {
        const s = employeeSettings[userId] || {};
        return { entry: s.scheduledEntry || DEFAULT_ENTRY, exit: s.scheduledExit || DEFAULT_EXIT, flexible: s.scheduleMode === 'flexible' };
    }, [employeeSettings]);

    const mySchedule = me ? getSchedule(me.id) : { entry: DEFAULT_ENTRY, exit: DEFAULT_EXIT };
    const myBreakMinutes = getBreakMinutes(employeeSettings[me?.id], globalSettings);
    const myRate = me ? (userRates[me.id] || 0) : 0;
    const mySalary = me ? (employeeSettings[me.id]?.monthlySalary || 0) : 0;
    const myWorkHrs = me ? (employeeSettings[me.id]?.workHoursPerDay || DEFAULT_WORK_HOURS) : DEFAULT_WORK_HOURS;

    const [entryH, entryM] = mySchedule.entry.split(':').map(Number);
    const [exitH, exitM] = mySchedule.exit.split(':').map(Number);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const entryMinutes = entryH * 60 + entryM;
    const exitMinutes = exitH * 60 + exitM;
    // Horario irregular: puede marcar entrada a la hora que quiera, sin
    // ventana de +-horas contra un horario fijo que no tiene.
    const canMarkEntry = mySchedule.flexible || (nowMinutes >= (entryMinutes - 120) && nowMinutes <= (entryMinutes + 240));
    const isWeekendDay = now.getDay() === 0;

    const elapsedDisplay = useMemo(() => {
        if (!todayRecord?.entryTime) return '00:00:00';
        const [eh, em] = todayRecord.entryTime.split(':').map(Number);
        const startMs = new Date(now);
        startMs.setHours(eh, em, 0, 0);
        const diff = now.getTime() - startMs.getTime();
        if (diff < 0) return '00:00:00';
        const totalSec = Math.floor(diff / 1000);
        const hh = Math.floor(totalSec / 3600);
        const mm = Math.floor((totalSec % 3600) / 60);
        const ss = totalSec % 60;
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    }, [now, todayRecord]);

    const elapsedFriendly = useMemo(() => {
        if (!todayRecord?.entryTime) return '';
        const [eh, em] = todayRecord.entryTime.split(':').map(Number);
        const startMin = eh * 60 + em;
        const diffMin = nowMinutes - startMin;
        if (diffMin <= 0) return '';
        const h = Math.floor(diffMin / 60);
        const m = diffMin % 60;
        return h > 0 ? `${h}h ${m}min` : `${m} min`;
    }, [todayRecord, nowMinutes]);

    // Horario irregular: no hay hora de salida pactada, la cuenta regresiva y
    // la barra de progreso se miden contra sus horas del día (desde que marcó
    // entrada), no contra un reloj fijo.
    const targetMinutes = Math.round((myWorkHrs || DEFAULT_WORK_HOURS) * 60);
    const [todayEntryH, todayEntryM] = (todayRecord?.entryTime || '0:0').split(':').map(Number);
    const todayEntryMinutes = todayEntryH * 60 + todayEntryM;
    const estimatedExitMinutes = mySchedule.flexible ? todayEntryMinutes + targetMinutes : exitMinutes;
    const estimatedExitStr = `${String(Math.floor((estimatedExitMinutes % 1440) / 60)).padStart(2, '0')}:${String(estimatedExitMinutes % 60).padStart(2, '0')}`;

    const remainingDisplay = useMemo(() => {
        if (!todayRecord?.entryTime || todayRecord?.exitTime) return '--:--';
        const remainingMin = estimatedExitMinutes - nowMinutes;
        if (remainingMin <= 0) return mySchedule.flexible ? 'Completo' : 'Excedido';
        const h = Math.floor(remainingMin / 60);
        const m = remainingMin % 60;
        return `${h}h ${m}m`;
    }, [todayRecord, estimatedExitMinutes, nowMinutes, mySchedule.flexible]);

    let progressPct = 0;
    if (todayRecord?.entryTime) {
        const span = estimatedExitMinutes - todayEntryMinutes;
        if (span > 0) progressPct = Math.max(0, Math.min(100, ((nowMinutes - todayEntryMinutes) / span) * 100));
    }

    const myAttendance = useMemo(() => me ? attendanceRecords.filter(r => r.userId === me.id) : [], [attendanceRecords, me]);
    const myOvertime = useMemo(() => me ? combinedOvertimeRecords.filter(r => r.userId === me.id) : [], [combinedOvertimeRecords, me]);
    const myAttendanceMonth = useMemo(() => myAttendance.filter(r => !attMonthFilter || r.date?.startsWith(attMonthFilter)), [myAttendance, attMonthFilter]);
    const myOvertimeMonth = useMemo(() => myOvertime.filter(r => !attMonthFilter || r.date?.startsWith(attMonthFilter)), [myOvertime, attMonthFilter]);

    const myPuntualidad = useMemo(() => {
        const total = myAttendanceMonth.length;
        const onTime = myAttendanceMonth.filter(r => !r.lateMinutes || r.lateMinutes === 0).length;
        const pct = total > 0 ? Math.round((onTime / total) * 100) : 0;
        const entries = myAttendanceMonth.map(r => r.entryTime).filter(Boolean).map(t => {
            const [h, m] = t.split(':').map(Number);
            return h * 60 + m;
        });
        const avgMin = entries.length > 0 ? Math.round(entries.reduce((a, c) => a + c, 0) / entries.length) : 0;
        const avgEntry = entries.length === 0 ? '--:--' :
            `${String(Math.floor(avgMin / 60)).padStart(2, '0')}:${String(avgMin % 60).padStart(2, '0')}`;
        const tardanzas = myAttendanceMonth.filter(r => (r.lateMinutes || 0) > 0)
            .sort((a, b) => b.date.localeCompare(a.date)).slice(0, 2);
        return { pct, onTime, total, avgEntry, tardanzas };
    }, [myAttendanceMonth]);

    const streak = useMemo(() => {
        const byDate = {};
        myAttendance.forEach(r => { byDate[r.date] = r; });
        let count = 0;
        const today = format(new Date(), 'yyyy-MM-dd');
        let cursor = new Date();
        for (let i = 0; i < 30; i++) {
            const ds = format(cursor, 'yyyy-MM-dd');
            if (ds > today) { cursor.setDate(cursor.getDate() - 1); continue; }
            if (cursor.getDay() === 0) { cursor.setDate(cursor.getDate() - 1); continue; }
            const rec = byDate[ds];
            if (!rec) break;
            if ((rec.lateMinutes || 0) > 0) break;
            count++;
            cursor.setDate(cursor.getDate() - 1);
        }
        return count;
    }, [myAttendance]);

    const myPayslip = useMemo(() => {
        const otRecs = myOvertimeMonth;
        const daysWorked = myAttendanceMonth.length;
        const totalLateMin = myAttendanceMonth.reduce((a, c) => a + (c.lateMinutes || 0), 0);
        const totalEarlyMin = myAttendanceMonth.reduce((a, c) => a + (c.earlyExitMinutes || 0), 0);
        const lateCount = myAttendanceMonth.filter(r => (r.lateMinutes || 0) > 0).length;
        const totalBreakExcessMin = myAttendanceMonth.reduce((a, c) => a + (c.breakExcessMinutes || 0), 0);
        const totalOTHours = otRecs.reduce((a, c) => a + (c.hours || 0), 0);
        const totalOTPay = otRecs.reduce((a, c) => a + (c.totalPay || 0), 0);
        let workDays = 22;
        let faltaDates = [];
        if (attMonthFilter) {
            try {
                const [y, m] = attMonthFilter.split('-').map(Number);
                const start = startOfMonth(new Date(y, m - 1));
                const end = endOfMonth(new Date(y, m - 1));
                const days = eachDayOfInterval({ start, end });
                workDays = days.filter(d => isWorkingDay(d, globalSettings) && !getHolidayName(format(d, 'yyyy-MM-dd'), globalSettings)).length;

                // Mismas faltas que ve el admin: día laborable ya pasado sin
                // marcación, desde la primera vez que marqué, salvo que tenga
                // una justificación aprobada.
                const todayStr = format(new Date(), 'yyyy-MM-dd');
                const firstEver = myAttendance.reduce((min, r) => (!min || r.date < min ? r.date : min), null);
                const withRecord = new Set(myAttendanceMonth.map(r => r.date));
                const approved = new Set(
                    (mySolicitudes || [])
                        .filter(sol => sol.type === 'justificacion' && sol.status === 'aprobada' && sol.date)
                        .map(sol => sol.date)
                );
                if (firstEver) {
                    faltaDates = days
                        .map(d => format(d, 'yyyy-MM-dd'))
                        .filter((ds, i) => ds < todayStr && ds >= firstEver
                            && isWorkingDay(days[i], globalSettings)
                            && !getHolidayName(ds, globalSettings)
                            && !withRecord.has(ds) && !approved.has(ds));
                }
            } catch { /* keep default */ }
        }
        const registeredFaltas = myAttendanceMonth.filter(r => r.status === 'falta' && !getHolidayName(r.date, globalSettings)).length;
        const faltasCount = faltaDates.length + registeredFaltas;

        const minutesPerDay = myWorkHrs * 60;
        // 30 dias fijos (no `workDays`, que son los dias laborables del mes
        // sin domingos): mismo criterio que computeEmployeePeriod, para que
        // "Mi recibo" cuadre con el Resumen que ve el admin.
        const expectedMinutes = 30 * minutesPerDay;
        const lostMin = totalLateMin + totalEarlyMin + totalBreakExcessMin + faltasCount * minutesPerDay;
        const discount = lostMin > 0 && mySalary > 0 && expectedMinutes > 0
            ? (lostMin / expectedMinutes) * mySalary
            : 0;
        const holidayWork = computeHolidayWork({ records: myAttendanceMonth, global: globalSettings, workHrsPerDay: myWorkHrs, dayRate: mySalary / 30 });
        const net = mySalary - discount + totalOTPay + holidayWork.total;
        return {
            holidaysWorked: holidayWork.items, holidayPay: holidayWork.total,
            daysWorked, workDays, totalLateMin, totalEarlyMin, lateCount,
            faltasCount, faltaDates, totalBreakExcessMin,
            totalOTHours, totalOTPay, salary: mySalary, discount, net,
            otRecords: otRecs, lateRecords: myAttendanceMonth.filter(r => (r.lateMinutes || 0) > 0)
        };
    }, [myAttendance, myAttendanceMonth, myOvertimeMonth, attMonthFilter, mySalary, myWorkHrs, globalSettings, mySolicitudes]);

    const calendarData = useMemo(() => {
        if (!attMonthFilter) return { days: [], offset: 0 };
        try {
            const [y, m] = attMonthFilter.split('-').map(Number);
            const start = startOfMonth(new Date(y, m - 1));
            const end = endOfMonth(new Date(y, m - 1));
            const days = eachDayOfInterval({ start, end });
            const byDate = {};
            myAttendance.forEach(r => { byDate[r.date] = r; });
            const today = format(new Date(), 'yyyy-MM-dd');
            const result = days.map(d => {
                const ds = format(d, 'yyyy-MM-dd');
                const rec = byDate[ds];
                const dow = d.getDay();
                // Día no laboral según la configuración del workspace, no sólo el domingo.
                const isWeekend = !isWorkingDay(d, globalSettings);
                const isFuture = ds > today;
                const isTodayCell = ds === today;
                let state = 'empty';
                let badge = '';
                if (isFuture) { state = 'future'; }
                else if (isWeekend) { state = 'libre'; badge = DAY_LABELS[dow].toLowerCase(); }
                else if (!rec && getHolidayName(ds, globalSettings)) { state = 'libre'; badge = 'feriado'; }
                else if (!rec) { state = isTodayCell ? 'empty' : 'falta'; }
                else if ((rec.overtimeMinutes || 0) > 0) { state = 'extra'; badge = `+${((rec.overtimeMinutes || 0) / 60).toFixed(1)}h`; }
                else if ((rec.lateMinutes || 0) > 0) { state = 'tarde'; badge = `${rec.lateMinutes}m↑`; }
                else { state = 'puntual'; badge = rec.workedHours ? `${rec.workedHours.toFixed(1)}h` : 'OK'; }
                return { date: ds, day: d.getDate(), dow, isFuture, isWeekend, isToday: ds === today, state, badge, record: rec };
            });
            const firstDow = start.getDay();
            const offset = firstDow === 0 ? 6 : firstDow - 1;
            return { days: result, offset };
        } catch { return { days: [], offset: 0 }; }
    }, [attMonthFilter, myAttendance, globalSettings]);

    const monthOptions = useMemo(() => {
        const months = new Set();
        const n = new Date();
        for (let i = 0; i < 6; i++) {
            const d = new Date(n.getFullYear(), n.getMonth() - i, 1);
            months.add(format(d, 'yyyy-MM'));
        }
        myAttendance.forEach(r => { if (r.date) months.add(r.date.substring(0, 7)); });
        return Array.from(months).sort().reverse();
    }, [myAttendance]);

    // Incidencias del mes que restan sueldo: alimenta el contador de la pestaña.
    const descuentosCount = useMemo(() => myAttendanceMonth.reduce((n, r) => (
        n + ((r.lateMinutes || 0) > 0 ? 1 : 0)
        + ((r.earlyExitMinutes || 0) > 0 ? 1 : 0)
        + ((r.breakExcessMinutes || 0) > 0 ? 1 : 0)
        + (r.status === 'falta' ? 1 : 0)
    ), 0), [myAttendanceMonth]);

    const pendingCount = mySolicitudes.filter(s => s.status === 'pendiente').length;
    const position = employeeSettings[me?.id]?.position || 'Colaborador';

    const tabs = [
        { id: 'historial', label: 'Mi Historial', icon: FiCalendar, count: myAttendanceMonth.length },
        { id: 'descuentos', label: 'Mis Descuentos', icon: FiMinusCircle, count: descuentosCount },
        { id: 'extras', label: 'Mis Horas Extra', icon: FiClock, count: myOvertimeMonth.length },
        { id: 'recibo', label: 'Mi Recibo', icon: FiFileText },
        { id: 'solicitudes', label: 'Solicitudes', icon: FiFileText, count: mySolicitudes.length, badge: pendingCount },
    ];

    return (
        <div className="h-full flex bg-gray-50">
            {/* SIDEBAR colab */}
            <aside className="hidden lg:flex w-[240px] xl:w-[260px] bg-white border-r border-gray-200 flex-col shrink-0 overflow-y-auto">
                <div className="px-5 pt-5 pb-5 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center shadow-md shadow-sky-500/20">
                            <span className="text-white text-[20px] font-extrabold leading-none">H</span>
                        </div>
                        <div>
                            <div className="text-[16px] font-extrabold tracking-tight leading-none text-gray-900">Hingenia</div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mt-1 font-semibold">Mi Espacio</div>
                        </div>
                    </div>
                </div>
                <div className="mx-3 mb-5 p-3 rounded-xl bg-gradient-to-br from-sky-50 to-blue-100/40 border border-blue-200/60 shrink-0">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="relative">
                            {me?.photoURL ? (
                                <img src={me.photoURL} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white text-[13px] font-extrabold">
                                    {(me?.displayName || currentUser?.email || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 border-2 border-white rounded-full ${todayRecord?.entryTime && !todayRecord?.exitTime ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-extrabold text-gray-900 truncate">{me?.displayName || currentUser?.email?.split('@')[0]}</div>
                            <div className="text-[10.5px] text-gray-500 font-semibold truncate">{position}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-white/60">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800">
                            <span className="w-1 h-1 rounded-full bg-emerald-500" />
                            {todayRecord?.entryTime && !todayRecord?.exitTime ? 'En jornada' : todayRecord?.exitTime ? 'Cerrada' : 'Sin marcar'}
                        </span>
                        <span className="text-[10px] text-gray-500 font-bold font-mono ml-auto">{formatHM12(mySchedule.entry)}–{formatHM12(mySchedule.exit)}</span>
                    </div>
                </div>
                <div className="px-6 mb-2 shrink-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Menú</div>
                </div>
                <nav className="flex-1 px-3 pb-4 space-y-0.5">
                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-500 font-medium hover:bg-gray-50 cursor-not-allowed opacity-70">
                        <FiHome size={16} /> <span>Inicio</span>
                    </button>
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] bg-sky-100 text-sky-700 font-bold cursor-pointer">
                        <FiClock size={16} /> <span>Mi Jornada</span>
                        <FiChevronsDown size={12} className="ml-auto opacity-70" />
                    </div>
                    <div className="ml-3 py-1 relative">
                        <div className="absolute left-[13px] top-0 bottom-2 w-px bg-gray-200" />
                        <button onClick={() => setActiveTab('historial')} className={`w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] font-medium ${activeTab === 'historial' ? 'bg-sky-50 text-sky-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                            <span className={`absolute left-[13px] top-1/2 w-2 h-px ${activeTab === 'historial' ? 'bg-sky-600' : 'bg-gray-200'}`} />
                            Mi marcación
                        </button>
                        <button onClick={() => setActiveTab('historial')} className="w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] text-gray-600 font-medium hover:bg-gray-50">
                            <span className="absolute left-[13px] top-1/2 w-2 h-px bg-gray-200" />
                            Mi historial
                        </button>
                        <button onClick={() => setActiveTab('extras')} className={`w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] font-medium ${activeTab === 'extras' ? 'bg-sky-50 text-sky-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                            <span className={`absolute left-[13px] top-1/2 w-2 h-px ${activeTab === 'extras' ? 'bg-sky-600' : 'bg-gray-200'}`} />
                            Mis horas extra
                        </button>
                        <button onClick={() => setActiveTab('recibo')} className={`w-full relative flex items-center gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] font-medium ${activeTab === 'recibo' ? 'bg-sky-50 text-sky-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                            <span className={`absolute left-[13px] top-1/2 w-2 h-px ${activeTab === 'recibo' ? 'bg-sky-600' : 'bg-gray-200'}`} />
                            Mi recibo de pago
                        </button>
                    </div>
                    <button onClick={() => setActiveTab('solicitudes')} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium ${activeTab === 'solicitudes' ? 'bg-sky-100 text-sky-700 font-bold' : 'text-gray-500 hover:bg-gray-50'}`}>
                        <FiFileText size={16} /> <span>Solicitudes</span>
                        {pendingCount > 0 && <span className="ml-auto text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">{pendingCount}</span>}
                    </button>
                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-400 font-medium hover:bg-gray-50 cursor-not-allowed opacity-60">
                        <FiUsers size={16} /> <span>Mi equipo</span>
                        <span className="ml-auto text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-extrabold">PRONTO</span>
                    </button>
                    <button onClick={() => setShowHR(true)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors ${showHR ? 'bg-sky-100 text-sky-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                        <FiFileText size={16} /> <span>Mis documentos</span>
                    </button>
                    <div className="px-3 mt-5 mb-2">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Personal</div>
                    </div>
                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-400 font-medium hover:bg-gray-50 cursor-not-allowed opacity-60">
                        <FiUser size={16} /> <span>Mi perfil</span>
                        <span className="ml-auto text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-extrabold">PRONTO</span>
                    </button>
                    <button disabled className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] text-gray-400 font-medium hover:bg-gray-50 cursor-not-allowed opacity-60">
                        <FiHelpCircle size={16} /> <span>Ayuda</span>
                    </button>
                </nav>
                {streak > 0 && (
                    <div className="mx-3 mb-3 p-3.5 rounded-2xl relative overflow-hidden shrink-0" style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)', border: '1px solid #FED7AA' }}>
                        <div className="absolute -top-2 -right-2 text-[40px] opacity-30">🔥</div>
                        <div className="relative">
                            <div className="text-[10px] font-extrabold text-orange-700 uppercase tracking-[0.15em] mb-0.5">Racha actual</div>
                            <div className="text-[18px] font-extrabold text-gray-900 leading-tight">{streak} {streak === 1 ? 'día puntual' : 'días puntuales'}</div>
                            <div className="text-[11px] text-gray-500 font-semibold mt-1">¡Sigue así!</div>
                            <div className="h-1.5 bg-white/70 rounded-full mt-2 overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full" style={{ width: `${Math.min(100, (streak / 10) * 100)}%` }} />
                            </div>
                        </div>
                    </div>
                )}
                <div className="px-3 py-3 border-t border-gray-200 shrink-0">
                    <div className="flex items-center gap-3 px-2 py-1.5 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors">
                        <div className="relative">
                            {me?.photoURL ? (
                                <img src={me.photoURL} className="w-9 h-9 rounded-full object-cover" alt="" />
                            ) : (
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white text-[12px] font-extrabold">
                                    {(me?.displayName || currentUser?.email || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold text-gray-900 truncate">{me?.displayName || currentUser?.email?.split('@')[0]}</div>
                            <div className="text-[11px] text-gray-400 truncate font-medium">Colaborador</div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* RIGHT CONTENT */}
            <div className="flex-1 overflow-y-auto relative">
                {/* Mis documentos (overlay) */}
                {showHR && (
                    <div className="absolute inset-0 z-30 bg-gray-50 overflow-y-auto">
                        <HRDocumentation currentUser={currentUser} isAdmin={false} users={users} onClose={() => setShowHR(false)} />
                    </div>
                )}
                {solicitudModal && (
                    <SolicitudModal type={solicitudModal.type} mySchedule={mySchedule} initialDate={solicitudModal.date}
                        onClose={() => setSolicitudModal(null)}
                        onSubmit={async (data) => {
                            await handleCreateSolicitud({ type: solicitudModal.type, ...data });
                            setSolicitudModal(null);
                        }} />
                )}

                {/* HEADER greeting */}
                <div className="px-6 lg:px-10 pt-6 pb-2">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <div className="flex items-center gap-2 mb-2.5">
                                <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold capitalize">
                                    {format(new Date(), "EEEE · d 'de' MMMM · yyyy", { locale: es })}
                                </span>
                                <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-bold bg-emerald-100 px-2 py-0.5 rounded-full">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {todayRecord?.entryTime && !todayRecord?.exitTime ? 'Trabajando ahora' : 'En jornada'}
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-[34px] font-extrabold leading-[1.05] tracking-tight text-gray-900">
                                ¡Hola, {((currentUser?.displayName || currentUser?.email || '').split(' ')[0] || '').split('@')[0] || 'colaborador'}! <span className="inline-block ml-1">👋</span>
                            </h1>
                            <p className="text-sm text-gray-500 mt-2 font-medium">
                                {todayRecord?.entryTime && !todayRecord?.exitTime ? (
                                    <>Llevas <span className="text-gray-900 font-bold">{elapsedFriendly} trabajando</span> hoy. {mySchedule.flexible ? <>Tu meta: <span className="text-sky-700 font-bold">{myWorkHrs}h</span>.</> : <>Tu salida estimada: <span className="text-sky-700 font-bold">{formatHM12(mySchedule.exit)}</span>.</>}</>
                                ) : todayRecord?.exitTime ? (
                                    <>Hoy trabajaste <span className="text-gray-900 font-bold">{todayRecord.workedHours?.toFixed(1)}h</span>. ¡Buen trabajo!</>
                                ) : mySchedule.flexible ? (
                                    <>Aún no marcas entrada. Tu horario es flexible: puedes marcar cuando quieras, con <span className="text-gray-900 font-bold">{myWorkHrs}h</span> de meta diaria.</>
                                ) : (
                                    <>Aún no marcas entrada. Tu horario es <span className="text-gray-900 font-bold">{formatHM12(mySchedule.entry)}–{formatHM12(mySchedule.exit)}</span>.</>
                                )}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setSolicitudModal({ type: 'olvido' })} className="px-3.5 py-2 text-xs md:text-sm bg-white border border-gray-200 hover:border-gray-300 rounded-xl flex items-center gap-2 font-semibold text-gray-700 transition-colors">
                                <FiAlertTriangle size={14} /> Reportar olvido
                            </button>
                            <button onClick={() => setSolicitudModal({ type: 'permiso' })} className="px-3.5 py-2 text-xs md:text-sm bg-white border border-gray-200 hover:border-gray-300 rounded-xl flex items-center gap-2 font-semibold text-gray-700 transition-colors">
                                <FiCalendar size={14} /> Solicitar permiso
                            </button>
                            <button onClick={() => setActiveTab('recibo')} className="px-3.5 py-2 text-xs md:text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-xl flex items-center gap-2 font-bold transition-colors shadow-sm">
                                <FiFileText size={14} /> Ver mi recibo
                            </button>
                        </div>
                    </div>
                </div>

                {/* HERO grid */}
                <div className="px-6 lg:px-10 pt-4 pb-2">
                    <div className="grid grid-cols-12 gap-5">
                        {/* Live Timer */}
                        <div className="col-span-12 lg:col-span-5 rounded-3xl p-6 relative overflow-hidden text-white shadow-xl shadow-blue-900/10 min-h-[440px]"
                            style={{ background: 'linear-gradient(135deg, #1E293B 0%, #1E40AF 60%, #2563EB 130%)' }}>
                            <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-blue-500/30 blur-3xl pointer-events-none" />
                            <div className="absolute -bottom-24 -left-10 w-48 h-48 rounded-full bg-blue-400/20 blur-3xl pointer-events-none" />
                            <div className="relative h-full flex flex-col">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur px-2.5 py-1 rounded-full">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                        <span className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-200 font-bold">Mi marcación</span>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-mono px-2 py-0.5 border border-white/15 rounded-md font-bold">
                                        {me?.displayName ? me.displayName.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() : '--'}
                                    </div>
                                </div>
                                {!todayRecord?.entryTime ? (
                                    <>
                                        <div className="font-extrabold text-[60px] md:text-[64px] tabular-nums text-white leading-none tracking-tight">
                                            {formatDate12s(now)}
                                        </div>
                                        <p className="text-blue-200 text-[13px] font-bold mt-3 mb-6 capitalize">
                                            {format(now, "EEEE, d 'de' MMMM", { locale: es })} · {mySchedule.flexible
                                                ? `Horario flexible · ${myWorkHrs}h/día`
                                                : <>Horario {formatHM12(mySchedule.entry)} – {formatHM12(mySchedule.exit)}</>}
                                        </p>
                                        {canMarkEntry && !isWeekendDay ? (
                                            <button onClick={handleMarkEntry} className="w-full bg-white text-blue-700 py-3 rounded-xl text-sm font-extrabold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 shadow-lg active:scale-95">
                                                <FiLogIn size={16} /> Marcar Entrada
                                            </button>
                                        ) : (
                                            <div className="bg-orange-500/20 border border-orange-400/30 rounded-xl px-4 py-3">
                                                <p className="text-orange-200 text-xs font-bold flex items-center gap-2"><FiAlertTriangle size={14} /> Fuera de horario</p>
                                                <p className="text-orange-200/80 text-[11px] mt-1">
                                                    {isWeekendDay ? 'Hoy es domingo.' : `Tu entrada es a las ${formatHM12(mySchedule.entry)}.`} Reporta el olvido si fue accidental.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                ) : !todayRecord?.exitTime ? (
                                    <>
                                        <div className="font-extrabold text-[60px] md:text-[64px] tabular-nums text-white leading-none tracking-tight">
                                            {elapsedDisplay.split(':').map((p, i, a) => (
                                                <React.Fragment key={i}>{p}{i < a.length - 1 && <span className="text-white/40">:</span>}</React.Fragment>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-2 mt-3 mb-6">
                                            <FiLogIn size={14} className="text-blue-300" />
                                            <span className="text-blue-300 text-[13px] font-bold">Marqué entrada a las {formatHM12(todayRecord.entryTime)}</span>
                                            {todayRecord.lateMinutes > 0 && <span className="text-orange-300 text-[11px] font-bold ml-1">· {todayRecord.lateMinutes} min tarde</span>}
                                        </div>
                                        <div className="mb-6">
                                            <div className="flex justify-between text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2 font-bold">
                                                <span>{formatHM12(todayRecord.entryTime)}</span>
                                                <span className="text-blue-300">ahora · {formatDate12(now)}</span>
                                                <span>{formatHM12(estimatedExitStr)}</span>
                                            </div>
                                            <div className="h-1.5 bg-white/10 rounded-full relative">
                                                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-300 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                                                <div className="absolute top-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-lg" style={{ left: `${progressPct}%`, transform: 'translate(-50%, -50%)', boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.5)' }} />
                                            </div>
                                            <div className="grid grid-cols-3 mt-4 gap-2">
                                                <div>
                                                    <div className="text-[18px] font-extrabold tabular-nums text-white leading-none">{formatHM12(todayRecord.entryTime)}</div>
                                                    <div className="text-[9.5px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Entrada</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-[18px] font-extrabold tabular-nums text-blue-300 leading-none">{remainingDisplay}</div>
                                                    <div className="text-[9.5px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Restante</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-[18px] font-extrabold tabular-nums text-white leading-none">{formatHM12(estimatedExitStr)}</div>
                                                    <div className="text-[9.5px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Salida est.</div>
                                                </div>
                                            </div>
                                        </div>
                                        <BreakControls
                                            record={todayRecord}
                                            allowedMinutes={myBreakMinutes}
                                            onStart={handleMarkBreakStart}
                                            onEnd={handleMarkBreakEnd} />

                                        <button onClick={handleMarkExit} className="w-full bg-white text-blue-700 py-3 rounded-xl text-sm font-extrabold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 shadow-lg active:scale-95">
                                            <FiLogOut size={16} /> Marcar Salida
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="font-extrabold text-[36px] md:text-[44px] tabular-nums text-white leading-none tracking-tight flex items-center gap-2">
                                            <FiCheckCircle className="text-emerald-300" size={36} />
                                            Jornada cerrada
                                        </div>
                                        <p className="text-blue-200 text-[13px] font-bold mt-3 mb-6 capitalize">{format(now, "EEEE, d 'de' MMMM", { locale: es })}</p>
                                        <div className="grid grid-cols-3 gap-3 bg-white/[0.08] backdrop-blur rounded-2xl p-4 border border-white/10">
                                            <div>
                                                <div className="text-[22px] font-extrabold tabular-nums text-white leading-none">{formatHM12(todayRecord.entryTime)}</div>
                                                <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Entrada</div>
                                            </div>
                                            <div className="text-center border-x border-white/10 px-2">
                                                <div className="text-[22px] font-extrabold tabular-nums text-white leading-none">{formatHM12(todayRecord.exitTime)}</div>
                                                <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Salida</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[22px] font-extrabold tabular-nums text-blue-300 leading-none">{todayRecord.workedHours?.toFixed(1)}h</div>
                                                <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1.5 font-bold">Trabajadas</div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* KPIs personales (7/12) */}
                        <div className="col-span-12 lg:col-span-7 grid grid-cols-1 gap-5">
                            {/* Mi próximo pago */}
                            <div className="bg-white border border-gray-200 rounded-3xl p-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-br from-emerald-100 to-transparent rounded-bl-full pointer-events-none" />
                                <div className="relative">
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-100 text-emerald-800 mb-2">
                                                <FiDollarSign size={11} /> Mi próximo pago
                                            </span>
                                            <h3 className="text-[15px] font-bold text-gray-900 capitalize">Recibo de {attMonthFilter ? format(parseISO(attMonthFilter + '-01'), "MMMM yyyy", { locale: es }) : 'mes'}</h3>
                                            <p className="text-[12px] text-gray-500 font-medium">Se acreditará al cierre del mes</p>
                                        </div>
                                        <button onClick={() => setActiveTab('recibo')} className="text-[12px] text-emerald-700 font-bold hover:bg-emerald-50 flex items-center gap-1 px-3 py-1.5 rounded-lg">
                                            Ver desglose <FiArrowUpRight size={12} />
                                        </button>
                                    </div>
                                    <div className="flex items-end gap-5 flex-wrap">
                                        <div>
                                            <div className="text-[44px] font-extrabold leading-none tracking-tight text-emerald-600 tabular-nums">
                                                S/&nbsp;{Math.floor(myPayslip.net)}<span className="text-[22px] text-gray-400">.{String(Math.round((myPayslip.net % 1) * 100)).padStart(2, '0')}</span>
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-1.5 uppercase tracking-[0.18em] font-bold">neto a recibir</div>
                                        </div>
                                    </div>
                                    <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.1em] text-gray-500 font-bold mb-1">Sueldo base</div>
                                            <div className="text-[14px] font-extrabold tabular-nums text-gray-900">S/ {mySalary.toFixed(2)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.1em] text-sky-700 font-bold mb-1">+ H. Extra</div>
                                            <div className="text-[14px] font-extrabold tabular-nums text-sky-700">+ S/ {myPayslip.totalOTPay.toFixed(2)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.1em] text-red-600 font-bold mb-1">− Descuento</div>
                                            <div className="text-[14px] font-extrabold tabular-nums text-red-600">− S/ {myPayslip.discount.toFixed(2)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* KPI 2 + 3 row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="bg-white border border-gray-200 rounded-3xl p-5 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-100 to-transparent rounded-bl-full pointer-events-none" />
                                    <div className="relative">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-blue-100 text-blue-800 mb-2">
                                            <FiClock size={11} /> Acumulado
                                        </span>
                                        <h3 className="text-[14px] font-bold text-gray-900">Mis horas extra (mes)</h3>
                                        <div className="flex items-end gap-3 mt-3 mb-4">
                                            <div>
                                                <div className="text-[34px] font-extrabold tabular-nums text-gray-900 leading-none">
                                                    {myPayslip.totalOTHours.toFixed(1)}<span className="text-[13px] text-gray-400 ml-1">h</span>
                                                </div>
                                                <div className="text-[10px] text-gray-500 mt-1.5 uppercase tracking-[0.18em] font-bold">en {myPayslip.otRecords.length} registros</div>
                                            </div>
                                            <div className="flex-1 border-l border-gray-200 pl-3">
                                                <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-0.5 font-bold">A pagar</div>
                                                <div className="text-[18px] font-extrabold text-sky-700 leading-none tabular-nums">S/ {myPayslip.totalOTPay.toFixed(2)}</div>
                                            </div>
                                        </div>
                                        <div className="pt-3 border-t border-gray-100">
                                            <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-2 font-bold">Mi tarifa por hora</div>
                                            <div className="flex items-center justify-between p-3 bg-sky-50 rounded-lg">
                                                <span className="text-[12px] text-sky-800 font-semibold">Tarifa hora extra</span>
                                                <span className="font-mono font-extrabold text-sky-800">S/ {myRate.toFixed(2)} /hr</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-3xl p-5 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-100 to-transparent rounded-bl-full pointer-events-none" />
                                    <div className="relative">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-100 text-emerald-800 mb-2">
                                            <FiCheckCircle size={11} /> Mi score
                                        </span>
                                        <h3 className="text-[14px] font-bold text-gray-900">Mi puntualidad</h3>
                                        <div className="flex items-end gap-3 mt-3 mb-4">
                                            <div>
                                                <div className="text-[34px] font-extrabold tabular-nums text-gray-900 leading-none">
                                                    {myPuntualidad.pct}<span className="text-[13px] text-gray-400">%</span>
                                                </div>
                                                <div className="text-[10px] text-gray-500 mt-1.5 uppercase tracking-[0.18em] font-bold">{myPuntualidad.onTime} de {myPuntualidad.total} puntuales</div>
                                            </div>
                                            <div className="flex-1 border-l border-gray-200 pl-3">
                                                <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-0.5 font-bold">Mi promedio</div>
                                                <div className="text-[18px] font-extrabold text-gray-900 leading-none tabular-nums">{myPuntualidad.avgEntry}</div>
                                            </div>
                                        </div>
                                        <div className="pt-3 border-t border-gray-100">
                                            <div className="text-[9.5px] uppercase tracking-[0.18em] text-gray-400 mb-2 font-bold">Tardanzas del mes</div>
                                            {myPuntualidad.tardanzas.length > 0 ? (
                                                <div className="space-y-1.5">
                                                    {myPuntualidad.tardanzas.map(r => (
                                                        <div key={r.id} className="flex items-center justify-between text-[12px]">
                                                            <span className="text-gray-500 font-semibold flex items-center gap-1.5">
                                                                <FiAlertTriangle size={11} className="text-orange-500" />
                                                                {format(parseISO(r.date), "d MMM", { locale: es })}
                                                            </span>
                                                            <span className="font-mono font-bold text-orange-600">{r.lateMinutes} min</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-[11px] text-emerald-700 font-bold flex items-center gap-1.5">
                                                    <FiCheckCircle size={12} /> Sin tardanzas este mes
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABS pills */}
                <div className="px-6 lg:px-10 pt-4 pb-2 flex items-center gap-2 flex-wrap">
                    {tabs.map(tab => {
                        const active = activeTab === tab.id;
                        return (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold transition-all border ${
                                    active ? 'bg-sky-50 border-sky-500 text-sky-700 font-bold' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                }`}>
                                <tab.icon size={14} />
                                {tab.label}
                                {typeof tab.count === 'number' && (
                                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold tabular-nums ${active ? 'bg-white text-sky-600' : 'bg-gray-100 text-gray-500'}`}>{tab.count}</span>
                                )}
                                {tab.badge > 0 && (
                                    <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-extrabold">{tab.badge}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* TAB CONTENT */}
                <div className="px-6 lg:px-10 pt-4 pb-10">
                    <div className="mb-4 inline-flex items-center bg-white border border-gray-200 rounded-full pl-3 pr-1 py-1">
                        <FiCalendar size={12} className="text-gray-500 mr-1.5" />
                        <select value={attMonthFilter} onChange={e => setAttMonthFilter(e.target.value)} className="text-[12.5px] bg-transparent border-0 focus:ring-0 focus:outline-none font-semibold text-gray-700 pr-1 capitalize cursor-pointer">
                            {monthOptions.map(m => <option key={m} value={m}>{format(parseISO(m + '-01'), 'MMMM yyyy', { locale: es })}</option>)}
                        </select>
                    </div>
                    {activeTab === 'historial' && <CollabHistorialTab calendarData={calendarData} myAttendanceMonth={myAttendanceMonth} setSolicitudModal={setSolicitudModal} mySolicitudes={mySolicitudes} computeRecordDiscount={computeRecordDiscount} />}
                    {activeTab === 'descuentos' && <CollabDescuentosTab
                        myAttendanceMonth={myAttendanceMonth}
                        computeRecordDiscount={computeRecordDiscount}
                        mySchedule={mySchedule} />}
                    {activeTab === 'extras' && <CollabExtrasTab myOvertimeMonth={myOvertimeMonth} myPayslip={myPayslip} myRate={myRate} mySalary={mySalary} />}
                    {activeTab === 'recibo' && <CollabReciboTab myPayslip={myPayslip} mySalary={mySalary} myRate={myRate} attMonthFilter={attMonthFilter} me={me} mySchedule={mySchedule} myWorkHrs={myWorkHrs} employeeSettings={employeeSettings} />}
                    {activeTab === 'solicitudes' && <CollabSolicitudesTab mySolicitudes={mySolicitudes} setSolicitudModal={setSolicitudModal} handleCancelSolicitud={handleCancelSolicitud} />}
                </div>
            </div>
        </div>
    );
};

// ══════════════════════════════════════
// SOLICITUD MODAL
// ══════════════════════════════════════
const SolicitudModal = ({ type, mySchedule, initialDate, onClose, onSubmit }) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const [date, setDate] = useState(initialDate || today);
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [hourFrom, setHourFrom] = useState(mySchedule?.entry || DEFAULT_ENTRY);
    const [hourTo, setHourTo] = useState(mySchedule?.exit || DEFAULT_EXIT);
    const [missingMark, setMissingMark] = useState('exit');
    const [proposedTime, setProposedTime] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const titleMap = {
        vacaciones: 'Solicitar vacaciones',
        olvido: 'Reportar olvido de marcación',
        permiso: 'Solicitar permiso',
        home_office: 'Solicitar home office',
        justificacion: 'Justificar una falta',
    };

    const daysCount = (() => {
        if (type !== 'vacaciones' || !dateFrom || !dateTo) return 0;
        try {
            const start = parseISO(dateFrom);
            const end = parseISO(dateTo);
            if (end < start) return 0;
            return eachDayOfInterval({ start, end }).filter(d => d.getDay() !== 0).length;
        } catch { return 0; }
    })();

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const data = { reason };
            if (type === 'vacaciones') Object.assign(data, { dateFrom, dateTo, daysCount });
            else if (type === 'olvido') Object.assign(data, { date, missingMark, proposedTime });
            else if (type === 'permiso') Object.assign(data, { date, hourFrom, hourTo });
            else if (type === 'home_office') Object.assign(data, { date });
            else if (type === 'justificacion') Object.assign(data, { date });
            await onSubmit(data);
        } catch { /* parent handles toast */ }
        finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-[16px] font-extrabold text-gray-900">{titleMap[type]}</h3>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><FiX size={18} /></button>
                </div>
                <div className="p-5 space-y-3">
                    {type === 'vacaciones' && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Desde</label>
                                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Hasta</label>
                                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                                </div>
                            </div>
                            <div className="text-[12px] text-sky-700 font-semibold bg-sky-50 px-3 py-2 rounded-lg">{daysCount} {daysCount === 1 ? 'día hábil' : 'días hábiles'}</div>
                        </>
                    )}
                    {type === 'olvido' && (
                        <>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Fecha</label>
                                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">¿Qué olvidaste?</label>
                                    <select value={missingMark} onChange={e => setMissingMark(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100">
                                        <option value="entry">Entrada</option>
                                        <option value="exit">Salida</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Hora aprox.</label>
                                    <input type="time" value={proposedTime} onChange={e => setProposedTime(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                                </div>
                            </div>
                        </>
                    )}
                    {type === 'permiso' && (
                        <>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Fecha</label>
                                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Desde</label>
                                    <input type="time" value={hourFrom} onChange={e => setHourFrom(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Hasta</label>
                                    <input type="time" value={hourTo} onChange={e => setHourTo(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                                </div>
                            </div>
                        </>
                    )}
                    {type === 'home_office' && (
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Fecha</label>
                            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                        </div>
                    )}
                    {type === 'justificacion' && (
                        <>
                            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                                <FiAlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-[11.5px] text-amber-800 leading-snug font-medium">
                                    Explica por qué no marcaste ese día. El admin la revisa y decide si se descuenta o no.
                                </p>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">Día de la falta</label>
                                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-mono font-semibold focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
                            </div>
                        </>
                    )}
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 block mb-1.5 font-bold">
                            {type === 'justificacion' ? 'Motivo de la falta' : 'Motivo / detalle'}
                        </label>
                        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
                            placeholder={type === 'justificacion' ? 'Ej: cita médica, emergencia familiar...' : 'Cuéntale al admin por qué necesitas esto...'}
                            className="w-full px-3 py-2.5 text-[13px] bg-gray-50 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100 resize-none" />
                    </div>
                </div>
                <div className="p-5 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-[13px] bg-white border border-gray-200 rounded-xl font-semibold text-gray-700 hover:border-gray-300">Cancelar</button>
                    <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 text-[13px] bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold disabled:opacity-50 flex items-center gap-1.5">
                        {submitting ? 'Enviando...' : <><FiCheck size={14} /> Enviar solicitud</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OvertimeCalculator;
